/**
 * AiMpScenarioPlanning.js
 *
 * MP(Master Planning) 전용 AI 시나리오 플래닝 화면.
 * MP_MODULE 설정과 MpExtraInitContent(현황 분석 탭 콘텐츠)만 제공하며,
 * 실제 화면은 AiScenarioPlanning.js 의 공통 4탭 워크플로우로 위임한다.
 *
 * 4탭 워크플로우 (전 모듈 공통):
 *   Tab 0 — 현황 분석  (MpExtraInitContent + KPI 요약)
 *   Tab 1 — 시나리오 구성  (시나리오 생성[표준/직접/AI] → 파라미터 설정 · 엔진 실행)
 *   Tab 2 — 시나리오 비교  (KPI 비교)
 *   Tab 3 — 생산계획 확정
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n, { getAgentLanguage } from "@plannel/utils/i18n";
import { hasEffectiveValue, paramRowSx,
         PARAM_ROW_RADIUS, PARAM_ROW_PAD_X,
         PARAM_NUM_INPUT_W, PARAM_SELECT_W, PARAM_ROW_MIN_H, PARAM_CTRL_H,
         isInlineEditable, rememberModuleCatalog } from "./scenarioParamUtil";
import { baselineCurrentText, currentPrefixFor, ParamValueCells, ParamRevertButton,
         useEditEndGuard } from "./scenarioParamCells";
import ScenarioParamAddPicker from "./ScenarioParamAddPicker";
import { suggestValue, findOverlappingPeriodIndices, findLotBoundConflicts, yieldDecToPct,
         MP_RESOURCE_NUM, seedTargetsFromValue, shownResourceCds, applyChatValueToShownTargets,
         dropTargetCds, rowGroupKeys, rememberRowScopeTargets,
         companionScopePatches } from "./mpScenarioParamUtil";
import scenarioService from "../../services/ai/scenarioService";
import planVersionService from "@plannel/services/data/plan-version-service";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import CodeUtil from "@plannel/pages/utils/code-util";
import { reloadResourceFilter } from "@plannel/components/filter/ResourceFilter";
import {
  Box, Card, CardContent, Chip, CircularProgress, Collapse,
  Dialog, DialogTitle, DialogContent,
  IconButton, LinearProgress, Stack, Tooltip, Typography,
} from "@mui/material";
import { blue, green, red } from "@mui/material/colors";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon  from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon  from "@mui/icons-material/InfoOutlined";
import RefreshIcon       from "@mui/icons-material/Refresh";
import CloseIcon         from "@mui/icons-material/Close";
import RestartAltIcon    from "@mui/icons-material/RestartAlt";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LockOutlinedIcon  from "@mui/icons-material/LockOutlined";
import { CollapseArrow } from "@plannel/components/ai/CollapseArrow";
import { AiButton } from "@plannel/components/ai/AiButton";
import { AiDeleteButton } from "@plannel/components/ai/AiDeleteButton";
import { AiTargetMultiSelect } from "@plannel/components/ai/AiTargetMultiSelect";
import { StatusKpiSection, StatusSection, NavName, ForecastActualChart,
         StatusItemHeader, StatusItemRow, StatusItemList,
         STATUS_BAR_COL_SX, STATUS_BAR_H, STATUS_BAR_TRACK, STATUS_BAR_RADIUS,
         BAR_TIP_PROPS, BarTip,
         StatusEmpty, StatusEmptyPanel, StatusPendingTag, buildStatusPanelSummary,
         StatusInsightCard, StatusCardField, STATUS_CARD_GRID_SX, STATUS_TARGET_CHIP_SX,
         openPlanRunScreen, useLastRunSummary,
         StatusToolbar, StatusToolbarSelect } from "./scenarioStatusUi";
// 경고 그릇·카드 껍데기·집계 헬퍼는 RP/IP/DP 와 단일 정의처를 공유한다 — 복제하면 색·규격이 조용히 갈라진다.
import CompareFeasibilityWarnings, { FeasibilityHeroBadges, feasWarnCount } from "./CompareFeasibilityWarnings";
import AiSegmentedControl from "./AiSegmentedControl";
import AiStyledSwitch from "./AiStyledSwitch";
import { DirArrow } from "@plannel/components/DemandPriorityList";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { C } from "@plannel/components/ai/aiTokens";
import { measureText } from "@plannel/components/ai/fitColumns";
import { getFontFamily, getFontSizePx } from "@plannel/utils/font-util";
import { MpChartButton, MpResourceLoadChart } from "./MpResourceLoadChart";
import { fmtMoney } from "./aiMoney";


// 병목 자원 리스트 우측 "대체 자원 있음/없음" 칩 열 폭 — 실측 동기화(StatusItemList) 전
// 첫 페인트용 폴백값. 실제 폭은 전 행의 칩 실측 최대값으로 맞춰진다.
const ALT_RES_CHIP_W = 124;

// 누적 세그먼트 바 세그먼트 구분선 — 4px 높이에서는 색만으로 경계가 안 보여 한 덩어리로 뭉친다.
// inset 이라 세그먼트 폭에 영향이 없다(border 로 넣으면 flex 폭 계산이 흔들린다).
const SEG_DIVIDER = "inset -1px 0 0 #FFFFFF";

// 일자별 Capa 대비 부하 누적 막대 — 자원 부하 분석 화면(ResourceUtilization.js)과 같은 색을 쓴다
const CAPA_TREND_COLORS = {
  capa_load_in_h: blue[400],
  capa_avail_h:   green[300],
  capa_over_h:    red[300],
};
const CAPA_TREND_BAR_KEYS = Object.keys(CAPA_TREND_COLORS);

// 납기 현황 누적 막대 — 같은 화면 문제 주문 리스트 바와 같은 의미색(준수 초록·지연 앰버·부족 빨강).
// 위 Capa 차트가 MUI 팔레트를 쓰는 건 자원 부하 분석 화면과의 정합 때문이라 팔레트가 서로 다른 게 맞다.
const OTD_TREND_COLORS = {
  otd_count:   C.g1,
  late_count:  C.g2s,
  short_count: C.g3,
};
const OTD_TREND_BAR_KEYS = Object.keys(OTD_TREND_COLORS);

// 일자 키(YYYY-MM-DD) → 주/월 버킷 키. 월="YYYY-MM", 주=월요일 시작일 — 둘 다 사전순이 곧 시간순이다.
// new Date("YYYY-MM-DD") 는 UTC 자정 파싱이라 음수 오프셋 지역에서 하루 밀린다 → UTC 산술만 쓴다.
const DAY_MS = 86400000;
const dayTs  = (dk) => { const [y, m, d] = String(dk).slice(0, 10).split("-").map(Number); return Date.UTC(y, m - 1, d); };
const trendBucketKey = (dk, grain) => {
  if (grain === "month") return dk.slice(0, 7);
  if (grain !== "week")  return dk;
  const ts   = dayTs(dk);
  const back = (new Date(ts).getUTCDay() + 6) % 7;   // 주 시작 = 월요일(ISO)
  return new Date(ts - back * DAY_MS).toISOString().slice(0, 10);
};

// 축 상한 — 계획 구간 설정이 깨진 테넌트에서 루프가 폭주하지 않게 한다(약 8년).
const TREND_MAX_DAYS = 3000;

/**
 * 일자 행 배열을 [from, to] 전 구간의 조밀한 일자 배열로 채운다(주문·능력 없는 날도 버킷을 만든다).
 * @param {Array}  rows  [{date, ...}] — 정렬 여부 무관
 * @param {string} from  구간 시작 YYYY-MM-DD (없으면 데이터 최소일)
 * @param {string} to    구간 종료 YYYY-MM-DD (없으면 데이터 최대일)
 * @param {object} blank 없는 날에 채울 값 — 건수는 {…:0}, 능력은 {} (막대를 그리지 않음)
 */
const fillDays = (rows, from, to, blank) => {
  if (!rows?.length) return rows || [];
  const byDate = new Map(rows.map((r) => [String(r.date).slice(0, 10), r]));
  const keys   = [...byDate.keys()].sort();
  const start  = from && from < keys[0]                 ? from : keys[0];
  const end    = to   && to   > keys[keys.length - 1]   ? to   : keys[keys.length - 1];
  const out = [];
  for (let ts = dayTs(start), last = dayTs(end); ts <= last && out.length < TREND_MAX_DAYS; ts += DAY_MS) {
    const dk = new Date(ts).toISOString().slice(0, 10);
    out.push(byDate.get(dk) ?? { date: dk, ...blank });
  }
  return out;
};

/**
 * 일자 행을 주/월 버킷으로 합산한다. 건수도 시간(h)도 가산적이라 단순 합이 곧 그 버킷의 값이다.
 * null 은 더하지 않는다 — 버킷 전체가 null 이면 null 로 남아 막대를 그리지 않는다.
 */
const rollupDays = (rows, keys, grain) => {
  if (grain === "day" || !rows?.length) return rows || [];
  const acc = new Map();
  rows.forEach((row) => {
    const k = trendBucketKey(String(row.date).slice(0, 10), grain);
    let cur = acc.get(k);
    if (!cur) { cur = { date: k }; keys.forEach((kk) => { cur[kk] = null; }); acc.set(k, cur); }
    keys.forEach((kk) => {
      const v = row[kk];
      if (v == null) return;
      cur[kk] = (cur[kk] ?? 0) + Number(v);
    });
  });
  return [...acc.values()].sort((a, b) => a.date.localeCompare(b.date));
};

// ── MP 전용 파라미터 옵션 ─────────────────────────────────────────────────
// label 값은 i18n 키 문자열 — 모듈 레벨 상수라 t()를 직접 쓸 수 없으므로 소비처(CustomParamField 등)에서 t()로 변환한다.
// 라벨은 설정 화면과 같은 공유 키(backward/forward/forward2) — 전용 키를 두면 forward2 가 빠진다.
const MP_SCHEDULING_OPTIONS = Object.values(CodeUtil.planStrategies).map((v) => ({ value: v, label: v }));
const MP_CAPACITY_OPTIONS = [
  { value: "standard",  label: "mpCapacityStandardLabel" },
  { value: "overtime",  label: "mpCapacityOvertimeLabel" },
  { value: "outsource", label: "mpCapacityOutsourceLabel" },
];
const MP_LOT_SIZE_OPTIONS = [
  { value: "standard", label: "mpLotSizeStandardLabel" },
  { value: "min_lot",  label: "mpLotSizeMinLotLabel" },
  { value: "no_split", label: "mpLotSizeNoSplitLabel" },
];
const MP_PRIORITY_OPTIONS = [
  { value: "FIFO",   label: "mpPriorityFifoLabel" },
  { value: "EDD",    label: "mpPriorityEddLabel" },
  { value: "custom", label: "mpPriorityCustomLabel" },
];

// ── 계획 방식(PS) 선택치 단일 소스 ─────────────────────────────────────────────
// 공급계획 설정 화면과 동일한 code-util.planStrategies 에서 계획 방식 선택치를 파생한다.
// 공급계획에 계획 방식이 추가되면(planStrategies + 언어팩) 시나리오 구성 탭 드롭다운에도 자동 반영된다.
// 라벨은 언어팩 키(forward/forward2/backward…)로 표시 언어를 따른다(백엔드 하드코딩 라벨 미사용).
const PS_STRATEGY_VALUES = Object.values(CodeUtil.planStrategies);   // 예: ["forward", "forward2", "backward"]
const psStrategyLabel = (v) => i18n.t(v);

// ── 수요 우선순위 선택치 단일 소스 ─────────────────────────────────────────────
// 공급계획 설정(MpSettings) 화면과 동일한 code-util.priorityItems 에서 파생한다.
// 화면 표시(label)만 다국어(i18n.t) — 내부 제어/판별은 항상 시스템 코드(value)로 한다.
// 설정 화면에 기준이 추가되면(priorityItems + 언어팩) 시나리오 구성 탭에도 자동 반영된다.
const { DATE_PRIORITY: MP_DP_DATE } = CodeUtil.priorityItems;
const MP_DATE_PRIORITY_ITEMS = [
  { value: MP_DP_DATE.DUE_DATE,     nameKey: "dueDate" },
  { value: MP_DP_DATE.W_DUE_DATE,   nameKey: "weekOfDueDate" },
  { value: MP_DP_DATE.M_DUE_DATE,   nameKey: "monthOfDueDate" },
  { value: MP_DP_DATE.PLAN_HORIZON, nameKey: "planHorizon" },
];
const mpDatePriorityOpts = () =>
  MP_DATE_PRIORITY_ITEMS.map((o) => ({ value: o.value, label: i18n.t(o.nameKey) }));

// 기타 기준 — DEMAND_PRIORITY 에서 dueDate(날짜) 제외(설정 화면 defaultPriorityData 와 동일 필터).
// 방향은 sorting 원본 그대로 — 표기 규칙은 공용 DirArrow 가 갖는다.
const MP_DEMAND_PRIORITY_ETC = Object.entries(CodeUtil.priorityItems.DEMAND_PRIORITY)
  .filter(([, it]) => it.name !== "dueDate")
  .sort(([, a], [, b]) => a.seq - b.seq)
  .map(([value, it]) => ({ value, nameKey: it.name, sorting: it.sorting }));
const mpDemandPriorityEtcOpts = () =>
  MP_DEMAND_PRIORITY_ETC.map((o) => ({ value: o.value, label: i18n.t(o.nameKey), sorting: o.sorting }));

// ── 챗 set_param → MP 시나리오 반영 ──────────────────────────────────────────
// MP 의 override 는 **파라미터당 한 엔트리**({param_key, scope_type, targets, value}) 로,
//   IP/RP 의 {scope_type, params:{…}} 와 형태가 다르다. 공통 경로로 쓰면 화면(ovOf/upsertOv)이
//   읽지 못해 "적용했다는데 값은 그대로"가 된다.
// 수요 우선순위(DEMAND_PRIORITY)는 단일 값이 아니라 순위 목록(priority_items = [날짜?, ...기타]) 이다.
//   화면 빌더와 같은 규칙으로 병합한다 — 날짜는 하나만(교체), 기타는 미적용→적용으로 옮기되
//   이미 적용된 항목 **뒤에** 붙인다(선택 순서가 곧 우선순위).
const mpApplyChatParam = (sc, field, value) => {
  // 이 시나리오가 노출하지 않는 파라미터는 카드에 행 자체가 없어(편집기는 param_defs 로만 그린다)
  //   override 를 써도 화면에 나타나지 않는다 — 반영된 척하지 않고 거부한다(컨테이너가 실패 처리).
  const defs = Array.isArray(sc.param_defs) ? sc.param_defs : [];
  if (defs.length && !defs.some(d => d.param_key === field)) return null;
  const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
  const i = list.findIndex(o => o.param_key === field);
  const cur = i >= 0 ? list[i] : null;
  const upsert = (patch) => {
    const row = { param_key: field, scope_type: cur?.scope_type ?? "all",
                  targets: cur?.targets ?? [], reason: cur?.reason ?? null, ...patch };
    if (i >= 0) list[i] = { ...list[i], ...row }; else list.push(row);
    return { ...sc, overrides: list };
  };
  // 챗은 대상을 지정하지 못한다 — 자원 그리드 스코프면 화면에 표시 중인 대상 전체에 적용한다.
  //   합성 행(정규+초과 작업 시간)은 동반 키에 행이 없다 — 적용 단위·표시 행 모두 대표 키가 정본.
  const rowKeys = rowGroupKeys(defs, field);
  const rowOv = rowKeys.map(k => list.find(o => o.param_key === k)).find(Boolean) || null;
  const scope = rowOv?.scope_type;
  if (MP_RESOURCE_NUM.has(field) && (scope === "resource" || scope === "workcenter")) {
    const patch = applyChatValueToShownTargets(cur, shownResourceCds(sc, rowKeys[0], scope), value);
    return patch ? upsert({ ...patch, scope_type: scope }) : null;
  }
  if (field !== "DEMAND_PRIORITY") return upsert({ value });

  const v = String(value);
  const dateSet = new Set(mpDatePriorityOpts().map(o => o.value));
  const etcOpts = mpDemandPriorityEtcOpts();
  const etcSet  = new Set(etcOpts.map(o => o.value));
  const baseOf  = (c) => etcOpts.find(x => x.value === c)?.base || c;
  const rows    = cur?.priority_items ?? (cur?.value ? [String(cur.value)] : []);
  let date      = rows.find(c => dateSet.has(c)) || null;
  let etc       = rows.filter(c => etcSet.has(c));
  if (dateSet.has(v)) {
    date = v;                       // 날짜 — 항상 하나(교체, 맨 앞)
  } else if (etcSet.has(v)) {
    if (!etc.includes(v)) {         // 이미 적용 중이면 순서를 흐트러뜨리지 않는다
      etc = [...etc.filter(c => baseOf(c) !== baseOf(v)), v];   // 같은 기준 다른 방향은 교체
    }
  } else {
    return null;                    // 화면이 모르는 코드 → 적용하지 않음
  }
  return upsert({ priority_items: [...(date ? [date] : []), ...etc], value: null });
};

// ── MP 모듈 데이터 (AiScenarioPlanning 이 import 하는 실제 설정) ──────────────
export const MP_MODULE = {
  code: "MP",
  // 챗 파라미터 적용 훅 — 컨테이너 executeAction 이 이 함수가 있으면 위임한다(MP 전용 저장 형태).
  applyChatParam: mpApplyChatParam,
  // ── 비교 탭 "품목 수준 차이"(CompareItemDiff) 설정 ───────────────────────────
  // problem_qty = late+short 합성(백엔드) — 낮을수록 좋음. 드릴다운은 주문추적 화면이
  //   컴포넌트 내부 상태 의존이라 v1 미지원(makeDrillTargets 생략 → 일반 텍스트).
  itemDiffCfg: {
    locKey: "loc_cd",
    locNameKey: "loc_name",   // z_mp_order.loc_name 패스스루 — 셀·드롭다운 표시는 거점명, 필터값은 코드
    columns: [
      { key: "problem_qty",     labelKey: "itemDiffColProblemQty", ko: "문제수량(EA)",     digits: 0, dir: "down" },
      { key: "late_order_qty",  labelKey: "itemDiffColLateQty",    ko: "납기지연수량(EA)", digits: 0, dir: "down" },
      { key: "short_order_qty", labelKey: "itemDiffColShortQty",   ko: "공급부족수량(EA)", digits: 0, dir: "down" },
    ],
    // 행별 분류 근거 배지 — 위험도 전이가 없을 때 primary(problem_qty) delta 표기용 축약 라벨.
    primaryReason: { labelKey: "itemDiffReasonProblemShort", ko: "문제수량", unit: "EA" },
  },
  // label/step2lbl/step4lbl/outputs/aiRec* 값은 i18n 키 문자열 — 모듈 레벨 상수라 t()를 직접
  //   쓸 수 없으므로 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
  label: "mpModuleLabel", Icon: PrecisionManufacturingIcon,
  // ── Analysis 탭 Quick Prompt (자유 질문, 액션 없음) — { i18nKey, ko } ──────────
  quickPrompts: {
    analysis: {
      0: [
        { i18nKey: "agentAskMpKpi",                ko: "현황 KPI 해석해줘" },
        { i18nKey: "agentAskMpCapacityShortOrder", ko: "능력 부족 주문은?" },
        { i18nKey: "agentAskMpOverload",           ko: "과부하 자원은?" },
        { i18nKey: "agentAskMpLate",               ko: "납기지연 주문은?" },
      ],
      1: [
        { i18nKey: "agentAskMpSchedule", ko: "계획 방식 차이는?" },
        { i18nKey: "agentAskMpOtVsOut",  ko: "능력 늘리려면 뭘 바꿔야 해?" },
      ],
      2: [
        { i18nKey: "agentAskMpBest",     ko: "어떤 시나리오가 최적이야?" },
        { i18nKey: "agentAskMpUtilOtd",  ko: "가동률 vs 납기 비교" },
      ],
      3: [
        { i18nKey: "agentAskMpChecklist", ko: "확정 전 체크리스트" },
      ],
    },
  },

  // ── Action 탭: 화면 하위상태(step·mode·aiResult)에 맞는 "지금 가능한 액션"만 선언 (DP 와 동일 패턴) ──
  // 값 고정 set_param 프리셋은 clickOnly(자유입력 dispatch 제외). 자유입력 파라미터 변경은 set_param 값 추출 경로가 처리.
  // description/confirmKo 는 모듈 레벨 함수(컴포넌트 아님)라 useTranslation 훅을 쓸 수 없어
  //   i18n 싱글턴(i18n.t)으로 미리 번역해 둔다 (IP/DP/RP agentActions 와 동일 패턴).
  agentActions: ({ step, mode, aiResult, exec }) => {
    const selMode = (m, key, ko) => {
      const modeLabel = i18n.t(key, { defaultValue: ko });
      return {
        id: `mp.mode.${m}`, i18nKey: key, ko, group: "action", style: "secondary",
        description: i18n.t("ipSelectModeDesc", { mode: modeLabel }),
        run: () => exec({ action: "select_mode", payload: { mode: m } }),
        confirmKo: i18n.t("ipSelectModeConfirm", { mode: modeLabel }),
      };
    };
    // 값이 고정된 파라미터 프리셋은 제거했다 — 그 수치의 근거가 없고 사용자가 원하는 값이라는
    //   보장도 없다. 파라미터 변경은 컨테이너 공통 scn.param.change(연속 선택)가 받는다.
    const runEngine = {
      id: "mp.engine.run", i18nKey: "agentActMpRunEngine", ko: "엔진 실행",
      group: "action", style: "primary", description: i18n.t("mpRunEngineDesc"),
      run: () => exec({ action: "run_engine" }),
      confirmKo: i18n.t("MSG_IpEngineRunConfirm"),
    };
    if (step === 0) return [
      selMode("ai",     "agentActMpModeAi",     "AI 추천 시나리오 생성"),
      selMode("custom", "agentActMpModeCustom", "표준 시나리오 선택"),
    ];
    if (step === 1) {
      // mode 미설정(null)은 표준 시나리오 선택 상태와 동일(통합 뷰 기본) — 초기 방식 선택 액션 없이
      //   표준 경로 액션을 그대로 노출. AI 전환은 컨테이너 공통 scn.mode.ai 가 제공.
      if (mode === "ai" && !aiResult) return [];
      return [runEngine];
    }
    return [];   // 단계 이동은 컨테이너 generic nav(nav.step*)가 전담 — 모듈별 고정 navTo 는 라벨 중복이라 제거
  },
  step2lbl: "paramSetupEngineRunTitle", step4lbl: "mpConfirmLabel",
  //   flowTopNodes = 공통 3(현황·시나리오 확정·KPI 해석) + 분기 앵커 1.
  //   카탈로그 조회는 AI 추천 경로(_run_suggest_only)라 엔진 실행 흐름에 들어오지 않는다.
  engineTrack: { parentTool: "mp_step3_engine", childPrefix: "mp_engine_",
                 match: "role", flowTopNodes: 4 },
  inputFrom: "RP",
  baseline: null,
  scenarios: [
    { role: "base", name: "scnMpBaseName",
      desc: "scnMpBaseDesc",
      tags: ["scnTagBackwardScheduling", "scnTagStandardLot", "FIFO", "scnTagUtilizationBalance"],
      score: null, kpis: null },
    { role: "alt1", name: "scnMpAlt1Name",
      desc: "scnMpAlt1Desc",
      tags: ["scnTagEddPriority", "scnTagOvertimeAllowed", "scnTagDeliveryTopPriority", "scnTagOtdMaximize"],
      score: null, kpis: null },
    { role: "alt2", name: "scnMpAlt2Name",
      desc: "scnMpAlt2Desc",
      tags: ["scnTagAsapStart", "scnTagMinLot", "scnTagOutsourceUse", "scnTagUtilizationMax"],
      score: null, kpis: null },
  ],
  // params[].k / kpiLabels / enginePhases / aiRec* 값은 i18n 키 문자열 — 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
  params: [
    { k: "planStrategy", paramKey: "scheduling_method", type: "select",
      options: MP_SCHEDULING_OPTIONS,
      vals: { base: "backward", alt1: "backward", alt2: "forward" } },
    { k: "mpCapacityPolicyLabel", paramKey: "capacity_policy", type: "select",
      options: MP_CAPACITY_OPTIONS,
      vals: { base: "standard", alt1: "overtime", alt2: "outsource" } },
    { k: "mpLotSizePolicyLabel", paramKey: "lot_size_policy", type: "select",
      options: MP_LOT_SIZE_OPTIONS,
      vals: { base: "standard", alt1: "standard", alt2: "min_lot" } },
    { k: "mpPriorityPolicyLabel", paramKey: "priority_policy", type: "select",
      options: MP_PRIORITY_OPTIONS,
      vals: { base: "FIFO", alt1: "EDD", alt2: "FIFO" } },
  ],
  // 미달은 '금액(피해 크기) → 건수' 순으로 짝지어 붙이고, 부족을 지연보다 앞에 둔다(부족이 더 심각).
  //   추천 카드 변화 요약(deltaRows)은 이 순서의 앞 4개(a·h·d·i)를 가져간다.
  kpiLabels: ["mpKpiOtdLabel", "mpKpiAvgUtilLabel", "mpKpiShortOrderAmtLabel", "mpKpiShortOrderCountLabel", "mpKpiLateOrderAmtLabel", "mpKpiLateOrderCountLabel", "mpKpiBottleneckResCountLabel", "mpKpiAddCapaRateLabel", "mpKpiOverCapaResCountLabel"],
  kpiKeys:   ["a", "b", "h", "d", "i", "c", "e", "f", "g"],
  // dir 어휘는 공통 컨테이너 계약 — "max"/"min"/"neutral" 만 해석한다(그 외 값은 전부 중립 취급).
  // f 추가 능력 투입률(%) = Base 기준 능력 대비 초과 투입 비율 / g 초과 자원 수 / h·i 부족·지연 금액
  kpiDir: { a: "max", b: "neutral", c: "min", d: "min", e: "min", f: "min", g: "min", h: "min", i: "min" },
  moneyKpiKeys: ["h", "i"],
  bestMap: { a: "base", b: "alt1", c: "alt1", d: "alt2", e: "alt1", f: "base", g: "base", h: "alt2", i: "alt1" },
  enginePhases: [
    "mpEnginePhaseRpLoad",
    "mpEnginePhaseDemandExplode",
    "mpEnginePhaseCapaCalc",
    "mpEnginePhaseSequenceDetermine",
    "mpEnginePhaseOverloadLevel",
    "complete",
  ],
  aiRec: 1,
  // AI 추천 시나리오는 '권장 대책'(ComposeExtra=MpComposeExtraContent)을 입력으로 사용 →
  //   권장 대책 로딩(getMpInitialState(true)) 완료 전까지 AI 생성 버튼 비활성 + recommended_actions 전달.
  aiRecNeedsRecommended: 1,
  skipKpiSection: true,  // MpExtraInitContent가 현황 KPI를 직접 표시 — StepKpiAnalysis 중복 렌더 제거

  // AI 추천 시나리오 안내 — 주 경로는 현황 분석 → 권장 대책 → 등록 시나리오 기반 파라미터 조립이다
  //   (orchestrator._source_scenarios). RP 발주·MRP·BOM은 미사용.
  aiRecCardDesc: "mpAiRecCardDesc",
  aiRecIntroLines: [
    "mpAiRecIntroLine1",
    "mpAiRecIntroLine2",
  ],
  // 현황 KPI 카드와 동일 키 재사용. 가동률은 FC base 에서 100% 를 넘지 않아 변별력이 없으므로
  //   헤드라인에서 빼고, 심각도 순(부족 → 지연 → 병목)으로 세운다.
  aiRecFactors: ["mpKpiShortOrderCountLabel", "mpKpiLateOrderCountLabel", "mpKpiBottleneckResCountLabel", "mpAiRecFactorRiskDist"],
  aiRecLoadingDesc: "mpAiRecLoadingDesc",
  // agentActMpModeAi 의 ko("AI 추천 시나리오 생성")와 동일 문구라 동일 키를 재사용한다.
  aiRecButtonLabel: "agentActMpModeAi",
};

// ── 숫자 포맷 헬퍼 ───────────────────────────────────────────────────────────
function fmtPct(v)  { if (v == null || v === "") return "--"; return Number(v).toFixed(1); }
function fmtCount(v){ if (v == null || v === "") return "--"; return Math.round(Number(v)).toLocaleString(); }
// 금액 — 회사 기본 통화 표기(aiMoney). 0/누락은 "-".
function fmtAmt(v) {
  if (v == null || Number(v) === 0) return "-";
  return fmtMoney(v) ?? "-";
}

// ── 현황 이슈 스타일 ─────────────────────────────────────────────────────────
// 문제 항목 등 다른 섹션과 색감 통일 — 선명한 단일 강조색(바·칩·숫자 공용) + 연한 배경.
const ISSUE_STYLE = {
  error:   { color: C.g3,  bg: C.g3l,  text: C.g3t  },
  warning: { color: C.g2,  bg: C.g2l,  text: C.g2t  },
  info:    { color: C.acc, bg: C.accL, text: C.accT },
};
// ── 드릴다운 내비게이션 (Redux 탭) — 현황 탭·비교 탭 공용 ──────────────────────
// 코드/명은 String() 강제 — scenarioService 가 숫자형 코드를 BigInt 로 파싱하면 MUI 가 value 를
// JSON.stringify 하다 BigInt 직렬화 불가로 크래시하기 때문. updateViewState 는 병합 리듀서라
// 이전 드릴다운 stale 필드가 남으므로 먼저 removeViewState 로 비운다.
// 버전(version)이 주어지면 해당 버전으로 조회되도록 동반 주입하고, 없으면 대상 화면이 자기 기본(최신)
// 버전을 선택한다. 자원 부하 분석 화면은 버전 옵션 잘림(BigInt 직렬화 크래시) 이슈가 없어 주입이 안전하다.
// (주문 추적 화면도 버전을 주입한다 — MpVersionFilter 옵션은 100개 제한이 있으나 통상 버전 수가 100개를
//  넘지 않아 기준 버전이 잘리지 않는다. goToOrderTracking 참조.)
export function openResourceUtilization(dispatch, res, version) {
  const RESOURCE_UTILIZATION = "RESOURCE_UTILIZATION";
  // res.resource_cd 가 있으면 그 자원으로 필터, 없으면 필터 없이 화면만 연다(전체 무한 → 화면 링크만).
  const payload = {};
  if (res?.resource_cd) payload.resource = { resCd: String(res.resource_cd), name: String(res.resource_name ?? "") };
  if (res?.loc_cd) payload.filter = { locations: [{ locCd: String(res.loc_cd), locName: String(res.loc_name ?? "") }] };
  // 해당(추천) 버전 데이터를 조회하도록 버전 동반. name 은 String() 필수(BigInt 직렬화 크래시 회피).
  if (version?.id != null) payload.version = { id: version.id, name: String(version.name ?? "") };
  dispatch(reduxUtil.removeViewState({ viewName: RESOURCE_UTILIZATION }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(RESOURCE_UTILIZATION, payload)));
  dispatch(reduxUtil.updateHistoryState(RESOURCE_UTILIZATION));
}

// 자재 소요계획 발주 리스트(MRP_ORDER_LIST) 드릴다운. item: {item_cd?, item_name?, loc_cd?, loc_name?} | null.
// item.item_cd 가 있으면 그 품목으로 필터, 없으면 필터 없이 화면만 연다(전체/주문별 무한 → 화면 링크만).
// MrpOrderList 는 viewState 의 filter.locations / extraItemCodes / version 을 읽는다.
// 품목 코드 검색조건 주입 — 화면 검색창에 코드가 텍스트로 보이도록 스칼라 itemSearch 사용.
//   단, 순수 숫자 코드는 getViewState 의 BigInt 재파싱 → MUI 크래시 위험이라 배열(extraItemCodes,
//   중첩이라 재파싱 비대상)로 폴백한다. 자재(품목) 코드는 대개 영숫자(SKU)라 itemSearch 경로로 표시된다.
function applyItemSearch(payload, item) {
  if (!item?.item_cd) return;
  const code = String(item.item_cd);
  if (/^\d+$/.test(code)) payload.extraItemCodes = [code];
  else payload.itemSearch = code;
}

export async function openMrpOrderList(dispatch, item, version) {
  const MRP_ORDER_LIST = "MRP_ORDER_LIST";
  const payload = {};
  applyItemSearch(payload, item);
  if (item?.loc_cd) payload.filter = { locations: [{ locCd: String(item.loc_cd), locName: String(item.loc_name ?? "") }] };
  // 해당(추천) 버전 데이터를 조회하도록 완전한 버전(planConfigObj 포함)을 조회해 동반한다.
  // MrpOrderList 는 초기 렌더에서 selectVersion.planConfigObj.BUCKET_UNIT 를 가드 없이 읽으므로,
  // 부분 버전({id,name})만 주입하면 크래시한다. planConfigObj 가 확인될 때만 주입한다.
  if (version?.id != null) {
    try {
      const { data } = await planVersionService.get(version.id);
      if (data?.planConfigObj) payload.version = data;
    } catch (e) {
      // 조회 실패 시 버전 주입 생략 → MrpOrderList 가 자체 기본(최신) 버전을 선택(크래시 회피).
    }
  }
  dispatch(reduxUtil.removeViewState({ viewName: MRP_ORDER_LIST }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(MRP_ORDER_LIST, payload)));
  dispatch(reduxUtil.updateHistoryState(MRP_ORDER_LIST));
}

// 자재 소요계획 리포트(MRP_REPORT) 드릴다운. item: {item_cd?, loc_cd?, loc_name?} | null.
// MrpReport 는 reduxViewState 의 version.id 로 자체 조회한 전체 버전 목록에서 재매칭하고
//   planConfigObj 접근을 가드하므로(MrpReport.js:198·498), 부분 버전({id,name})만 주입해도 안전하다.
export function openMrpReport(dispatch, item, version) {
  const MRP_REPORT = "MRP_REPORT";
  const payload = {};
  applyItemSearch(payload, item);
  if (item?.loc_cd) payload.filter = { locations: [{ locCd: String(item.loc_cd), locName: String(item.loc_name ?? "") }] };
  if (version?.id != null) payload.version = { id: version.id, name: String(version.name ?? "") };
  dispatch(reduxUtil.removeViewState({ viewName: MRP_REPORT }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(MRP_REPORT, payload)));
  dispatch(reduxUtil.updateHistoryState(MRP_REPORT));
}

// BOM 상세(INPUT_BOM_TREE) 드릴다운 — 해당 품목으로 필터, 전개 방향은 역전개(UP) 라디오 선택.
//   BomDetail 은 reduxViewState.itemSearch/extraItemCodes(품목)와 expansionType("DOWN"=정전개/"UP"=역전개)을 읽는다.
export function openBomDetail(dispatch, item) {
  const INPUT_BOM_TREE = "INPUT_BOM_TREE";
  const payload = { expansionType: "UP", currentPage: 1 };
  applyItemSearch(payload, item);
  // 우측 BOM 트리는 좌측 그리드의 행 선택으로 조회된다 — selectedRow(itemCd) 주입 시 좌측 로드 후
  //   해당 행이 자동 선택(onSelectionChanged)되어 우측이 조회된다(선택된 전체 행으로 itemType 확정).
  if (item?.item_cd) payload.selectedRow = { itemCd: String(item.item_cd) };
  dispatch(reduxUtil.removeViewState({ viewName: INPUT_BOM_TREE }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(INPUT_BOM_TREE, payload)));
  dispatch(reduxUtil.updateHistoryState(INPUT_BOM_TREE));
}

// 주기 캘린더(INPUT_CALENDAR) 화면으로 이동 — 해당 휴무 캘린더 그룹으로 자동 필터·조회.
//   CycleCalendarMaster 가 reduxViewState.calendarGrp 를 id 로 매칭(retrieveCalendarGroup)하므로
//   {id, name} 을 동반 주입한다. id 는 calendarGrp 객체에 중첩되어 top-level BigInt 직렬화 크래시 비대상
//   (버전 주입 openResourceUtilization 과 동일 패턴). 두 데이터 경로가 동일 json-bigint 파서라 타입 일치.
export function openCycleCalendar(dispatch, calGrpId, calGrpName) {
  if (calGrpId == null) return;
  const INPUT_CALENDAR = "INPUT_CALENDAR";
  const payload = { calendarGrp: { id: calGrpId, name: String(calGrpName ?? "") } };
  dispatch(reduxUtil.removeViewState({ viewName: INPUT_CALENDAR }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(INPUT_CALENDAR, payload)));
  dispatch(reduxUtil.updateHistoryState(INPUT_CALENDAR));
}

// 자재 입고 계획(INPUT_MATERIAL_RECEIPT_PLAN) 드릴다운. item: {item_cd?, loc_cd?, loc_name?} | null.
// 입고 계획은 버전 무관(데이터 관리 화면) → 버전 미주입. 품목은 itemSearch, 거점은 locSearch.
// 조회기간: 시작일은 제약 해제(과거 전체), 종료일은 계획 종료 일자(version.planConfigObj.PLAN_END_DATE).
export async function openMaterialReceiptPlan(dispatch, item, version) {
  const INPUT_MATERIAL_RECEIPT_PLAN = "INPUT_MATERIAL_RECEIPT_PLAN";
  const payload = {};
  applyItemSearch(payload, item);
  // locSearch 는 객체(중첩)라 BigInt 재파싱 비대상 — locCd 는 코드 문자열이므로 String() 으로 충분.
  if (item?.loc_cd) payload.locSearch = { locCd: String(item.loc_cd), name: String(item.loc_name ?? "") };
  // 시작일 제약 해제(화면 허용 범위 하한). 종료일은 계획 종료 일자 — 추천 버전의 planConfigObj 에서 조회.
  const y = new Date().getFullYear();
  let end = `${y + 10}-01-01`;
  if (version?.id != null) {
    try {
      const { data } = await planVersionService.get(version.id);
      if (data?.planConfigObj?.PLAN_END_DATE) end = data.planConfigObj.PLAN_END_DATE;
    } catch (e) {
      // 조회 실패 시 종료일 폴백(현재년+10) 유지.
    }
  }
  payload.period = { start: `${y - 10}-01-01`, end };
  dispatch(reduxUtil.removeViewState({ viewName: INPUT_MATERIAL_RECEIPT_PLAN }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(INPUT_MATERIAL_RECEIPT_PLAN, payload)));
  dispatch(reduxUtil.updateHistoryState(INPUT_MATERIAL_RECEIPT_PLAN));
}

// 생산 자원 마스터(INPUT_PRODUCTION_RESOURCE) 드릴다운. res: {resource_cd?, resource_name?, loc_cd?, loc_name?} | null.
// 자원 마스터는 버전 무관(데이터 관리 화면) → 버전 미주입. 거점·자원은 locationSearch/resourceSearch 객체(중첩→BigInt 안전).
export function openProductionResource(dispatch, res) {
  const INPUT_PRODUCTION_RESOURCE = "INPUT_PRODUCTION_RESOURCE";
  const payload = {};
  if (res?.loc_cd) payload.locationSearch = { locCd: String(res.loc_cd), name: String(res.loc_name ?? "") };
  if (res?.resource_cd) payload.resourceSearch = { resCd: String(res.resource_cd), name: String(res.resource_name ?? "") };
  dispatch(reduxUtil.removeViewState({ viewName: INPUT_PRODUCTION_RESOURCE }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(INPUT_PRODUCTION_RESOURCE, payload)));
  dispatch(reduxUtil.updateHistoryState(INPUT_PRODUCTION_RESOURCE));
}

// 자원 생산 계획(RESOURCE_PRODUCTION_PLAN) 드릴다운. res: {resource_cd?, resource_name?, loc_cd?, loc_name?} | null.
// ResourceProductionPlan 은 초기 retrieve 에서 selectVersion.planConfigObj.BUCKET_UNIT 를 가드 없이 읽으므로
//   (ResourceProductionPlan.js:247) 완전한 버전(planConfigObj 포함)을 조회해 주입한다(MrpOrderList 와 동일 패턴).
export async function openResourceProductionPlan(dispatch, res, version) {
  const RESOURCE_PRODUCTION_PLAN = "RESOURCE_PRODUCTION_PLAN";
  const payload = {};
  if (res?.loc_cd) payload.filter = { locations: [{ locCd: String(res.loc_cd), locName: String(res.loc_name ?? "") }] };
  if (res?.resource_cd) payload.resourceSearch = { resCd: String(res.resource_cd), name: String(res.resource_name ?? "") };
  if (version?.id != null) {
    try {
      const { data } = await planVersionService.get(version.id);
      if (data?.planConfigObj) payload.version = data;
    } catch (e) {
      // 조회 실패 시 버전 주입 생략 → 화면이 자체 기본(최신) 버전을 선택(크래시 회피).
    }
  }
  dispatch(reduxUtil.removeViewState({ viewName: RESOURCE_PRODUCTION_PLAN }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(RESOURCE_PRODUCTION_PLAN, payload)));
  dispatch(reduxUtil.updateHistoryState(RESOURCE_PRODUCTION_PLAN));
}

// FLO 차트(MP_FLO_CHART) 드릴다운. item: {item_cd?} | null.
// FloChart 는 마운트 시 reduxViewState.selectedItemCd(루트 품목 코드 스칼라)를 읽어 그 품목으로 그래프를 조회한다.
// FLO 는 버전 무관(최신 모델 그래프) → 버전 미주입. 품목 코드가 숫자여도 FloChart 가 String() 으로 방어(FloChart.js 참조).
export function openFloChart(dispatch, item) {
  const MP_FLO_CHART = "MP_FLO_CHART";
  const payload = {};
  if (item?.item_cd) payload.selectedItemCd = String(item.item_cd);
  dispatch(reduxUtil.removeViewState({ viewName: MP_FLO_CHART }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(MP_FLO_CHART, payload)));
  dispatch(reduxUtil.updateHistoryState(MP_FLO_CHART));
}

// 자원 간트차트(MP_RESOURCE_GANTT) 드릴다운. res: {resource_cd?, resource_name?} | null.
// ResourceGantt 는 마운트 시 reduxViewState.resource(자원 다중선택 배열)와 mpVersion 을 읽어 조회한다(version.id 필수).
// [주의] 공통 ResourceFilter 는 마운트 시 초기 선택을 `selectedResource.includes(result.resCd)` 로 검증한다.
//   includes 는 원시값 === 비교라, 자원을 {resCd,name} 객체 배열로 주입하면 절대 매칭되지 않아(객체≠문자열)
//   필터가 선택을 null 로 초기화 → 전체 조회가 된다. 그래서 여기서는 "자원 코드 문자열 배열"로 주입한다.
//   ResourceFilter 가 코드 매칭에 성공하면 스스로 전체 자원 객체({resCd,name,…})로 교체하므로,
//   이후 간트 서버 조회(resource.map(r => r.resCd))는 정상적으로 해당 자원만 필터한다.
//   (문자열은 배열 원소라 getViewState 의 top-level BigInt 재파싱 대상이 아님.)
// [2회차 전체조회 버그 방지] ResourceFilter 는 코드 매칭 성공 시 선택을 {resCd,name} 객체로 바꿔 같은
//   viewState.resource 에 되쓴다. 자원 마스터 캐시(RESOURCE_FILTER)가 남아 있으면 다음 진입 시 동기 경로로
//   그 되쓴 객체를 재검증하다 includes(객체≠코드) 실패 → 선택을 비워 전체 조회가 된다(첫 진입은 캐시 없어
//   비동기 경로라 정상). 진입 전 캐시를 비워 항상 비동기(정상) 경로로 강제한다.
// 버전은 완전 버전(planConfigObj 포함)을 주입해 기간 레이스를 피한다(MpVersionFilter 재매칭, goToOrderTracking 과 동일 패턴).
export async function openResourceGantt(dispatch, res, version) {
  const MP_RESOURCE_GANTT = "MP_RESOURCE_GANTT";
  const payload = {};
  if (res?.resource_cd) payload.resource = [String(res.resource_cd)];
  if (version?.id != null) {
    try {
      const { data } = await planVersionService.get(version.id);
      payload.mpVersion = data?.planConfigObj ? data : { id: version.id, name: String(version.name ?? "") };
    } catch (e) {
      payload.mpVersion = { id: version.id, name: String(version.name ?? "") }; // 조회 실패 시 부분 주입
    }
  }
  reloadResourceFilter(dispatch);   // 자원 마스터 캐시 비우기 → 매 진입 비동기 로드(정상) 경로 강제
  dispatch(reduxUtil.removeViewState({ viewName: MP_RESOURCE_GANTT }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(MP_RESOURCE_GANTT, payload)));
  dispatch(reduxUtil.updateHistoryState(MP_RESOURCE_GANTT));
}

// ── MP ExtraInitContent (현황 분석 탭) ──────────────────────────────────────
// AiScenarioPlanning.js 의 현황 분석 탭(Tab 0)에서 mod.ExtraInitContent 로 렌더됨
export function MpExtraInitContent({ mod, phase, versionInfo, cachedData, onDataChange, onReadyChange }) {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  /*
   * 드릴다운 링크 권한 게이트 — 권한 밖 화면은 **핸들러를 주지 않는다**.
   *   NavName 이 onClick 이 함수가 아닌 항목을 스스로 걸러내므로(scenarioStatusUi.js), 링크 목록에서
   *   그 줄이 사라지고 남은 대상이 하나면 바로 이동, 하나도 없으면 평범한 텍스트가 된다.
   */
  const { isPermitted } = useMenuAccess();
  const navIf = (target, enabled, fn) => (enabled && isPermitted(target) ? fn : undefined);
  const [data,    setData]    = useState(cachedData ?? null);
  const [loading, setLoading] = useState(false);   // 기본(LLM 외) 데이터 로딩
  const [aiLoading, setAiLoading] = useState(false); // LLM 요약(주요 이슈/문제점) 로딩
  // 증상별 보기 — 전체(문제 규모순) / 지연 / 부족. 기본은 전체(현행 동작 유지).
  //   부족 ≫ 지연 이지만, 부족 0건인 계획에서 빈 목록으로 시작하지 않도록 전체를 기본으로 둔다.
  //   좌(품목)·우(자원)는 서로 다른 질문에 답하므로 상태를 분리한다 — "품목은 전반, 자원은
  //   부족 주범만" 같은 조합이 성립한다. 0건 축은 비활성(누르기 전) + axisOr 폴백(누른 뒤
  //   데이터가 바뀐 경우)으로 막아, 한쪽이 비어 '문제 없음' 초록으로 오독되는 일이 없다.
  const [itemViewSel, setItemViewSel] = useState("total");
  const [resViewSel, setResViewSel]   = useState("total");
  const [err,     setErr]     = useState(null);

  // 병목 자원 TOP5 자원명 클릭 → 자원 부하 분석 / 자원 간트차트 중 선택해 드릴다운(NavName targets).
  // 현황 분석 기준 버전(versionInfo) · 거점 · 자원 검색조건을 채운 채 자동 조회되도록 viewState 를 주입한다.
  // (자원 부하 분석 화면은 버전 옵션 잘림 이슈가 없어 기준 버전 주입이 안전 — 비교 탭 경고 링크와 동일 경로.)
  const goToResourceUtilization = (res) => openResourceUtilization(reduxDispatch, res, versionInfo);

  // 문제 주문 현황 품목명 클릭 → 주문 추적 / FLO 차트 중 선택해 드릴다운(NavName targets).
  // 현황 분석 기준 버전 · 거점 · 품목 + 주문상태(지연·부족) 검색조건을 채운 채 자동 조회되도록 viewState 를 주입한다.
  const goToOrderTracking = async (row) => {
    if (!row?.item_cd && !row?.loc_cd) return; // 식별 불가 시 무시
    const ORDER_TRACKING = "ORDER_TRACKING";
    const payload = {
      mpOrderStatus: ["DELAY", "SHORT"], // 주문상태: 지연(DELAY) · 부족(SHORT) — CodeUtil.orderStatusCode
    };
    // 현황 분석 기준 버전을 동반 주입 → 주문 추적 화면이 해당 버전 데이터를 조회한다(mpVersion = MpVersionFilter 가 읽는 키).
    //   planConfigObj 까지 포함한 완전 버전을 주입한다. {id,name} 만 주입하면 OrderTracking 이 버전을 '준비됨'으로
    //   보고 planConfigObj 없는 잘못된 기간(Invalid)으로 1차 조회 → MpVersionFilter 가 재매칭해 완전 객체로 교체 →
    //   올바른 기간으로 2차 조회가 일어나 두 비동기 응답이 엇갈려 화면이 깜빡인다(건수 1↔다수). 완전 객체로 기간을 처음부터 확정.
    //   id 는 객체 안에 중첩되어 getViewState 의 parseJsonWithBigInt 재파싱 비대상, name 은 String() 강제(BigInt 직렬화 크래시 회피).
    //   (MpVersionFilter 는 버전 100개까지만 옵션에 노출하지만, 통상 사용에서 버전이 100개를 넘지 않으므로 기준 버전이 잘릴 일은 없다.)
    if (versionInfo?.id != null) {
      try {
        const { data } = await planVersionService.get(versionInfo.id);
        payload.mpVersion = data?.planConfigObj ? data : { id: versionInfo.id, name: String(versionInfo.name ?? "") };
      } catch (e) {
        payload.mpVersion = { id: versionInfo.id, name: String(versionInfo.name ?? "") }; // 조회 실패 시 부분 주입(기간 레이스 가능성은 있으나 동작은 유지)
      }
    }
    // 거점은 객체 안에 중첩되어 getViewState 의 parseJsonWithBigInt 재파싱을 받지 않으므로 String() 으로 충분.
    if (row.loc_cd)  payload.filter = { locations: [{ locCd: String(row.loc_cd), locName: String(row.loc_name ?? "") }] };
    // 품목은 itemSearch(스칼라)로 주입해 입력란에 코드가 텍스트로 보이게 한다.
    // (스칼라 숫자 문자열은 getViewState 가 BigInt 로 재파싱하므로, 대상 OrderTracking 이 읽을 때 String() 으로
    //  되돌린다 — OrderTracking.js itemSearch ref 초기화에 String() 방어 추가.)
    if (row.item_cd) payload.itemSearch = String(row.item_cd);
    // updateViewState 는 병합 리듀서라 이전 드릴다운의 stale itemSearch 등이 남아 BigInt 재파싱 크래시를 유발한다
    // → 먼저 비우고 우리 payload 만 남긴다.
    reduxDispatch(reduxUtil.removeViewState({ viewName: ORDER_TRACKING }));
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(ORDER_TRACKING, payload)));
    reduxDispatch(reduxUtil.updateHistoryState(ORDER_TRACKING));
  };

  const lang = getAgentLanguage();   // LLM 문장은 이 언어로 생성된다 — 바뀌면 요약을 다시 받는다.

  // 1) LLM 외 기본 데이터(KPI·과부하 TOP5·문제 주문 등)를 먼저 빠르게 조회 → 즉시 렌더.
  const load = useCallback(() => {
    setLoading(true); setErr(null);
    return scenarioService.getMpInitialState(false)
      .then(r => setData(prev => (prev?.status_narrative
        ? { ...r, status_narrative: prev.status_narrative }   // 요약이 먼저 도착했으면 보존
        : r)))
      .catch(e => setErr(e?.response?.data?.detail || e.message || t("MSG_IpQueryError")))
      .finally(() => setLoading(false));
  }, [t]);

  // 2) LLM 요약(status_narrative)은 별도로(느림) 조회 → 도착하면 주요 이슈/문제점에 반영.
  //    _lang 도 함께 갱신 — 안 넘기면 prev 의 옛 언어가 남아 매번 재조회 대상이 된다.
  const loadNarrative = useCallback(() => {
    setAiLoading(true);
    scenarioService.getMpInitialState(true)
      .then(r => setData(prev => ({ ...(prev ?? r), status_narrative: r.status_narrative, _lang: r._lang })))
      .catch(() => { /* 실패해도 기본 데이터는 유지 */ })
      .finally(() => setAiLoading(false));
  }, []);

  useEffect(() => {
    if (cachedData) {
      // 기본만 캐시된 경우 요약만 보강(빠른 데이터는 이미 있음).
      // 언어가 바뀌면 캐시된 요약은 옛 언어 그대로라 다시 받는다(고정 라벨만 바뀌고 LLM 문장은 안 바뀜).
      if (!cachedData.status_narrative || cachedData._lang !== lang) loadNarrative();
      return;
    }
    // 기본 데이터를 먼저 조회한 뒤 LLM 요약을 조회 — 백엔드 기본 state TTL 캐시에 적중시켜
    // 기본 쿼리가 두 번 실행되지 않게 한다(빠른 데이터는 이미 화면에 렌더됨).
    load().finally(loadNarrative);
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // 데이터 변경 시 부모에 캐시 전달
  useEffect(() => {
    if (data) onDataChange?.(data);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // 조회 완료 여부를 부모에 보고 → 상단 액션바(자동 수행 / 시나리오 구성으로) 잠금 해제 기준.
  //   기준은 기본 데이터(load)뿐이며 LLM 요약(aiLoading)은 넣지 않는다 — 요약 대기는 구성 탭의
  //   AI 추천 버튼 게이트(aiRecNeedsRecommended/mpNarr)가 이미 담당하고, 여기까지 묶으면
  //   느린 LLM 때문에 탭 이동이 수십 초 잠긴다.
  useEffect(() => {
    onReadyChange?.(!loading && (!!data || !!err));
  }, [loading, data, err]); // eslint-disable-line react-hooks/exhaustive-deps

  // 기간별 현황 추이 — 전사+그룹별 일자 데이터를 일괄 보유하고 세그먼트·버킷·그룹을 로컬 전환한다(재조회 없음).
  // ※ 훅은 아래 loading/err early return 보다 반드시 앞에 있어야 한다(훅 순서 규칙).
  const [trendViewSel, setTrendViewSel] = useState("capa");   // capa | otd
  const [trendGrain,   setTrendGrain]   = useState("day");    // day | week | month — 두 차트 공용
  const [trendGroup,   setTrendGroup]   = useState("");       // "" = 전 라인 합산
  const [otdGroup,     setOtdGroup]     = useState("");       // "" = 전 품목군 합산
  // 버전이 바뀌어 사라진 코드가 선택돼 있으면 빈 Select 트리거가 뜬다 → 전체로 되돌린다.
  const trendGroupSel = (data?.capa_load_trend?.group_labels || {})[trendGroup] ? trendGroup : "";
  const otdGroupSel   = (data?.otd_trend?.group_labels || {})[otdGroup] ? otdGroup : "";
  const planStart = data?.plan_period?.start || null;
  const planEnd   = data?.plan_period?.end   || null;

  // 미집계 = 릴리즈 결과(z_ai_fact_mp) 미적재. 응답 공통 필드 하나로 판정하며, 이 화면의
  // 모든 데이터 소스를 이 값 하나로 일괄 게이트한다(모듈 간 표현 편차 제거).
  const mpNotAggregated = !data || data?.released_fact_available === false;
  // 최근 실행 기록(빈 상태 패널 우측 요약) — 전 모듈 공용 훅. 미집계일 때만 1건 조회한다.
  const lastRun = useLastRunSummary("MP", mpNotAggregated);

  const selectedTrend = useMemo(() => {
    const td = data?.capa_load_trend;
    if (!td) return null;
    const raw = trendGroupSel ? (td.by_group?.[trendGroupSel] ?? { data: [], series: [] }) : td.all;
    if (!raw?.data?.length) return raw;
    // 백엔드가 주는 (Capa 상한, 부하) 2계열을 누적 막대용 3계열로 분해한다.
    // 스택 총합 = max(부하, 상한) → 정상일엔 막대 높이가 곧 상한, 과부하일엔 빨강이 상한 위로 솟는다.
    // 상한선을 별도 계열로 그리지 않는 이유: 가용/과잉 세그먼트의 경계가 그대로 상한선이다.
    const daily = raw.data.map((row) => {
      const total = row.capa_total_h;
      const load  = row.capa_load_h;
      // 둘 중 하나라도 없으면 막대를 그리지 않는다(0 으로 채우면 가동 없는 날처럼 보인다)
      if (total == null || load == null) {
        return { ...row, capa_load_in_h: null, capa_avail_h: null, capa_over_h: null };
      }
      return {
        ...row,
        capa_load_in_h: Math.min(load, total),
        capa_avail_h:   Math.max(0, total - load),
        capa_over_h:    Math.max(0, load - total),
      };
    });
    // 롤업은 상한/부하가 아니라 **분해된 3계열**을 더한다. 상한을 먼저 합치면 어느 하루의 과부하가
    // 다른 날 여유에 상쇄돼 사라진다. 이렇게 더해도 (부하+가용)의 합 = 상한의 합이라 경계는 그대로다.
    return {
      ...raw,
      data: rollupDays(fillDays(daily, planStart, planEnd, {}), CAPA_TREND_BAR_KEYS, trendGrain),
      series: [
        { key: "capa_load_in_h", name_key: "mpSeriesCapaLoad" },
        { key: "capa_avail_h",   name_key: "mpSeriesCapaAvail" },
        { key: "capa_over_h",    name_key: "mpSeriesCapaOver" },
      ],
    };
  }, [data, trendGroupSel, trendGrain, planStart, planEnd]);
  // ⚠️ 미집계면 릴리즈 fact 에서 파생되지 않는 보조 데이터(자원 가동률 추이 등)도 함께 감춘다 —
  //   일부 섹션만 값이 남으면 "어디는 보이고 어디는 —"인 반쪽 화면이 된다(전 모듈 공통 원칙).
  const capaTrendHasData = !mpNotAggregated
    && (data?.capa_load_trend?.all?.data?.length || 0) > 0;
  // 라인(wc_cd) 드롭다운 옵션 — z_resource_util 에 wc_name 이 없어 라벨=코드
  const trendGroupOptions = useMemo(() => {
    const labels = data?.capa_load_trend?.group_labels || {};
    return Object.entries(labels).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [data]);

  const otdHasData = !mpNotAggregated && (data?.otd_trend?.all?.data?.length || 0) > 0;
  // 고른 세그먼트에 데이터가 없으면 있는 쪽으로 넘긴다 — 데이터는 나중에 도착해 초기값으로는 못 정한다.
  const trendView = (trendViewSel === "capa" && !capaTrendHasData && otdHasData) ? "otd"
                  : (trendViewSel === "otd"  && !otdHasData && capaTrendHasData) ? "capa"
                  : trendViewSel;
  const otdGroupOptions = useMemo(() => {
    const labels = data?.otd_trend?.group_labels || {};
    return Object.entries(labels).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [data]);

  const selectedOtdTrend = useMemo(() => {
    const td = data?.otd_trend;
    if (!td) return null;
    const raw = otdGroupSel ? (td.by_group?.[otdGroupSel] ?? { data: [], series: [] }) : td.all;
    if (!raw?.data?.length) return raw;
    // 주문 없는 날은 0 으로 채운다 — 능력 차트와 달리 '그날 납기 주문이 0건'이 사실이다.
    const dense = fillDays(raw.data, planStart, planEnd, { otd_count: 0, late_count: 0, short_count: 0 });
    return { ...raw, data: rollupDays(dense, OTD_TREND_BAR_KEYS, trendGrain) };
  }, [data, otdGroupSel, trendGrain, planStart, planEnd]);

  const kpi        = mpNotAggregated ? {} : (data?.kpi_current ?? {});

  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: "16px !important" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} sx={{ color: C.acc }} />
            <Typography variant="caption" color="text.secondary">
              {t("mpStatusAnalyzingDesc")}
            </Typography>
          </Stack>
          <LinearProgress sx={{ mt: 1.5, borderRadius: 2, height: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${C.g3}30` }}>
        <CardContent sx={{ p: "10px 14px !important" }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <WarningAmberIcon sx={{ fontSize: 15, color: C.g2 }} />
            <Typography variant="caption" sx={{ color: C.g2 }}>{t("MSG_IpStatusQueryFailed", { err })}</Typography>
            <IconButton size="small" onClick={load} sx={{ ml: "auto !important", p: "3px" }}>
              <RefreshIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const otdRate     = kpi.otd_rate_pct;
  const lateCnt      = kpi.late_order_count;
  const shortCnt     = kpi.short_order_count;   // 공급 부족 주문 — OTD 정의상 지연과 함께 '납기 미준수'로 집계
  const lateQty      = kpi.late_order_qty;      // 지연 수량(EA) — 조치 규모(만회 물량)
  const shortQty     = kpi.short_order_qty;     // 부족 수량(EA) — 신규 발주·증설 규모와 직결
  const lateAmt      = kpi.late_order_amt;      // 지연 금액 — 물량×단가(우선순위 렌즈)
  const shortAmt     = kpi.short_order_amt;     // 부족 금액 — 물량×단가
  const avgUtil      = kpi.avg_util_pct;
  const overloadCnt  = kpi.overload_resource_count;
  const totalOrdCnt  = kpi.total_order_count;   // OTD 카드 footer 분모(전체 주문) 및 납기 준수 건수 계산
  const avgDelay     = kpi.avg_delay_days;
  const materialCnt  = kpi.material_cause_count;
  const capacityCnt  = kpi.capacity_cause_count;

  // 납기 준수 건수 = 전체 − 지연 − 부족. (백엔드 OTD 정의: 미준수 = 지연 + 부족, otd_rate_pct = 100 − 미준수율)
  //   → 카드도 동일 축(증상)으로 분해: 미준수 = '납기 지연'(late) + '공급 부족'(short). 두 카드 합이 (전체 − 준수)와 일치.
  const otdMetCnt    = (totalOrdCnt != null && lateCnt != null && shortCnt != null)
    ? Number(totalOrdCnt) - Number(lateCnt) - Number(shortCnt) : null;
  // 공급 부족(short) 주문의 원인 분해 — SHORT 전용 크로스탭 우선, 미적재(DDL 미반영) 시 전체 원인으로 폴백.
  const shortMatCnt  = kpi.short_material_count;
  const shortCapCnt  = kpi.short_capacity_count;
  const hasShortCause = shortMatCnt != null && shortCapCnt != null
    && (Number(shortMatCnt) + Number(shortCapCnt)) > 0;
  const causeMat     = hasShortCause ? Number(shortMatCnt) : Number(materialCnt || 0);
  const causeCap     = hasShortCause ? Number(shortCapCnt) : Number(capacityCnt || 0);
  // 능력 초과 자원 — z_resource_util.capa_util > 100 인 자원 수(백엔드 집계). 필드 없으면 0.
  const overCapResCnt = kpi.over_capacity_resource_count ?? null;
  // 전체 자원 수 — 과부하 자원 비율(overload/total)의 분모. 미집계면 null(0 으로 접지 않는다).
  const totalResCnt   = kpi.total_resource_count ?? null;

  // OTD 3단계: 95% 이상 정상(g1) / 85~95% 주의(g2) / 85% 미만 심각(g3). 값 없으면 fail-safe 심각.
  //   목표 95% 는 유지하되(월드클래스 축), 85~95% "거의 준수" 계획을 항상 빨강으로 몰던 문제 해소.
  const otdTier = (() => {
    if (otdRate == null) return { color: C.g3, bg: C.g3l };
    const v = Number(otdRate);
    if (v >= 95) return { color: C.g1, bg: C.g1l };
    if (v >= 85) return { color: C.g2, bg: C.g2l };
    return { color: C.g3, bg: C.g3l };
  })();
  const utilOk     = avgUtil  != null && Number(avgUtil)  >= 60 && Number(avgUtil) <= 95;

  // 규모(비율) 기반 3단계: okRate 이하→정상(g1) / warnRate 이하→주의(g2) / 초과→심각(g3).
  //   부족은 okRate=0(1건이라도 있으면 최소 주의), 병목은 소수 병목이 현장 정상이라 okRate 여유.
  const rateTier = (cnt, denom, warnRate, okRate = 0) => {
    const n = Number(cnt) || 0;
    const r = Number(denom) > 0 ? n / Number(denom) : (n > 0 ? 1 : 0);
    if (r <= okRate) return { color: C.g1, bg: C.g1l };
    return r <= warnRate ? { color: C.g2, bg: C.g2l } : { color: C.g3, bg: C.g3l };
  };
  // 심각도 위계: 부족(발주·증설 필요한 구조 결손) > 지연(독촉·잔업·외주로 만회 가능).
  //   지연은 2단계(정상/주의)만 — 만회 가능해 심각(빨강)으로 격상하지 않는다(내부 회의 반영). 1건이라도 있으면 주의.
  const lateTier     = (Number(lateCnt) || 0) > 0 ? { color: C.g2, bg: C.g2l } : { color: C.g1, bg: C.g1l };
  const shortTier    = rateTier(shortCnt,    totalOrdCnt, 0.10);
  const overloadTier = rateTier(overloadCnt, totalResCnt, 0.20, 0.05);
  const lateRatePct     = totalOrdCnt > 0 ? (Number(lateCnt)     || 0) / totalOrdCnt * 100 : 0;
  const overloadRatePct = totalResCnt > 0 ? (Number(overloadCnt) || 0) / totalResCnt * 100 : 0;

  // ── 서브라인 2줄 구조(디자이너 권장) ── 윗줄(sub)=정도(율/평균/건수), 아랫줄(sub2)=규모(수량·금액).
  //   sub2 는 "" 라도 컴포넌트가 높이 고정용으로 렌더 → 5개 카드 서브 블록 높이 통일(빈 슬롯 정렬).
  // 규모 서브(EA·금액) — 단위가 곧 라벨이라 접두어 없음(디자이너 원칙: 모호한 단위만 라벨).
  const qtyAmtSub = (qty, amt) => t("mpQtyAmtSubLabel", { qty: fmtCount(qty ?? 0), amt: fmtAmt(amt) });
  // OTD 롤업 = 총 납기리스크 금액(지연 금액 + 부족 금액). 분해 카드엔 없는 bottom-line 합계라 요약 카드에만.
  //   색/심각도 판정엔 미사용 — 규모 정보 전용(만회가능 지연 + 구조결손 부족 혼합이라 심각도 축과 분리).
  const riskAmt = (Number(lateAmt) || 0) + (Number(shortAmt) || 0);

  // 값이 없으면 "--" 문자열이 아니라 null — 표기·단위·상태색은 StatusKpiCard 가 결정한다.
  const kpiCards = [
    { label: t("mpKpiOtdLabel"),  value: otdRate  != null ? fmtPct(otdRate)            : null, unit: t("percentUnit"), color: otdTier.color, bg: otdTier.bg,
      sub: otdMetCnt != null ? t("mpOtdMetSubLabel", { met: fmtCount(otdMetCnt), total: fmtCount(totalOrdCnt) }) : t("mpTarget95PctLabel"),
      sub2: riskAmt > 0 ? t("mpOtdRiskAmtSubLabel", { amt: fmtAmt(riskAmt) }) : "" },
    { label: t("mpKpiLateOrderCountLabel"),     value: lateCnt  != null ? fmtCount(lateCnt)  : null, unit: t("caseUnitLabel"), color: lateTier.color,  bg: lateTier.bg,
      sub: (lateCnt != null && Number(lateCnt) > 0) ? t("mpLateRateSubLabel", { pct: fmtPct(lateRatePct), days: fmtPct(avgDelay ?? 0) }) : t("mpTarget0CaseLabel"),
      sub2: (lateCnt != null && Number(lateCnt) > 0) ? qtyAmtSub(lateQty, lateAmt) : "" },
    { label: t("mpKpiShortOrderCountLabel"),     value: shortCnt != null ? fmtCount(shortCnt) : null, unit: t("caseUnitLabel"), color: shortTier.color, bg: shortTier.bg,
      // 부족>0 이면 원인 분해(자재/능력)를 서브라인으로 노출 — 조치 주체(발주 vs 증설)가 갈려 트리아지에 직결.
      //   부족 0 이면 원인 분해가 무의미(0/0)하므로 지연 카드와 동일한 '목표 0건' 문구로 통일.
      sub: (shortCnt != null && Number(shortCnt) > 0) ? t("mpShortCauseSubLabel", { mat: fmtCount(causeMat), cap: fmtCount(causeCap) }) : t("mpTarget0CaseLabel"),
      sub2: (shortCnt != null && Number(shortCnt) > 0) ? qtyAmtSub(shortQty, shortAmt) : "" },
    { label: t("mpKpiBottleneckResCountLabel"),       value: overloadCnt != null ? fmtCount(overloadCnt) : null, unit: t("unitCount"), color: overloadTier.color, bg: overloadTier.bg,
      sub: (overloadCnt != null && Number(overloadCnt) > 0 && totalResCnt > 0) ? t("mpOverloadRateSubLabel", { pct: fmtPct(overloadRatePct), cnt: fmtCount(overloadCnt), total: fmtCount(totalResCnt) }) : t("mpOverloadResSubLabel"),
      sub2: "" },
    { label: t("mpKpiAvgUtilLabel"),        value: avgUtil  != null ? fmtPct(avgUtil)            : null, unit: t("percentUnit"), color: utilOk  ? C.g1 : C.g2, bg: utilOk  ? C.g1l : C.g2l,
      sub: t("mpOverCapaResSubLabel", { count: fmtCount(overCapResCnt) }),
      sub2: "" },
  ];

  // ── 주요 이슈 / 문제점 — kpi_current 로부터 재구성 ─────────────────────────
  const issueItems = (() => {
    const items = [];
    if (Number(overloadCnt) > 0)
      items.push({ level: "warning", title: t("mpResourceBottleneckTitle"),   desc: t("mpIssueOverloadDesc", { count: Math.round(overloadCnt) }), kpiLabel: t("mpBottleneckShortLabel"),   count: Math.round(overloadCnt) });
    if (Number(materialCnt) > 0)
      items.push({ level: "error",   title: t("mpMaterialShortageOrderTitle"), desc: t("mpIssueMaterialShortageDesc", { count: Math.round(materialCnt) }), kpiLabel: t("ProblemTokenMaterialShortage"), count: Math.round(materialCnt) });
    if (Number(capacityCnt) > 0)
      items.push({ level: "error",   title: t("mpCapacityShortageOrderTitle"), desc: t("mpIssueCapacityShortageDesc", { count: Math.round(capacityCnt) }), kpiLabel: t("mpCapacityShortageShortLabel"), count: Math.round(capacityCnt) });
    // ⚠️ "이슈 없음 / 정상 / 0건" 합성 행을 넣지 않는다 — 집계가 없는 구간에서 그 행은
    //    "문제가 없다"는 거짓 확인이 된다. 빈 목록은 호출부가 StatusEmpty(중립)로 렌더한다.
    return items;
  })();

  // ── 병목 자원 TOP 5 — 공통 키 highlights (구 critical_resources, util_pct 내림차순) ──
  //   증상별 보기(전체/지연/부족)는 축마다 후보군이 다르다 → highlight_views 를 그대로 쓰고
  //   highlights 를 프런트에서 거르지 않는다(거르면 부족 주범이 total TOP5 밖일 때 영영 안 보임).
  //   구버전 응답(highlight_views 없음)은 기존대로 highlights 만 표시.
  const resViews = data?.highlight_views ?? null;
  const resView = resViews ? axisOr(resViews, resViewSel) : resViewSel;
  const criticalResources = mpNotAggregated ? null
    : ((resViews ? resViews[resView]?.rows : data?.highlights) ?? null);
  const resViewTabs = resViews ? buildViewTabs(resViews, t) : [];

  // ── 예외 주문 목록 — exception_items (조회 API 연동 시 채워짐) ───────────────
  // 백엔드 /mp/initial-state 가 exception_items 를 반환하면 표시되고, 없으면 빈 상태.
  //   증상별 보기는 exception_views 를 그대로 쓴다(자원 쪽과 동일 — 프런트에서 total 목록을
  //   거르면 부족 주범 품목이 total TOP5 밖일 때 영영 안 보임). 구버전 응답은 토글 미표시.
  const itemViews = data?.exception_views ?? null;
  const itemView = itemViews ? axisOr(itemViews, itemViewSel) : itemViewSel;
  const exceptionItems = mpNotAggregated ? null
    : ((itemViews ? itemViews[itemView]?.rows : data?.exception_items) ?? null);
  const itemViewTabs = itemViews ? buildViewTabs(itemViews, t) : [];

  // 주요 이슈 / 문제점 — main_causes 는 KPI 결정형(LLM 불필요)이라 빠른 응답 top-level 에 실려 온다.
  //   느린 요약(recommended_actions) 대기 없이 즉시 렌더 → top-level 우선, 구버전 호환으로 status_narrative 폴백.
  const aiCauses = data?.main_causes ?? data?.status_narrative?.main_causes ?? null;
  const useAiCauses = Array.isArray(aiCauses) && aiCauses.length > 0;
  const causeNames = collectDataNames(data);   // 실데이터 명칭(자원·품목·거점) 빨강 강조용

  // 주요 이슈 카드(구조화 type+수치+명칭)를 화면 문구로 조립 — 표시 문언은 전부 i18n(t),
  //   명칭(names)만 실데이터 보간. 백엔드 build_main_causes 가 언어 무관 구조만 반환한다.
  const describeCause = (c) => {
    // 대상 — 백엔드는 [{name, loc_name, count}] 를 준다(구버전 캐시는 문자열 배열).
    //   count = 그 대상 자신의 건수. 병기하지 않으면 카드 상단 전역 건수(예: 부족 14건)가
    //   대상 하나의 몫으로 읽힌다(부족 1건짜리 자원이 14건의 주범처럼 보임).
    //   거점은 조치처 식별용이자 행의 그레인 — 빼면 그 건수가 품목의 전사 합계로 읽힌다.
    //   조각별 역할이 달라 문자열이 아니라 배열로 넘긴다(렌더는 CauseTargetChips).
    const targets = (Array.isArray(c.names) ? c.names : []).map((n) => (
      typeof n === "string"
        ? { name: n, loc: null, count: 0 }                       // 구버전 폴백(건수 없음)
        : { name: n?.name || "", loc: n?.loc_name || null, count: Number(n?.count) || 0 }
    )).filter(x => x.name);
    // 라벨/값/단위를 분리 — 한 문장으로 합치면 값만 키울 수 없다. accent 는 심각도 축에만 준다.
    //   값 없는 지표는 빼서 1개짜리 카드(과부하)도 같은 틀로 성립시킨다.
    const metric = (label, raw, fmt, unit, accent) =>
      (raw == null || raw === "" ? null : { label, value: fmt(raw), unit, accent });
    const shortMetrics = () => [
      metric(t("mpKpiShortOrderCountLabel"), c.count, fmtCount, t("caseUnitLabel")),
      metric(t("mpCauseNonOtdShareLabel"), c.share_pct, fmtPct, t("percentUnit"), true),
    ].filter(Boolean);
    const lateMetrics = () => [
      metric(t("mpKpiLateOrderCountLabel"), c.count, fmtCount, t("caseUnitLabel")),
      metric(t("mpCauseAvgDelayLabel"), c.avg_delay_days, fmtPct, t("dayUnitShort"), true),
    ].filter(Boolean);
    return { targets, more: Number(c.names_more) || 0, ...(() => {
    switch (c.type) {
      case "short_material":
        return { symptom: t("mpSupplyShortageShortLabel"), level: "error",
          title: t("mpCauseShortMaterialTitle"), metrics: shortMetrics(),
          cause: t("mpCauseShortMaterialDetail") };
      case "short_capacity":
        return { symptom: t("mpSupplyShortageShortLabel"), level: "error",
          title: t("mpCauseShortCapacityTitle"), metrics: shortMetrics(),
          cause: t("mpCauseShortCapacityDetail") };
      case "late_capacity":
        return { symptom: t("mpLateDeliveryShortLabel"), level: "error",
          title: t("mpCauseLateCapacityTitle"), metrics: lateMetrics(),
          cause: c.peak_util_pct != null
            ? t("mpCauseLateCapacityDetailPeak", { peak: fmtCount(c.peak_util_pct) })
            : t("mpCauseLateCapacityDetail") };
      case "late_material":
        return { symptom: t("mpLateDeliveryShortLabel"), level: "error",
          title: t("mpCauseLateMaterialTitle"), metrics: lateMetrics(),
          cause: t("mpCauseLateMaterialDetail") };
      case "overload":
        // 과부하 건수는 현황 KPI 카드와 같은 overload_resource_count — 라벨 키도 같은 것을 쓴다.
        return { symptom: t("mpOverloadLabel"), level: "warning",
          title: t("mpCauseOverloadTitle"),
          metrics: [metric(t("mpKpiBottleneckResCountLabel"), c.count, fmtCount, t("unitCount"))].filter(Boolean),
          cause: t("mpCauseOverloadDetail") };
      default:
        // 구버전(평면 title/impact/cause) 폴백 — 캐시된 옛 응답. 수치 분해가 불가해 문장으로 남긴다.
        return { symptom: null, level: "error", metrics: [],
          title: c.title || c.cause || "", impact: c.impact || c.evidence || "",
          keyItems: c.key_items || "", cause: (c.cause && c.title) ? c.cause : "" };
    }
    })() };
  };

  return (
    <Stack spacing={1.5}>

      {/* 미집계 빈 상태 패널 — 화면에서 유일한 solid CTA(MP 실행 화면으로 이동) */}
      {mpNotAggregated && (() => {
        const verName   = data?.plan_ver_name ?? versionInfo?.name ?? null;
        const releaseTs = data?.plan_release_ts ?? versionInfo?.releaseTs ?? null;
        return (
          <StatusEmptyPanel
            title={t("statusNoAggregatedResultTitle")}
            desc={verName
              ? t("statusNoAggregatedResultDesc", { name: verName })
              : t("statusNoReleasedVersionDesc")}
            ctaLabel={t("menuMpRun")}
            onCta={() => openPlanRunScreen(reduxDispatch, "MP_RUN")}
            summary={buildStatusPanelSummary(t, {
              verName,
              releaseTs: releaseTs ? new Date(releaseTs).toLocaleDateString() : null,
              lastRun,
            })}
          />
        );
      })()}

      {/* 현황 KPI 요약 */}
      <StatusKpiSection
        collapsible
        defaultOpen
        versionInfo={versionInfo}
        right={mpNotAggregated ? <StatusPendingTag /> : undefined}
        columns={5}
        cards={data ? kpiCards.map((kp, i) => ({
          key: i,
          label: kp.label,
          value: kp.value,
          unit: kp.unit,
          color: kp.color,
          footer: kp.sub, footer2: kp.sub2,
        })) : []}
        empty={data ? null : <StatusEmpty text={t("mpNoCompletedPlanDesc")} />}
      />

      {/* 문제 항목 TOP 5 — 문제 주문 현황(좌) + 과부하 자원 TOP 5(우) 2열 통합 */}
      {data && (
        <StatusSection
          collapsible
          defaultOpen
          title={t("topProblemItemsTitle")}
          subtitle={t("mpProblemTop5Subtitle")}
          contentSx={{ p: "10px 15px !important" }}
        >
          {/* MP만 lg 기준 2열 — 컬럼이 6개(바·OTD·지연/부족·원인칩·원인명·꼬리)라
              md(900px) 2열에서는 우측 칩이 잘린다. 좁은 폭에서는 세로로 쌓는다. */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.5,
                       "& > *": { minWidth: 0 } }}>

            {/* 좌: 문제 주문 현황 */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
                <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>{t("mpProblemOrderByItemTitle")}</Typography>
                <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("mpProblemOrderByItemSubtitle")}</Typography>
                {/* 섹션 헤더가 아니라 소제목 줄에 두는 이유 — 헤더는 접기 클릭 영역이라
                    Enter/Space 를 preventDefault 로 가로채 버튼이 작동하지 않는다.
                    전 축 0건이면 세그먼트 자체를 숨긴다 — 다 눌리지 않는 컨트롤은 장식이다. */}
                {itemViewTabs.some(o => !o.disabled) && (
                  <AiSegmentedControl accent={C.acc} value={itemView} onChange={setItemViewSel}
                    options={itemViewTabs} sx={{ ml: "auto", flexShrink: 0 }} />
                )}
              </Box>
              {(!exceptionItems || exceptionItems.length === 0) ? (
                <Box sx={{ p: "8px 10px", display: "flex", alignItems: "center", gap: 1, borderRadius: 1, bgcolor: C.bg2, border: `0.5px solid ${C.border}`, borderLeft: `3px solid ${C.g1}` }}>
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: C.g1 }} />
                  <Typography sx={{ fontSize: FONT.BODY, color: C.g1t }}>
                    {t("mpNoProblemOrderDesc")}
                  </Typography>
                </Box>
              ) : (<StatusItemList>
                <StatusItemHeader
                  nameLabel={`${t("item")} / ${t("location")}`}
                  rightW={0}
                  barValue={{ w: 44, label: t("mpOtdShortLabel") }}
                  cells={[
                    { w: 88, label: `${t("delayed")} · ${t("short")}` },
                    { w: 104, label: t("bfCauseLabel"), align: "center" },
                  ]} />
                <Stack spacing={0.7} sx={{ flex: 1, justifyContent: "flex-start" }}>
                  {exceptionItems.map((row, i) => {
                    const otd      = row.otd_rate != null ? Number(row.otd_rate) : null;   // 납기 준수율(%)
                    // 레일·수치 같은 색 — 색을 갈라두면 수치가 어느 세그를 가리키는지 대응이 끊긴다
                    const otdColor = otd == null ? C.text3 : otd >= 95 ? C.g1 : otd >= 80 ? C.g2s : C.g3;
                    // 앰버만 한 단계 어둡게 — g2s 는 흰 배경 대비 2.2:1 로 수치가 읽히지 않는다
                    const otdTextColor = otd == null ? C.text3 : otd >= 95 ? C.g1 : otd >= 80 ? C.g2 : C.g3;
                    // 미준수 구성(증상) — 부족(완전미납)>>지연. OTD 헤드라인 아래 뮤트 서브줄로 표기.
                    const lateN  = Math.round(Number(row.late_order_count ?? 0));   // 지연(출하됐으나 납기초과)
                    const shortN = Math.round(Number(row.short_order_count ?? 0));  // 부족(완전미납)
                    const orderN = Number(row.order_count ?? 0);                    // 전체 주문
                    const otdN   = Math.round(Number(row.otd_order_count ?? 0));    // 충족(납기 준수)
                    // 우세 원인 — 칩·드릴다운 공용. 부족(완전미납)이 지연보다 훨씬 심각하므로
                    // 부족의 원인을 먼저 판정하고, 부족에 원인이 없을 때만 전체 합계(사실상 지연분)로 폴백.
                    //   → 합계로만 보면 지연 건수가 대개 더 많아 "지연(능력)+부족(자재)" 품목이
                    //      항상 능력 부족으로 표기돼 정작 급한 자재 부족이 가려진다.
                    const matS = Number(row.short_material_count) || 0;
                    const capS = Number(row.short_capacity_count) || 0;
                    const hasShortCause = matS + capS > 0;
                    const matN  = hasShortCause ? matS : Number(row.material_cause_count) || 0;
                    const capN  = hasShortCause ? capS : Number(row.capacity_cause_count) || 0;
                    const isCap = capN >= matN && capN > 0;   // 동률 시 능력 우선(조치 대상 자원이 구체적)
                    const isMat = matN > capN;
                    const causeName = isCap ? row.cause_resource_name : isMat ? row.cause_material_name : null;
                    // 능력 우세 → 병목 자원 대상 / 자재 우세 → 부족 자재 대상(원인별 드릴다운 분기)
                    const capRes  = { resource_cd: row.cause_resource_cd, resource_name: row.cause_resource_name,
                                      loc_cd: row.loc_cd, loc_name: row.loc_name };
                    const matItem = { item_cd: row.cause_material_cd, item_name: row.cause_material_name,
                                      loc_cd: row.loc_cd, loc_name: row.loc_name };
                    const baseLabel = isCap
                      ? t("mpCapacityShortageShortLabel")
                      : isMat
                      // 칩 전용 축약 라벨 — 공용 ProblemTokenMaterialShortage("Material Shortage")는
                      // 영문이 길어 칩이 잘린다. 능력 부족 라벨과 표기 길이를 맞춘다.
                      ? t("mpMaterialShortageShortLabel")
                      : t("mpOtherProblemLabel");
                    // 칩은 중립 회색으로 통일하고 원인 종류는 좌측 컬러 dot 으로만 구분한다.
                    //   → 색은 바 3세그와 동일(g1/g2s/g3), 심각도와 분류는 위치(레일 vs dot)로 나뉜다.
                    const dotColor = isCap ? C.g3 : isMat ? C.g2s : C.text3;
                    // 칩 클릭 = 원인 대상 화면 직행 — 대상이 하나로 고정이라 선택 메뉴가 필요 없다.
                    const causeNav = navIf("RESOURCE_UTILIZATION", isCap && row.cause_resource_cd,
                      () => openResourceUtilization(reduxDispatch, capRes, versionInfo))
                      ?? navIf("MRP_REPORT", isMat && row.cause_material_cd,
                        () => openMrpReport(reduxDispatch, matItem, versionInfo))
                      ?? null;
                    const locText = row.loc_name || row.loc_cd || "";
                    const itemText = row.item_name || row.item_cd || "—";
                    return (
                    <StatusItemRow key={i}
                      accent={otdColor}
                      name={(
                        <NavName targets={[
                          { label: i18n.t("menuMpOrderTracking"),
                            onClick: navIf("ORDER_TRACKING", row.item_cd || row.loc_cd, () => goToOrderTracking(row)) },
                          // 능력 우세 → 병목 자원 부하/간트로 이동(어느 자원인지 바로 확인)
                          { label: i18n.t("menuMpResourceUtilization"),
                            onClick: navIf("RESOURCE_UTILIZATION", isCap && row.cause_resource_cd,
                              () => openResourceUtilization(reduxDispatch, capRes, versionInfo)) },
                          { label: i18n.t("menuMpResourceGantt"),
                            onClick: navIf("MP_RESOURCE_GANTT", isCap && row.cause_resource_cd,
                              () => openResourceGantt(reduxDispatch, capRes, versionInfo)) },
                          // 자재 우세 → 자재 소요계획(MRP) 발주 리스트/리포트로 이동(어느 자재인지 바로 확인)
                          { label: i18n.t("menuMpMrpOrderList"),
                            onClick: navIf("MRP_ORDER_LIST", isMat && row.cause_material_cd,
                              () => openMrpOrderList(reduxDispatch, matItem, versionInfo)) },
                          { label: i18n.t("menuMpMrpReport"),
                            onClick: navIf("MRP_REPORT", isMat && row.cause_material_cd,
                              () => openMrpReport(reduxDispatch, matItem, versionInfo)) },
                          { label: i18n.t("menuMpFloChart"),
                            onClick: navIf("MP_FLO_CHART", row.item_cd, () => openFloChart(reduxDispatch, row)) },
                        ]}>
                          {itemText}
                        </NavName>
                      )}
                      sub={locText}
                      /* 미준수 구성 3세그 누적 바 — 충족(green)|지연(amber)|부족(red) 전폭. OTD 0% 행도 가득 차
                         심각도 가시화(빈 바 역설 해소). green 폭=otd/order=OTD%, 좌→우 심각도 상승. */
                      bar={(
                        <Box sx={STATUS_BAR_COL_SX}>
                          {/* 호버 툴팁: 3세그(충족/지연/부족)를 건수+구성%(대 전체 주문)로 디코드. 색=바 세그먼트 일치, 순서=바 좌→우(충족→지연→부족), 부족 강조. */}
                          <Tooltip {...BAR_TIP_PROPS} title={
                            <BarTip title={`${itemText}${locText ? ` · ${locText}` : ""}`}
                              rows={[
                                { color: C.g1, label: t("mpOtdMetLabel"), value: `${t("countCases", { count: otdN })}${orderN > 0 ? ` (${Math.round(otdN / orderN * 100)}%)` : ""}` },
                                { color: C.g2s, label: t("delayed"),      value: `${t("countCases", { count: lateN })}${orderN > 0 ? ` (${Math.round(lateN / orderN * 100)}%)` : ""}` },
                                { color: C.g3, label: t("short"),         value: `${t("countCases", { count: shortN })}${orderN > 0 ? ` (${Math.round(shortN / orderN * 100)}%)` : ""}`, strong: true },
                              ]} />
                          }>
                            <Box sx={{ display: "flex", height: STATUS_BAR_H, overflow: "hidden",
                                       borderRadius: STATUS_BAR_RADIUS,
                                       bgcolor: STATUS_BAR_TRACK, cursor: "default" }}>
                              <Box sx={{ width: `${orderN > 0 ? (otdN / orderN) * 100 : 0}%`, bgcolor: C.g1, boxShadow: SEG_DIVIDER }} />
                              <Box sx={{ width: `${orderN > 0 ? (lateN / orderN) * 100 : 0}%`, bgcolor: C.g2s, boxShadow: SEG_DIVIDER }} />
                              <Box sx={{ width: `${orderN > 0 ? (shortN / orderN) * 100 : 0}%`, bgcolor: C.g3 }} />
                            </Box>
                          </Tooltip>
                        </Box>
                      )}
                      /* OTD 율만 — 준수/전체 건수는 바 세그먼트가 이미 보여주고 툴팁이 절대값까지 준다 */
                      barValue={{ w: 44, node: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: otdTextColor, lineHeight: 1, whiteSpace: "nowrap" }}>
                          {otd != null ? `${otd.toFixed(0)}%` : "—"}
                        </Typography>
                      ) }}
                      cells={[
                        { w: 88, node: (
                          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline", gap: 0.6,
                                     fontSize: FONT.SUB, lineHeight: 1.1, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {/* 지연=앰버·부족=레드 — 바 세그먼트와 같은 색상, 앰버만 가독성 때문에 한 단계 어둡게 */}
                            <Box component="span" sx={{ color: lateN > 0 ? C.g2 : C.text3,
                                                        fontWeight: lateN > 0 ? 700 : 400 }}>{lateN}</Box>
                            <Box component="span" sx={{ color: C.text3 }}>·</Box>
                            <Box component="span" sx={{ color: shortN > 0 ? C.g3 : C.text3, fontWeight: shortN > 0 ? 700 : 400 }}>{shortN}</Box>
                          </Box>
                        ) },
                        // 원인 유형 칩 — 별도 고정폭 컬럼. 원인명 길이와 무관하게 칩 위치가 세로로 일정하다.
                        //   flex 래퍼 필수 — 칩을 셀에 그대로 두면 inline 여백 때문에 행이 4px 높아져
                        //   좌우 카드 높이가 어긋난다.
                        { w: 104, align: "center", node: (
                          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            {/* 빈 title 이면 MUI 가 툴팁을 안 띄운다 — 원인 없는 행에 분기가 필요 없다 */}
                            <Tooltip {...BAR_TIP_PROPS} title={causeName || ""}>
                              <Chip
                                icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: dotColor }} />}
                                label={baseLabel}
                                size="small"
                                {...(causeNav ? { onClick: causeNav, disableRipple: true } : null)}
                                tabIndex={(causeName || causeNav) ? 0 : undefined}
                                sx={{
                                  bgcolor: C.bg3, color: C.text2, border: `0.5px solid ${C.border}`,
                                  fontSize: FONT.CAPTION, height: 19, flexShrink: 0, maxWidth: "100%",
                                  "& .MuiChip-label": { px: "5px" },
                                  "& .MuiChip-icon": { ml: "6px", mr: "-2px" },
                                  // MUI 기본 clickable hover 를 덮으려면 선택자 3개로 특이도를 맞춰야 한다
                                  "&.MuiChip-clickable:hover": { bgcolor: C.accL, borderColor: C.acc, color: C.acc },
                                }}
                              />
                            </Tooltip>
                          </Box>
                        ) },
                      ]} />
                    );
                  })}
                </Stack>
              </StatusItemList>)}
            </Box>

            {/* 우: 병목 자원 TOP 5 */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
                <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>{t("mpBottleneckResStatusTitle")}</Typography>
                <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("mpBottleneckResStatusSubtitle")}</Typography>
                {resViewTabs.some(o => !o.disabled) && (
                  <AiSegmentedControl accent={C.acc} value={resView} onChange={setResViewSel}
                    options={resViewTabs} sx={{ ml: "auto", flexShrink: 0 }} />
                )}
              </Box>
              <StatusItemList>
              {(criticalResources ?? []).length > 0 && (
                <StatusItemHeader
                  nameLabel={`${t("mpResourceLabel")} / ${t("location")}`}
                  rightW={ALT_RES_CHIP_W}
                  barValue={{ w: 60, label: t("mpProblemOrderShortLabel"), unit: t("caseUnitLabel") }}
                  cells={[
                    { w: 88, label: `${t("delayed")} · ${t("short")}` },
                    { w: 60, label: t("mpUtilLabel"), unit: "%" },
                  ]} />
              )}
              <Stack spacing={0.7} sx={{ flex: 1, justifyContent: "flex-start" }}>
                {(criticalResources ?? []).map((res, i) => {
                  const pct   = Number(res.util_pct ?? 0);
                  const total = Number(res.total_count ?? 0);   // 문제 주문 건수(지연+부족)
                  const lateN  = Math.round(Number(res.late_count ?? 0));   // 지연 건수
                  const shortN = Math.round(Number(res.short_count ?? 0));  // 부족 건수
                  // 심각도 색 = 부족(완전미납) 있으면 빨강(증설 필요)/지연만이면 앰버(회복 가능). 부족>>지연.
                  const sevColor = shortN > 0 ? C.g3 : C.g2s;
                  const resText = res.resource_name || res.resource_cd || "—";
                  const locText = res.loc_name || res.loc_cd || "";
                  return (
                    <StatusItemRow key={i}
                      accent={sevColor}
                      name={(
                        <NavName targets={[
                          { label: i18n.t("menuMpResourceProductionPlan"),
                            onClick: navIf("RESOURCE_PRODUCTION_PLAN", res.resource_cd,
                              () => openResourceProductionPlan(reduxDispatch, res, versionInfo)) },
                          { label: i18n.t("menuMpResourceUtilization"),
                            onClick: navIf("RESOURCE_UTILIZATION", res.resource_cd, () => goToResourceUtilization(res)) },
                          { label: i18n.t("menuMpResourceGantt"),
                            onClick: navIf("MP_RESOURCE_GANTT", res.resource_cd,
                              () => openResourceGantt(reduxDispatch, res, versionInfo)) },
                        ]}>
                          {resText}
                        </NavName>
                      )}
                      sub={locText}
                      /* 구성비 바 — 지연(앰버)/부족(빨강)이 각각 late/total·short/total 로 분할.
                         좌→우 심각도 상승(앰버→빨강). 규모·순위는 정렬+숫자가 담당. */
                      bar={(
                        <Box sx={STATUS_BAR_COL_SX}>
                          {/* 호버 툴팁: 2세그(지연/부족)를 건수+구성%(대 문제 건수)로 디코드. 부족 강조. */}
                          <Tooltip {...BAR_TIP_PROPS} title={
                            <BarTip title={`${resText}${locText ? ` · ${locText}` : ""}`}
                              rows={[
                                { color: C.g2s, label: t("delayed"), value: `${t("countCases", { count: lateN })}${total > 0 ? ` (${Math.round(lateN / total * 100)}%)` : ""}` },
                                { color: C.g3, label: t("short"),   value: `${t("countCases", { count: shortN })}${total > 0 ? ` (${Math.round(shortN / total * 100)}%)` : ""}`, strong: true },
                              ]} />
                          }>
                            <Box sx={{ display: "flex", height: STATUS_BAR_H, overflow: "hidden",
                                       borderRadius: STATUS_BAR_RADIUS,
                                       bgcolor: STATUS_BAR_TRACK, cursor: "default" }}>
                              <Box sx={{ width: `${total > 0 ? (lateN / total) * 100 : 0}%`, bgcolor: C.g2s, boxShadow: SEG_DIVIDER }} />
                              <Box sx={{ width: `${total > 0 ? (shortN / total) * 100 : 0}%`, bgcolor: C.g3 }} />
                            </Box>
                          </Tooltip>
                        </Box>
                      )}
                      barValue={{ w: 60, unit: t("caseUnitLabel"), num: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: shortN > 0 ? C.g3 : C.g2, lineHeight: 1, whiteSpace: "nowrap" }}>
                          {total.toLocaleString()}
                        </Typography>
                      ) }}
                      cells={[
                        // 지연·부족 — 바와 동일 순서(지연→부족), 부족 빨강 bold
                        { w: 88, node: (
                          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline", gap: 0.6,
                                     fontSize: FONT.SUB, lineHeight: 1.1, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {/* 지연=앰버·부족=레드 — 바와 같은 색상, 앰버만 한 단계 어둡게 (위 리스트와 동일 규칙) */}
                            <Box component="span" sx={{ color: lateN > 0 ? C.g2 : C.text3,
                                                        fontWeight: lateN > 0 ? 700 : 400 }}>{lateN}</Box>
                            <Box component="span" sx={{ color: C.text3 }}>·</Box>
                            <Box component="span" sx={{ color: shortN > 0 ? C.g3 : C.text3, fontWeight: shortN > 0 ? 700 : 400 }}>{shortN}</Box>
                          </Box>
                        ) },
                        // 보조 지표: 가동률 — FC에선 ≤100%라 변별력 낮아 참고용(뮤트)
                        { w: 60, unit: "%", num: (
                          <Typography sx={{ fontSize: FONT.SUB, fontWeight: 600, color: C.text2, lineHeight: 1, whiteSpace: "nowrap" }}>
                            {pct.toFixed(0)}
                          </Typography>
                        ) },
                      ]}
                      /* 대체 자원 유무 — 비대칭: 없음=증설 불가피(앰버 포커스) / 있음=리라우팅 가능(뮤트). */
                      rightW={ALT_RES_CHIP_W}
                      right={(
                        <Chip
                          icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: res.has_alt ? C.text3 : C.g2s }} />}
                          label={res.has_alt ? t("mpAltResAvailable") : t("mpAltResNone")}
                          size="small"
                          sx={{
                            bgcolor: C.bg3, color: res.has_alt ? C.text3 : C.g2t, border: `0.5px solid ${C.border}`,
                            fontSize: FONT.CAPTION, height: 19, flexShrink: 0,
                            "& .MuiChip-label": { px: "5px" },
                            "& .MuiChip-icon": { ml: "6px", mr: "-2px" },
                          }}
                        />
                      )} />
                  );
                })}
                {criticalResources && criticalResources.length === 0 && (
                  <Box sx={{ p: "8px 10px", borderRadius: 1, bgcolor: C.bg2, border: `0.5px solid ${C.border}`, borderLeft: `3px solid ${C.g1}` }}>
                    <Typography sx={{ fontSize: FONT.BODY, color: C.g1t }}>✓ {t("mpNoBottleneckResDesc")}</Typography>
                  </Box>
                )}
                {!criticalResources && (
                  <Box sx={{ p: "8px 10px", borderRadius: 1, bgcolor: "#FFFBEB", border: "0.5px solid #FDE68A" }}>
                    <Typography sx={{ fontSize: FONT.BODY, color: "#92400E" }}>{t("mpNoResourceDataDesc")}</Typography>
                  </Box>
                )}
              </Stack>
              </StatusItemList>
            </Box>

          </Box>
        </StatusSection>
      )}

      {/* ── 기간별 현황 추이 — Capa 누적 막대(일자별) / 납기 현황 누적 막대(일·주·월) 세그먼트 전환 ── */}
      {(capaTrendHasData || otdHasData) && (
        <StatusSection
          collapsible
          defaultOpen
          title={t("mpTrendSectionTitle")}
          contentSx={{ p: "14px 15px !important" }}
          // 헤더는 제목 + 접힘 화살표만 — 세그먼트·드롭다운은 본문 최상단 툴바로 내렸다.
          //   헤더 Box 전체가 접힘 토글(role=button)이라 그 안의 컨트롤은 Enter/Space 를 뺏긴다.
          // StatusToolbar 는 flex-end + 라벨 선행이라 세그먼트를 children 으로 넣으면 "조회 범위"
          //   라벨과 드롭다운이 갈라진다 → 바깥 flex 로 감싸고 전체를 우측 정렬한다.
          toolbar={(
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
              {/* 한쪽에 데이터가 없으면 세그먼트를 숨긴다 — 다 눌리지 않는 컨트롤은 장식이다. */}
              {capaTrendHasData && otdHasData && (
                <AiSegmentedControl accent={C.acc} value={trendView} onChange={setTrendViewSel}
                  options={[
                    { value: "capa", label: t("mpResourceLoadLabel") },
                    { value: "otd",  label: t("mpOtdStatusLabel") },
                  ]} sx={{ flexShrink: 0 }} />
              )}
              {/* 버킷은 두 차트 공용 — 세그먼트를 오갈 때 컨트롤이 생겼다 사라지지 않는다. */}
              <AiSegmentedControl accent={C.acc} value={trendGrain} onChange={setTrendGrain}
                options={[
                  { value: "day",   label: t("daily") },
                  { value: "week",  label: t("trendGrainWeeklyLabel") },
                  { value: "month", label: t("monthly") },
                ]} sx={{ flexShrink: 0 }} />
              {(trendView === "otd" ? otdGroupOptions : trendGroupOptions).length > 0 && (
                <StatusToolbar label={t("trendScopeLabel")}>
                  <StatusToolbarSelect
                    value={trendView === "otd" ? otdGroupSel : trendGroupSel}
                    onChange={trendView === "otd" ? setOtdGroup : setTrendGroup}
                    options={[
                      { value: "", label: t(trendView === "otd" ? "mpOtdGroupAll" : "mpTrendGroupAll") },
                      ...(trendView === "otd" ? otdGroupOptions : trendGroupOptions)
                        .map(([cd, name]) => ({ value: cd, label: name })),
                    ]}
                  />
                </StatusToolbar>
              )}
            </Box>
          )}
        >
          {trendView === "otd" ? (
            // x 범위가 계획 구간보다 앞서 시작할 수 있다 — due_date 기준이라 과거 미납 잔량까지 들어온다.
            // 계획 구간으로 자르지 말 것(그 과거 구간이 가장 조치가 필요한 부분이다).
            <ForecastActualChart
              trend={selectedOtdTrend}
              xKey="date"
              colorMap={OTD_TREND_COLORS}
              barKeys={OTD_TREND_BAR_KEYS}
              barStackId="otd"
              unit={t("caseUnitLabel")}
              emptyText={t("mpOtdTrendEmpty")}
            />
          ) : (
            <ForecastActualChart
              trend={selectedTrend}
              xKey="date"
              colorMap={CAPA_TREND_COLORS}
              barKeys={CAPA_TREND_BAR_KEYS}
              barStackId="capa"
              unit={t("mpHoursUnitAxisLabel")}
              emptyText={t("mpCapaLoadTrendEmpty")}
            />
          )}
        </StatusSection>
      )}

      {/* 주요 이슈 / 문제점 — 전체폭, data 있을 때만 표시 */}
      {data && (
        <StatusSection
          collapsible
          defaultOpen
          title={t("mainIssueTitle")}
          collapseDisabled={!aiLoading && !useAiCauses && issueItems.length === 0}
          right={(aiLoading && !useAiCauses) ? (
            <CircularProgress size={12} sx={{ color: C.g3t }} />
          ) : (useAiCauses ? aiCauses : issueItems).length === 0 ? (
            // 건수 칩(0건)은 "문제 없음"으로 읽힌다 — 미집계에서는 중립 '집계 대기' 태그로 바꾼다.
            <StatusPendingTag />
          ) : (
            // 권장 대책 헤더와 같은 조판 — 건수는 섹션의 규모 표시일 뿐이라 색 칩으로 강조하지 않는다.
            <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
              {t("countCases", { count: (useAiCauses ? aiCauses : issueItems).length })}
            </Typography>
          )}
          contentSx={{ p: "10px 15px !important" }}
        >
          {(aiLoading && !useAiCauses) ? (
            <Stack direction="row" alignItems="center" gap={1} sx={{ py: 1 }}>
              <CircularProgress size={14} sx={{ color: C.acc }} />
              <Typography sx={{ fontSize: FONT.SUB, color: C.text3 }}>{t("mpAiIssueAnalyzingDesc")}</Typography>
            </Stack>
          ) : useAiCauses ? (
            <Box sx={STATUS_CARD_GRID_SX}>
              {aiCauses.map((c, i) => {
                // 구조화 카드(type+수치+명칭)를 i18n 문구로 조립 — 구버전 평면 응답은 describeCause 가 폴백.
                const { symptom, level, title, metrics, impact, targets, more, keyItems, cause } = describeCause(c);
                const st = ISSUE_STYLE[level] ?? ISSUE_STYLE.error;
                // 대상 줄 = 구조화 targets(신) 또는 평면 key_items 문자열(구버전 캐시).
                const showTargets = targets.length > 0 || Boolean(keyItems);
                return (
                  <StatusInsightCard key={i} accent={st} eyebrow={symptom}
                    title={highlightText(title, causeNames)}>
                    {/* 상단 KPI(FONT.KPI)보다 한 단계 낮춘다 — 동급이면 전역 KPI 와 원인별 부분집합이 뒤섞여 읽힌다 */}
                    {metrics.length > 0 && (
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        {metrics.map((m, k) => (
                          <Box key={k} sx={{ minWidth: 0, pr: 1.5,
                            ...(k > 0 && { pl: 1.5, borderLeft: `1px solid ${C.border}` }) }}>
                            <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, lineHeight: 1.4 }}>
                              {m.label}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "baseline", mt: 0.2 }}>
                              <Typography sx={{ fontSize: FONT.FORMULA, fontWeight: 700, lineHeight: 1.1,
                                color: m.accent ? st.color : C.text1 }}>{m.value}</Typography>
                              <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, ml: "2px",
                                color: m.accent ? st.color : C.text2 }}>{m.unit}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {/* 구버전 평면 응답 — 수치 분해가 없어 기존 '영향' 문장 한 줄로 폴백 */}
                    {metrics.length === 0 && impact && (
                      <Box sx={{ display: "flex", gap: 0.8, alignItems: "flex-start" }}>
                        <Typography component="span" sx={{ fontSize: FONT.CAPTION, fontWeight: 700,
                          color: C.text3, lineHeight: 1.5, flexShrink: 0 }}>
                          {t("mpImpactLabel")}
                        </Typography>
                        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.5 }}>
                          {highlightText(impact, causeNames, C.g3t)}
                        </Typography>
                      </Box>
                    )}
                    {showTargets && (
                      <StatusCardField label={t("targetObj")}>
                        {targets.length > 0
                          ? <CauseTargetChips targets={targets} more={more} />
                          : (
                            <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.5 }}>
                              {highlightText(keyItems, causeNames)}
                            </Typography>
                          )}
                      </StatusCardField>
                    )}
                    {cause && (
                      <StatusCardField label={t("bfCauseLabel")}>
                        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.5 }}>
                          {highlightText(cause, causeNames)}
                        </Typography>
                      </StatusCardField>
                    )}
                  </StatusInsightCard>
                );
              })}
            </Box>
          ) : issueItems.length === 0 ? (
            // 이슈 0건은 "정상"이 아니라 판정 불가 — 중립 스켈레톤으로 표시한다.
            <StatusEmpty variant="rows" count={3} text={t("MSG_NoStatusIssueData")} />
          ) : (
            <Stack spacing={0.8}>
              {issueItems.map((issue, i) => {
                const st = ISSUE_STYLE[issue.level];
                return (
                  <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, p: "9px 12px", borderRadius: 1.5, bgcolor: C.bg2, border: `0.5px solid ${C.border}`, borderLeft: `3px solid ${st.color}` }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.3 }}>
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: st.text }}>{issue.title}</Typography>
                        <Chip label={issue.kpiLabel} size="small"
                          sx={{ bgcolor: st.bg, color: st.color, border: `0.5px solid ${st.color}55`, fontSize: FONT.CAPTION, height: 19, "& .MuiChip-label": { px: "5px" } }} />
                      </Box>
                      <Typography sx={{ fontSize: FONT.BODY, color: C.text2, lineHeight: 1.5 }}>{issue.desc}</Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: st.color, lineHeight: 1 }}>{issue.count}</Typography>
                      <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, mt: 0.2, whiteSpace: "nowrap" }}>{t("case")}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </StatusSection>
      )}

    </Stack>
  );
}

MP_MODULE.ExtraInitContent = MpExtraInitContent;

// ── MP CompareDetailContent (시나리오 비교 탭) — 실현 가능성 경고 + 병목 진단 + 후속 화면 링크 ──
//   백엔드(mp/feasibility_rules.py)가 RP/IP/DP 와 같은 계약(scn.feasibility_warnings)으로 방출한다.
//   표까지 공용 위젯이 열 메타로 그린다. MP 가 대는 것은 드릴다운(rowTargets)과 차트 버튼(renderCell) 뿐.
//   - 링크 : 능력 병목 → 자원 부하·능력 / 자재 병목 → 자재 소요계획 발주 리스트

// KPI 비교표 경고 행 — RP/IP/DP 와 동일한 공용 헬퍼. 심각도(tone)는 백엔드가 소유한다.
MP_MODULE.scenarioWarnCount = feasWarnCount;

// 바 툴팁(BAR_TIP_PROPS / BarTip)은 scenarioStatusUi 공용 — MP 누적 세그 바와 RP/IP 단일 바가 함께 쓴다.

// 비교 탭 실현 가능성 경고 — 표는 공용 위젯이 열 메타로 그리고, MP 는 드릴다운과 차트만 얹는다.
function MpCompareDetailContent({ mod }) {
  const dispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const [chartRes, setChartRes] = useState(null);   // 클릭한 자원(일자별 추이 팝업)

  // 이동 대상 한 줄 — 권한 밖 화면이면 onClick 을 비워 두고 아래에서 그 줄째 걸러낸다.
  const nav = (target, label, onClick) => ({ label, onClick: isPermitted(target) ? onClick : undefined });

  // 행 드릴다운 — 화면 이동은 데이터로 표현할 수 없어 모듈이 제공하는 유일한 훅.
  //   행 형상이 카드마다 달라(resource_cd vs res_cd vs item_cd) 경고 type 으로 분기한다.
  const rowTargets = (row, w) => {
    const ver = w?.version;
    const res = { resource_cd: row.resource_cd || row.res_cd, resource_name: row.resource_name || row.res_name,
                  loc_cd: row.loc_cd, loc_name: row.loc_name };
    const targets = (() => {
      switch (w?.type) {
        case "rc_infinite":
          return [
            nav("INPUT_PRODUCTION_RESOURCE", i18n.t("productionResource"), () => openProductionResource(dispatch, res)),
            nav("RESOURCE_UTILIZATION", i18n.t("menuMpResourceUtilization"), () => openResourceUtilization(dispatch, res, ver)),
            nav("RESOURCE_PRODUCTION_PLAN", i18n.t("menuMpResourceProductionPlan"), () => openResourceProductionPlan(dispatch, res, ver)),
            nav("MP_RESOURCE_GANTT", i18n.t("menuMpResourceGantt"), () => openResourceGantt(dispatch, res, ver)),
          ];
        case "mc_infinite":
          return [
            nav("INPUT_MATERIAL_RECEIPT_PLAN", i18n.t("materialReceiptPlan"), () => openMaterialReceiptPlan(dispatch, row, ver)),
            nav("MRP_REPORT", i18n.t("menuMpMrpReport"), () => openMrpReport(dispatch, row, ver)),
            nav("MRP_ORDER_LIST", i18n.t("menuMpMrpOrderList"), () => openMrpOrderList(dispatch, row, ver)),
            nav("INPUT_BOM_TREE", i18n.t("bomDetail"), () => openBomDetail(dispatch, row)),
          ];
        // 차트는 action 열 버튼(rowAction)이 열므로 여기서는 화면 이동만 둔다.
        case "nonworking":
          return [
            nav("INPUT_PRODUCTION_RESOURCE", i18n.t("productionResource"), () => openProductionResource(dispatch, res)),
            nav("MP_RESOURCE_GANTT", i18n.t("menuMpResourceGantt"), () => openResourceGantt(dispatch, res, ver)),
            ...(row.nop_cal_grp_id != null ? [nav("INPUT_CALENDAR", i18n.t("cycleCalendar"),
              () => openCycleCalendar(dispatch, row.nop_cal_grp_id, row.nop_cal_grp_name))] : []),
          ];
        case "capa_over":
          return [
            nav("INPUT_PRODUCTION_RESOURCE", i18n.t("productionResource"), () => openProductionResource(dispatch, res)),
            nav("MP_RESOURCE_GANTT", i18n.t("menuMpResourceGantt"), () => openResourceGantt(dispatch, res, ver)),
          ];
        default:
          return [];
      }
    })();
    // 권한 밖(onClick 없음) 줄은 목록에서 뺀다 — 눌러도 아무 일 없는 항목을 남기지 않는다.
    return targets.filter((tgt) => typeof tgt.onClick === "function");
  };

  // 차트 셀은 공용 표가 모른다 — 버튼도 모달도 MpResourceLoadChart.js 가 소유한다.
  const renderCell = (row, col, w) => col?.key === "_chart"
    ? <MpChartButton onClick={() => setChartRes({ ...row, kind: w?.type === "nonworking" ? "nonworking" : "capa" })} />
    : undefined;

  return (
    <>
      <CompareFeasibilityWarnings mod={mod}
        cfg={{ sectionTitleKey: "mpExecWarningSectionTitle" }}
        rowTargets={rowTargets} renderCell={renderCell} />
      <MpResourceLoadChart chartRes={chartRes} setChartRes={setChartRes} />
    </>
  );
}


MP_MODULE.CompareDetailContent = MpCompareDetailContent;

// 추천 히어로 카드(비교탭 상단) 경고 배지 — RP/IP/DP 와 동일한 공용 컴포넌트.
//   발화분만 내려오고 tone(crit=빨강 / warn=주황)·문구·순서를 모두 백엔드가 소유하므로 자체 구현이 불필요해졌다.
MP_MODULE.RecHeroBadges = FeasibilityHeroBadges;

// 파라미터 키워드 강조(파랑)는 폐지했다 — 산문을 경량 모델로 번역하면서 문장 속 용어 표기가
//   i18n 용어(t(key))와 어긋나, 같은 개념인데 강조가 붙었다 안 붙었다 하는 흔들림이 생겼다.
//   실데이터 명칭 강조(빨강)는 [[ ]] 마커 기반이라 번역과 무관하게 정확하므로 그대로 둔다.

// 증상별 보기 세그먼트의 옵션 — 좌(품목)·우(자원) 공용. {total,late,short} 뷰를 그대로 받는다.
//   target_count 는 잘라내기 전 전체 대상 수 → 0 이면 행이 없는 것이 확정이라 비활성으로 막는다
//   (누르면 빈 목록의 '문제 없음' 초록이 떠 전체 클리어로 오독된다).
function buildViewTabs(views, t) {
  return [["total", t("all")], ["late", t("delayed")], ["short", t("short")]]
    .map(([value, label]) => ({ value, label, disabled: Number(views[value]?.target_count ?? 0) === 0 }));
}

// 선택 축이 0건이 되면 전체로 폴백 — 비활성은 '누르기 전'만 막는다. 부족을 고른 뒤 부족 0건인
//   버전으로 재조회하면 선택이 이미 끝난 상태라 위 오독이 그대로 발생한다.
const axisOr = (views, sel) => (Number(views?.[sel]?.target_count ?? 0) > 0 ? sel : "total");

// 주요 이슈 카드의 '대상' — 칩 경계가 대상 1건의 단위를 그린다(쉼표 나열은 경계가 색으로만 남는다).
//   건수를 빼면 카드 상단 지표(전역 건수)가 대상 하나의 몫으로 읽힌다.
//   말줄임은 이름에만 — 거점·건수가 잘리면 행의 그레인이 사라져 전사 합계로 오독된다.
function CauseTargetChips({ targets, more }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {targets.map((tg, i) => (
        <Box key={i} sx={{ ...STATUS_TARGET_CHIP_SX, display: "inline-flex", alignItems: "center",
          gap: 0.6, maxWidth: "100%" }}>
          <Typography noWrap sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1, minWidth: 0 }}>
            {tg.name}
          </Typography>
          {tg.loc && (
            <Typography noWrap sx={{ fontSize: FONT.CAPTION, color: C.text3, minWidth: 0 }}>{tg.loc}</Typography>
          )}
          {tg.count > 0 && (
            <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1, flexShrink: 0 }}>
              {t("countCases", { count: fmtCount(tg.count) })}
            </Typography>
          )}
        </Box>
      ))}
      {/* 점선 칩 — 실칩과 같은 모양이면 "4건 외 1개"가 앞 대상에 걸리는 것으로 읽힌다 */}
      {more > 0 && (
        <Box sx={{ ...STATUS_TARGET_CHIP_SX, display: "inline-flex", alignItems: "center",
          bgcolor: "transparent", border: `0.5px dashed ${C.border}` }}>
          <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, whiteSpace: "nowrap" }}>
            {t("nMoreLabel", { count: more })}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// initial-state 데이터에서 LLM 이 참조할 수 있는 모든 실데이터 명칭(코드·이름)을 수집 — 빨강 강조용.
//   자원(highlights) + 문제 거점·품목(exception_items) 의 코드·이름 전부. 길이 2 이상만(과한 매칭 방지).
//   main_causes 의 대상명도 포함해야 한다 — 카드 대상은 증상별(부족/지연) 후보에서 뽑히므로
//   highlights(문제 합계 순 TOP5) 밖의 자원이 올 수 있고, 그러면 원인·제목 문장에서 강조가 빠진다.
//   거점명은 넣지 않는다 — 대상 칩은 CauseTargetChips 가 직접 착색하고, 여기 넣으면 거점이
//   원인·제목 문장에서도 자원명과 같은 빨강이 돼 한정자가 주체처럼 보인다.
function collectDataNames(data) {
  const causes = data?.main_causes ?? data?.status_narrative?.main_causes ?? [];
  const raw = [
    ...((data?.highlights || []).flatMap(r => [r.resource_name, r.resource_cd])),
    ...((data?.exception_items || []).flatMap(e => [e.item_name, e.item_cd, e.loc_name, e.loc_cd])),
    ...(causes.flatMap(c => (c?.names || []).map(n => (typeof n === "string" ? n : n?.name)))),
  ];
  return [...new Set(raw)].filter(n => typeof n === "string" && n.trim().length >= 2);
}

// 문장 안의 실데이터 명칭(자원명/자재명/거점명)을 빨강 볼드로 렌더. 긴 항목 우선 매칭.
//   numColor 지정 시 수치 토큰(예: 425건, 22.2%p, 95%, 3개)도 해당 색으로 강조.
function highlightMixed(text, names, numColor) {
  if (!text) return text;
  const items = (names || []).map(t => ({ t, color: C.g3t })).filter(x => x.t);
  const seen = new Set();
  const terms = [];
  for (const it of items.sort((a, b) => b.t.length - a.t.length)) {
    if (!seen.has(it.t)) { seen.add(it.t); terms.push(it); }
  }
  // 수치 토큰 — 숫자(소수·콤마) + 선택 단위(%p/%/건/개/배/일/시간/주/원/p). numColor 있을 때만.
  const NUM_RE = /^\d[\d,]*(?:\.\d+)?(?:\s?(?:%p|%|건|개|배|일|시간|주|원|p))?/;
  const out = [];
  let i = 0, key = 0, buf = "";
  const flush = () => { if (buf) { out.push(<React.Fragment key={key++}>{buf}</React.Fragment>); buf = ""; } };
  while (i < text.length) {
    const pos = i;
    const m = terms.find(x => text.startsWith(x.t, pos));
    if (m) {
      flush();
      let frag = m.t;
      i += m.t.length;
      // 가운뎃점(·) 나열 접미사(#2·#4·#5 등)까지 함께 강조 — 개별 자원명만 등록돼 있어
      //   "OLED 유기물 증착기 #2" 만 매칭되고 "·#4·#5" 가 누락되던 문제 보정.
      //   \w 는 한글 미포함 → "·OLED 라인" 같은 별개 한글 명칭은 흡수 안 함(LLM [[ ]] 마커가 담당).
      const cont = text.slice(i).match(/^(·#?[\w]+)+/);
      if (cont) { frag += cont[0]; i += cont[0].length; }
      out.push(<Box component="span" key={key++} sx={{ color: m.color, fontWeight: 700 }}>{frag}</Box>);
      continue;
    }
    if (numColor && text[i] >= "0" && text[i] <= "9") {
      const nm = text.slice(i).match(NUM_RE);
      if (nm && nm[0]) {
        flush();
        out.push(<Box component="span" key={key++} sx={{ color: numColor, fontWeight: 700 }}>{nm[0]}</Box>);
        i += nm[0].length;
        continue;
      }
    }
    buf += text[i++];
  }
  flush();
  return out.length ? out : text;
}

// [[ ]] 마커(LLM이 감싼 실데이터 명칭 — 결합·나열 포함)는 통째로 빨강 강조하고,
//   마커 밖 텍스트는 highlightMixed(완전일치 빨강)로 처리.
function highlightText(text, names, numColor) {
  if (!text) return text;
  const segs = String(text).split(/(\[\[[\s\S]*?\]\])/);
  const out = [];
  segs.forEach((seg, i) => {
    if (!seg) return;
    const m = seg.match(/^\[\[([\s\S]*)\]\]$/);
    if (m) {
      out.push(<Box component="span" key={`m${i}`} sx={{ color: C.g3t, fontWeight: 700 }}>{m[1]}</Box>);
    } else {
      out.push(<React.Fragment key={`s${i}`}>{highlightMixed(seg, names, numColor)}</React.Fragment>);
    }
  });
  return out;
}

// ── MP 권장 대책 (시나리오 구성 탭) — 현황 요약 LLM 의 recommended_actions 를 표시 ──────
//   현황 분석 탭(③)에서 이동. 자원/생산 공정/자재/계획 정책 등 param_group 으로 묶어 compact list 로 표시.
//   supported=false(참고)는 맨 아래 '참고' 그룹으로 흐리게. 파라미터 키워드는 문장 안에서 강조.
// ── 권장 대책 카드 — 대상(자원)별 변경 내역 ──────────────────────────────────
// 값·적용 기간은 백엔드가 시나리오 override 와 같은 정본에서 '표시 단위'로 환산해 내려준다(target_changes).
// 대상은 접지 않고 전량 한 행씩 — '외 N개'로 접으면 무엇이 바뀌는지 카드에서 확인이 안 된다.
const MP_CHANGE_UNIT_KEY = { work_hour_per_day: "mpUnitHour", over_work_hour_per_day: "mpUnitHour",
                             work_day_per_week: "dayUnit" };
// 그릇 안쪽 여백(7) + 화살표 칸(13) + gap(6) — 그릇 밖에 놓이는 구간 행의 값 끝을 대상 행과 같은 축에 세운다.
const MP_PERIOD_VALUE_PR = "26px";
// 작업 전환 시간의 단위 — 정본은 z_routing.time_uom 이라 행마다 다를 수 있다(엔진도 그 단위로 환산).
//   미정의 값은 z_routing DDL 기본값인 분으로 본다.
const JCT_UNIT_KEY = { minutes: "minutes", seconds: "seconds", hours: "mpUnitHour", days: "dayUnit" };
// 키보드 ↑↓ 의 증감 폭 — 단위마다 현실적인 단위가 다르다(600초 옆에서 1초씩은 쓸모가 없다).
const JCT_STEP = { seconds: 30, minutes: 1, hours: 0.25, days: 1 };
// 구간은 카드 안에 직접 편다 — 툴팁은 스크롤이 없어 실데이터(자원 10건 × 구간 12개)가 화면 밖으로 잘렸다.
//   대상 행에는 최대값만 요약하고, 펼치면 전 구간을 싣는다(카드 높이가 구간 수에 끌려가지 않게).

// 값 문구 — "현재값 → 제안값" 의 '→' 뒤(변경 후)를 굵게·강조. target_changes 없는 대책의 current_display 도 같은 규칙.
function mpChangeText(text) {
  const s = String(text || "");
  const i = s.indexOf("→");
  if (i < 0) return s;
  return (
    <>
      {s.slice(0, i + 1)}
      <Box component="span" sx={{ fontWeight: 700, color: C.acc }}>{s.slice(i + 1)}</Box>
    </>
  );
}


// 대상 1행 — [타입 배지][이름(말줄임)][값(우측 정렬)].
//   값에 ml:auto·flexShrink:0 을 주는 이유 — 이름 길이가 달라도 행마다 우측 끝에서 세로로 줄맞춤된다.
//   폭이 모자라면 값이 아니라 이름을 줄인다(값이 이 행의 핵심 정보).
//   valueSub(적용 기간·미충족)은 값을 한정하는 정보라 이름 옆이 아니라 값 아래에 둔다 — 이름 폭을 뺏지 않는다.
//   대상명은 강조하지 않는다(검정) — 이 행 자체가 '대상' 이라 빨강은 문장 속에서만 의미가 있다.
function MpTargetRow({ scopeLabel, name, locName, value, valueSub }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px", minWidth: 0 }}>
      {scopeLabel && (
        <Box component="span" sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3,
          border: `0.5px solid ${C.text3}55`, borderRadius: "4px", px: "4px",
          lineHeight: "14px", flexShrink: 0, whiteSpace: "nowrap" }}>
          {scopeLabel}
        </Box>
      )}
      {/* 이름은 줄이지 않는다 — '자원 #2/#3/#4' 처럼 끝자리로만 구분되는 이름이 많아 잘리면 못 가린다. */}
      <Typography component="span" sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1,
        lineHeight: 1.5, flexShrink: 0, whiteSpace: "nowrap" }}>
        {name}
      </Typography>
      {/* 거점은 이름에 종속된 보조 정보 — 옅게, 수축은 여기서만 흡수한다.
          상한(maxWidth)은 두지 않는다 — 여유가 있어도 잘려 보이기 때문. 모자랄 때만 줄어든다. */}
      {locName && (
        <Typography component="span" sx={{ fontSize: FONT.MICRO, color: C.text3, lineHeight: 1.5,
          flexShrink: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap" }}>
          {locName}
        </Typography>
      )}
      {value && (
        <Box sx={{ ml: "auto", pl: "8px", flexShrink: 0, display: "flex", flexDirection: "column",
          alignItems: "flex-end", whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.5,
            fontVariantNumeric: "tabular-nums" }}>
            {value}
          </Box>
          {valueSub}
        </Box>
      )}
    </Box>
  );
}

function MpTargetChanges({ action }) {
  const { t } = useTranslation();
  const [openCds, setOpenCds] = useState({});   // 대상별 '구간 전체 펼침' 상태
  const rows = action?.target_changes || [];
  const pk = action?.param_key;
  const scopeKey = mpTargetScopeKey(action);
  const scopeLabel = scopeKey ? t(scopeKey) : null;
  const unit = pk === "efficiency" ? "%" : (MP_CHANGE_UNIT_KEY[pk] ? t(MP_CHANGE_UNIT_KEY[pk]) : "");
  const fmt = (v) => (v == null || v === "" ? "—" : `${v}${unit}`);
  // 잔업 대책은 값이 아니라 '그 날의 하루 능력'으로 읽는다 — 정규 8h 자원의 잔업 0→16 은
  //   8시간 → 24시간(= 정규 + 기존 잔업 + 필요 추가)이다. 잔업 숫자만 보면 천장에 닿았는지 모른다.
  //   base(정규 기준)는 백엔드가 대상 행·구간마다 내려준다. 없으면 종전대로 잔업 값만 쓴다.
  const isOverRow = pk === "over_work_hour_per_day";
  const totalOf = (v, base) => {
    const n = Number(v);
    const b = Number(base);
    if (!isOverRow || !Number.isFinite(n) || !Number.isFinite(b)) return v;
    return n + b;
  };
  // 연도까지 표기 — 계획이 연도를 넘어가면 월-일만으로는 어느 해 구간인지 못 가린다.
  const spanText = (s, e) => {
    const a = String(s || "").slice(0, 10);
    const b = String(e || "").slice(0, 10);
    return !a ? "" : (a === b ? a : `${a} ~ ${b}`);
  };
  const anyShortfall = rows.some(r => r.shortfall_hours > 0);
  // 구간 값 1개 — [기간][현재값 → 구간값]. 대상 행과 같은 조판이라 펼쳐도 열이 그대로 맞는다.
  const dateCell = (start, end) => (spanText(start, end) ? (
    <Box component="span" sx={{ fontSize: FONT.MICRO, color: C.text3,
      fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {spanText(start, end)}
    </Box>
  ) : null);
  //   isMax=true 면 '최대값' 표기 — 구간마다 값이 달라 그 기간 내내 이 값은 아니라는 뜻.
  const valueCell = (from, to, isMax) => (
    <Box component="span">
      {fmt(from)}
      <Box component="span" sx={{ mx: "3px", color: C.text3 }}>→</Box>
      {isMax && (
        // 공유 menu.max("최대값")가 아니라 값 앞에 붙는 짧은 수식어 — 간격은 CSS(mr)가 담당한다.
        <Box component="span" sx={{ color: C.text3, fontSize: FONT.MICRO, mr: "2px" }}>
          {t("mpMaxPrefixLabel")}
        </Box>
      )}
      <Box component="span" sx={{ fontWeight: 700, color: C.acc }}>{fmt(to)}</Box>
    </Box>
  );
  // 펼친 구간 행 — 날짜는 좌측, 값은 대상 행과 같은 우측 정렬 축(MP_PERIOD_VALUE_PR).
  const periodRow = (r) => (p, k) => (
    <Box key={k} sx={{ display: "flex", alignItems: "baseline", gap: "8px", minWidth: 0, pl: "7px" }}>
      {dateCell(p.start_dt, p.end_dt)}
      <Box component="span" sx={{ ml: "auto", pl: "8px", pr: MP_PERIOD_VALUE_PR, flexShrink: 0,
        whiteSpace: "nowrap", fontSize: FONT.CAPTION, color: C.text2,
        fontVariantNumeric: "tabular-nums" }}>
        {valueCell(totalOf(p.current ?? r.current, p.base ?? r.base),
                   totalOf(p.value, p.base ?? r.base))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      {rows.map((r, i) => {
        // 대상 행은 '최대값' 요약 — 구간별 실제 값은 펼쳐서 본다(요약과 명세를 층으로 나눈다).
        //   피크는 표시하는 수치(잔업 대책이면 하루 합계)로 고른다 — 구간마다 정규 기준이 다르면
        //   잔업 값이 큰 구간과 합계가 큰 구간이 갈린다.
        const ps = r.periods || [];
        const peakOf = (p) => Number(totalOf(p.value, p.base ?? r.base));
        const peak = ps.length ? ps.reduce((m, p) => (peakOf(p) > peakOf(m) ? p : m), ps[0]) : null;
        const open = !!openCds[r.cd];
        const restCount = Math.max(0, ps.length - 1);
        return (
          <Box key={i} sx={{ mt: i ? "3px" : 0, minWidth: 0 }}>
            <Box sx={STATUS_TARGET_CHIP_SX}>
              <MpTargetRow scopeLabel={scopeLabel} name={r.name} locName={r.loc_name}
                value={(
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: "6px" }}>
                    {/* 미충족 표시는 값 왼쪽에 — 오른쪽에 두면 행마다 값 끝이 어긋난다. */}
                    {r.shortfall_hours > 0 && (
                      // 아이콘은 baseline 이 아니라 가운데 정렬 — SVG 는 밑변이 baseline 이라 그대로 두면 내려앉는다.
                      <ErrorOutlineIcon titleAccess={t("mpCapacityBackcalcShortfallLabel", { hours: r.shortfall_hours })}
                        sx={{ fontSize: 13, color: C.g3, flexShrink: 0, alignSelf: "center" }} />
                    )}
                    {/* 구간이 여럿이면 날짜는 붙이지 않는다 — 어느 구간 값인지는 펼친 목록이 답한다. */}
                    {!restCount && dateCell(peak?.start_dt, peak?.end_dt)}
                    {valueCell(
                      totalOf(peak ? (peak.current ?? r.current) : r.current,
                              (peak?.base ?? r.base)),
                      totalOf(peak ? peak.value : r.value, (peak?.base ?? r.base)),
                      restCount > 0)}
                    {/* 화살표 칸은 구간이 없어도 자리를 비워 둔다 — 행마다 값의 오른쪽 끝을 맞추기 위해.
                        아이콘을 본문 줄높이보다 작게(13px) 두어 행 높이가 늘지 않게 한다. */}
                    <Box component="span" sx={{ width: 13, flexShrink: 0, alignSelf: "center",
                      display: "inline-flex", justifyContent: "center",
                      "& svg": { fontSize: 13, display: "block" },
                      ...(restCount > 0 && { cursor: "pointer" }) }}
                      onClick={restCount > 0 ? () => setOpenCds(m => ({ ...m, [r.cd]: !open })) : undefined}>
                      {restCount > 0 && <CollapseArrow open={open} color={C.acc} />}
                    </Box>
                  </Box>
                )} />
            </Box>
            {/* 구간 명세는 필 밖(흰 바탕)에 둔다 — 대상(필)과 그 내역을 층으로 나눈다. */}
            {open && (
              <Box sx={{ py: "3px" }}>
                {ps.map(periodRow(r))}
              </Box>
            )}
          </Box>
        );
      })}
      {/* 각주 — 자원마다 반복하던 불변 안내를 대책당 한 번으로. */}
      {anyShortfall && (
        <Typography sx={{ mt: "3px", fontSize: FONT.MICRO, color: C.text3, lineHeight: 1.5 }}>
          {t("mpCapacityBackcalcShortfallNote")}
        </Typography>
      )}
    </Box>
  );
}

// target_changes 가 없는 대책(RC·MC 같은 select, 품목/주문 스코프) — 대상별 값이 존재하지 않으므로
//   대책 공통값(current_display)을 행마다 싣는다. 헤더에서 값을 뺀 이상 여기가 값의 유일한 자리다.
function MpTargetNames({ action, valueText }) {
  const { t } = useTranslation();
  const scopeKey = mpTargetScopeKey(action);
  const scopeLabel = scopeKey ? t(scopeKey) : null;
  // target_list(이름+거점)가 정본. 구버전 응답은 target_names(이름만)로, 그것도 없으면 단수 필드로 폴백.
  const names = (Array.isArray(action?.target_list) && action.target_list.length > 0)
    ? action.target_list
    : (Array.isArray(action?.target_names) && action.target_names.length > 0)
      ? action.target_names.map(n => ({ name: n, loc_name: "" }))
      : [{ name: action?.target_name || action?.loc_name || t("all"),
           loc_name: action?.target_name ? (action?.loc_name || "") : "" }];
  // 우측 여백은 구간 있는 카드의 화살표 칸(13px)+gap(6px) 과 같은 몫 — 카드가 달라도 값 끝이 같은 축에 선다.
  const value = valueText
    ? <Box component="span" sx={{ pr: "19px" }}>{mpChangeText(valueText)}</Box>
    : null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      {names.map((n, i) => (
        <Box key={i} sx={{ ...STATUS_TARGET_CHIP_SX, mt: i ? "3px" : 0 }}>
          <MpTargetRow scopeLabel={scopeLabel} name={n.name} locName={n.loc_name} value={value} />
        </Box>
      ))}
    </Box>
  );
}

function MpRecommendedActions({ actions, loading, names }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);   // 기본 접힘 — 공간 절약. 헤더/요약 클릭 시 펼침.
  const acts = actions || [];
  // 자원명·자재명·거점명 + 대책별 대상명(target_names, 예: 대체 자원 정책의 공정명) → 빨강 강조.
  //   공정명은 collectDataNames(자원·품목·거점)에 없어, 대상 문구에서 품목명 부분만 부분 강조되던 문제 보정.
  const redNames = [...new Set([...(names || []), ...acts.flatMap(a => a.target_names || [])])];
  const supported = acts.filter(a => a.supported);
  const refs      = acts.filter(a => !a.supported);

  // 파라미터 명칭의 정본은 LLM 문구가 아니라 i18n(param_label_key) — 백엔드 ko 라벨(param_label)을
  //   번역값으로 치환해, 카탈로그와 번역이 어긋나도 헤더는 PlanNEL 표준 문구로 나오게 한다.
  //   (비-ko 응답은 문장이 그 언어라 param_label 이 없어 무변경. 키 미등록 시 t()가 키를 되돌려주므로 폴백.)
  const paramLabel = (a) => {
    const k = a.param_label_key;
    const v = k ? t(k) : "";
    return (v && v !== k) ? v : (a.param_label || "");
  };
  const localizeParam = (text, a) => {
    const loc = paramLabel(a);
    return (text && a.param_label && loc && loc !== a.param_label)
      ? text.split(a.param_label).join(loc) : text;
  };
  // 선택지 값(자재 제약·대체 자원 정책 등)은 백엔드 문장이 아니라 i18n 키로 조립한다 —
  //   백엔드가 언어별 문자열을 들 필요가 없고, 표기도 설정 화면과 같은 표준 문구로 통일된다.
  //   키가 없는 대책(수치 파라미터)·구버전 응답은 null 을 돌려 current_display 폴백으로 넘긴다.
  const tKey = (k) => { const v = k ? t(k) : ""; return (v && v !== k) ? v : ""; };
  const valueTextFromKeys = (a) => {
    const next = tKey(a.value_key);
    if (!next) return null;
    const cur = tKey(a.current_value_key);
    return cur ? `${cur} → ${next}` : next;
  };
  // 카드 제목 — i18n 키가 정본. 키가 아직 번역에 없거나 구버전 응답이면 LLM 문구로 폴백한다.
  const actionTitle = (a) => tKey(a.action_label_key) || localizeParam(a.action_label || a.action, a);
  // 기대효과 — 진단형 대책은 고정 문구(i18n 키)가 정본. 그 외는 LLM 문구.
  const actionEffect = (a) => tKey(a.expected_effect_key) || a.expected_effect;
  // 근거 — reason_key 가 오면 그것이 정본(LLM 자유문은 ko 전용 경로에서만 온다).
  const actionReason = (a) => tKey(a.reason_key) || a.reason;

  // MP_GROUP_ORDER(자원/생산 공정/자재/계획 정책) 순서로 묶고, 비어있는 그룹은 제외.
  //   g.label 값은 i18n 키 문자열이라 여기서 t()로 변환한다.
  const groups = MP_GROUP_ORDER
    .map(g => ({ key: g.key, label: t(g.label), icon: g.icon,
      items: supported.filter(a => (a.param_group || "global") === g.key) }))
    .filter(g => g.items.length > 0);
  if (refs.length) groups.push({ key: "ref", label: t("mpReferenceLabel"), icon: "", items: refs, ref: true });

  const canToggle = acts.length > 0;

  return (
    <StatusSection
      title={t("mpRecommendedActionsTitle")}
      subtitle={t("mpAiRecommendedSubtitle")}
      // 헤더 클릭으로 펼침/접힘 토글(추천 대책이 있을 때만). open 은 화살표 방향 표시용.
      onHeaderClick={canToggle ? () => setOpen(o => !o) : undefined}
      open={open}
      right={(
        <Stack direction="row" alignItems="center" gap={0.5}>
          {loading && <CircularProgress size={12} sx={{ color: C.acc }} />}
          {acts.length > 0 && (
            <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>{t("countCases", { count: acts.length })}</Typography>
          )}
          {canToggle && (
            // 현황 분석 화면(StatusSection collapsible)과 동일한 공용 화살표로 통일.
            //   헤더 전체가 토글이므로 여기선 시각 표시만 담당.
            <CollapseArrow open={open} ml={0.5} />
          )}
        </Stack>
      )}
      contentSx={{ p: "10px 14px !important" }}
    >
      {acts.length === 0 ? (
        <Stack direction="row" alignItems="center" gap={1}>
          {loading
            ? <CircularProgress size={14} sx={{ color: C.acc }} />
            : <InfoOutlinedIcon sx={{ fontSize: 15, color: C.g1 }} />}
          <Typography sx={{ fontSize: FONT.SUB, color: C.text3 }}>
            {loading ? t("mpRecActionsGeneratingDesc") : t("mpNoRecActionsDesc")}
          </Typography>
        </Stack>
      ) : (
        <>
          {/* 접힘 상태: 그룹별 건수를 칩으로 요약(클릭 시 펼침) */}
          {!open && (
            <Box onClick={() => setOpen(true)}
              sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, cursor: "pointer" }}>
              {groups.map(g => {
                // 펼친 카드와 같은 장치 — 색은 도트만. 칩까지 색으로 채우면 접힘(컬러)/펼침(무채색)에서
                //   같은 그룹의 색 세기가 뒤집혀 두 상태가 다른 화면처럼 읽힌다.
                const gc = mpGroupColor(g.key);
                return (
                  <Box key={g.key} sx={{ display: "inline-flex", alignItems: "center", gap: "6px",
                    bgcolor: C.bg3, borderRadius: "10px", p: "3px 9px", fontSize: FONT.CAPTION }}>
                    <Box component="span" sx={{ width: 5, height: 5, borderRadius: "50%",
                      bgcolor: gc.bar, flexShrink: 0 }} />
                    <Box component="span" sx={{ color: C.text2 }}>{g.label}</Box>
                    {/* 건수는 접힘 상태의 유일한 정보라 뮤트하지 않는다. 자릿수는 tabular-nums 로 고정폭. */}
                    <Box component="span" sx={{ fontWeight: 700, color: C.text1,
                      fontVariantNumeric: "tabular-nums" }}>
                      {g.items.length}
                    </Box>
                  </Box>
                );
              })}
              <Typography sx={{ fontSize: FONT.CAPTION, color: C.acc, fontWeight: 600, ml: "auto", flexShrink: 0 }}>
                {t("mpViewAllLabel")}
              </Typography>
            </Box>
          )}
          {/* 펼침 상태: 추천 1건 = 카드. 그룹 순서를 유지하며 평탄화, 가로 3열 그리드. */}
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={STATUS_CARD_GRID_SX}>
              {groups.flatMap(g => {
                const gc = mpGroupColor(g.key);
                // 그룹은 도트 색 + 라벨로만 구분한다 — 틴트 헤더·컬러 테두리를 그룹 수(4+참고)만큼
                //   쓰면 카드가 색 덩어리로 읽혀 정작 제목·대상이 뒤로 밀린다.
                const accent = { color: gc.bar, text: gc.text };
                return g.items.map((a, i) => (
                  // 제목 정본은 i18n(action_label_key), 미등록 키·구버전 응답은 LLM 문구로 폴백.
                  <StatusInsightCard key={`${g.key}-${i}`} accent={accent} eyebrow={g.label} dim={g.ref}
                    title={highlightText(actionTitle(a), redNames, C.accT)}>
                    {/* 대상 = 한 대상 = 한 행. 행마다 타입 배지(자원/자재/주문…)를 붙인다 —
                        그룹(무엇을 바꾸는가)과 대상 타입(어디에 적용하는가)은 별개 축이라 어긋날 수 있다. */}
                    {(a.target_name || a.loc_name || a.target_names?.length > 0 || a.current_display) && (
                      <StatusCardField label={t("targetObj")}>
                        {/* 대상별 변경 내역이 있으면 '현재값→제안값·적용 기간'까지(시나리오 override 와 같은 값).
                            없으면 대책 공통값(current_display)을 행마다 싣는 폴백. */}
                        {a.target_changes?.length > 0 ? (
                          <MpTargetChanges action={a} />
                        ) : (
                          <MpTargetNames action={a}
                            valueText={valueTextFromKeys(a) ?? localizeParam(a.current_display, a)} />
                        )}
                      </StatusCardField>
                    )}
                    {actionReason(a) && (
                      <StatusCardField label={t("mpReasonLabel")}>
                        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.5 }}>
                          {highlightText(actionReason(a), redNames, C.accT)}
                        </Typography>
                      </StatusCardField>
                    )}
                    {actionEffect(a) && (
                      <StatusCardField label={t("mpExpectedEffectLabel")}>
                        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.5 }}>
                          {highlightText(actionEffect(a), redNames, C.accT)}
                        </Typography>
                      </StatusCardField>
                    )}
                  </StatusInsightCard>
                ));
              })}
            </Box>
          </Collapse>
        </>
      )}
    </StatusSection>
  );
}

function MpComposeExtraContent({ cache, onCacheChange }) {
  const [data,    setData]    = useState(cache ?? null);
  const [loading, setLoading] = useState(false);
  const lang = getAgentLanguage();   // LLM 문장은 이 언어로 생성된다 — 바뀌면 요약을 다시 받는다.

  useEffect(() => {
    // status_narrative 를 이미 포함한 캐시(현황 분석 탭 initialState 공유)면 재요청 불필요.
    //   KPI 만 있는 캐시(요약 미완)거나 캐시 언어가 현재 언어와 다르면 요약을 받으러 간다.
    if (cache?.status_narrative && cache._lang === lang) return;
    setLoading(true);
    // 현황 분석 탭과 동일한 initial-state(status_narrative 포함)를 사용.
    // 서비스단 in-flight 공유 + 백엔드 in-flight 집약으로 동시 호출이어도 LLM 은 1회만 실행된다.
    scenarioService.getMpInitialState(true)
      .then(res => {
        setData(res);
        onCacheChange?.(res);
      })
      .catch(err => console.warn("[MP] initial-state(권장 대책) 조회 실패:", err))
      .finally(() => setLoading(false));
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MpRecommendedActions
      actions={data?.status_narrative?.recommended_actions}
      loading={loading}
      names={collectDataNames(data)}
    />
  );
}

MP_MODULE.ComposeExtraContent = MpComposeExtraContent;

// ── MP 시나리오 파라미터 위젯 (EditableScenarioCards 가 mod.CustomScenarioFields 로 렌더) ──
// param_defs(엔진 키: RC/efficiency/work_hour_per_day/work_day_per_week/alt_res_policy) 기반.
//  - base : 현행 유지(읽기전용, current_display=MP 설정·자원 마스터 값).
//  - alt1/alt2 : per-param 스코프 모델 — 파라미터마다 적용 단위(전체/Work Center/자원/Route)와
//                대상을 선택해 값을 변경. overrides = per-param 엔트리 목록.
// label 값은 i18n 키 문자열 — 모듈 레벨 상수라 t()를 직접 쓸 수 없으므로 소비처(MpScenarioParamFields 등)에서 t()로 변환한다.
const MP_SCOPE_LABEL = { all: "all", workcenter: "wcCd", resource: "productionResource", route: "productionRoute", routing_op: "routing", loc_item: "mpOrderLabel", mc_order: "mpOrderLabel", material: "mpMaterialLabel" };
// 권장 대책 '대상' 타입 배지의 i18n 키 — 시나리오 편집기의 '적용 단위' 필과 같은 어휘(MP_SCOPE_LABEL)를 재사용.
//   백엔드 정합 scope(target_scope) 우선, 미부착(구버전·부착 실패) 시 LLM 원본 scope 폴백.
//   전역(all)은 대상 문구 "전체" 자체가 타입이라 배지 생략, 참고(param_key 없음)는 scope=null → 생략.
// 배지는 대상 행마다 반복되므로 짧은 어휘를 쓴다 — 편집기의 '적용 단위' 필(MP_SCOPE_LABEL)은 그대로 둔다.
const MP_TARGET_SCOPE_LABEL = { ...MP_SCOPE_LABEL, resource: "mpResourceLabel", route: "route" };
const mpTargetScopeKey = (a) => {
  const s = a?.target_scope ?? a?.scope;
  return (s && s !== "all") ? (MP_TARGET_SCOPE_LABEL[s] || null) : null;
};
// 파라미터 도메인 그룹 — 표시 순서·라벨·아이콘은 프론트 소유, group 키는 백엔드 param_defs[].group.
// 미지정/미매핑 def 는 마지막 그룹(global)으로 폴백.
const MP_GROUP_ORDER = [
  { key: "global",     label: "planPolicy", icon: "" },
  { key: "resource",   label: "productionResource",    icon: "" },
  { key: "production", label: "productionRoute", icon: "" },
  { key: "material",   label: "mpMaterialLabel",    icon: "" },
];
// 권장 대책 그룹별 색상(좌측 컬러바·연한 배경·라벨 색) — 아이콘 색과 연동해 그룹 경계를 명확히.
const MP_GROUP_COLOR = {
  resource:   { bar: C.acc,     bg: C.accL,   text: C.accT },   // 자원 — 파랑
  production: { bar: C.g2,      bg: C.g2l,    text: C.g2t  },   // 생산 공정 — 앰버
  material:   { bar: "#7A5AF8", bg: "#EFEBFD", text: "#4A2FB0" }, // 자재 — 바이올렛
  global:     { bar: C.g1,      bg: C.g1l,    text: C.g1t  },   // 계획 정책 — 그린
  ref:        { bar: C.text3,   bg: C.bg3,    text: C.text3 },  // 참고 — 그레이
};
const mpGroupColor = (key) => MP_GROUP_COLOR[key] || { bar: C.text3, bg: C.bg3, text: C.text2 };
const MP_GROUP_KEYS = new Set(MP_GROUP_ORDER.map(g => g.key));
const mpGroupOf = (def) => (MP_GROUP_KEYS.has(def?.group) ? def.group : "global");
// 입력 지연 방지 — 한 글자·스피너 한 번마다 시나리오 state 를 갱신하면 카드 전체가 다시 그려지고
//   viewState 직렬화까지 따라붙어 입력이 늦게 들어온다. 편집 중에는 로컬 값만 바꾸고
//   입력이 멈춘 뒤(commitDelay)·blur·Enter 에 한 번만 올린다.
//   밖에서 값이 바뀌면 편집 중이 아닐 때만 따라간다. 상한/하한 규칙은 호출부마다 달라 clamp 함수로 받는다.
const DRAFT_COMMIT_DELAY_MS = 400;
const DraftNumberInput = ({ value, onCommit, disabled, step, min, max, placeholder, clamp, sx,
                           commitDelay = DRAFT_COMMIT_DELAY_MS }) => {
  const [draft, setDraft] = React.useState(value ?? "");
  const [editing, setEditing] = React.useState(false);
  const timerRef = React.useRef(null);
  const pendingRef = React.useRef(null);
  // 늦게 터지는 타이머가 stale 클로저를 잡지 않도록 최신 props 를 ref 로 쥔다.
  const latestRef = React.useRef(null);
  latestRef.current = { value, onCommit, clamp };
  React.useEffect(() => { if (!editing) setDraft(value ?? ""); }, [value, editing]);
  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    pendingRef.current = null;
  };
  const commit = (raw) => {
    const { value: cur, onCommit: fn, clamp: cl } = latestRef.current;
    const out = cl ? cl(raw) : raw;
    if (String(out) !== String(raw)) setDraft(out);            // 클램프 결과를 입력칸에도 되돌린다
    if (String(out) !== String(cur ?? "")) fn?.(out);
  };
  const commitRef = React.useRef(null);
  commitRef.current = commit;
  // 확정 전에 행이 사라져도(접힘·외부 갱신) 입력을 잃지 않는다.
  React.useEffect(() => () => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current);
    const raw = pendingRef.current;
    if (raw != null) commitRef.current(raw);
  }, []);
  return (
    <Box component="input" type="number" disabled={disabled} step={step}
      min={min} max={max} placeholder={placeholder}
      value={draft}
      onFocus={() => setEditing(true)}
      onChange={(e) => {
        const v = e.target.value;
        setDraft(v);
        clearTimer();
        pendingRef.current = v;
        timerRef.current = setTimeout(() => { clearTimer(); commitRef.current(v); }, commitDelay);
      }}
      onBlur={() => { clearTimer(); setEditing(false); commit(draft); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      sx={sx} />
  );
};
// 대상 드롭다운의 좌측 dot — 능력부족(CAPACITY) 문제를 안고 있는 항목만 붉게. 색은 칩의 심각도 dot 과 동일.
//   문제가 없어도 자리는 남긴다 — 있는 행만 dot 을 그리면 라벨 시작점이 어긋나 목록이 읽기 어렵다.
const problemDot = (hasProblem) => (
  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: hasProblem ? C.g3 : "transparent" }} />
);
// 파라미터별 입력 상한 — 작업 시간/일 24h, 주당 작업일 수 7일.
const MP_NUM_MAX = { work_hour_per_day: 24, over_work_hour_per_day: 24, work_day_per_week: 7 };
// 하루 천장(정규+초과) — 엔진은 이 상한을 걸지 않으므로(BucketCalendar 는 가산만 한다) 화면이 지킨다.
const MP_DAY_HOUR_CAP = 24;
// 부족 글자색 — 토큰 g3t(#791F1F)는 연한 빨강 칩 위에서 거의 검정으로 보인다(대비 9:1).
//   칩 배경(g3l) 대비 5.7:1 로 AA 를 지키면서 빨강으로 읽히는 선까지 밝힌다.
const RES_SHORT_TEXT = "#B42322";
// 자원 행의 부족·지연 건수 칩 — 부족(미납)이 지연보다 심각하므로 빨강 틴트로 세우고, 지연은 회색 보조.
const countChipSx = (isShort) => ({
  display: "inline-flex", alignItems: "center", height: 15, px: "5px", borderRadius: "999px",
  fontSize: FONT.MICRO, fontWeight: 700, whiteSpace: "nowrap", lineHeight: 1,
  bgcolor: isShort ? C.g3l : C.bg3,
  border: `1px solid ${isShort ? C.g3s : C.border}`,
  color: isShort ? RES_SHORT_TEXT : C.text3,
});
// 하한 + 하드 클램프(범위 밖 입력 차단) 대상 — 주당 작업일 수는 1~7 만 유효(0·8+ 금지).
const MP_NUM_MIN = { work_day_per_week: 1 };
const MP_NUM_HARD = new Set(["work_day_per_week"]);   // min~max 하드 클램프(초과/미만 입력 자체를 막음)
// 기간별 능력 변경 지원 파라미터 — 자원 효율(%)·작업 시간/일(정규/초과). (주당 작업일 수는 전체 기간만)
const MP_PERIOD_CAPABLE = new Set(["efficiency", "work_hour_per_day", "over_work_hour_per_day"]);
// 한 행에 묶어 편집하는 파라미터 그룹은 카탈로그의 row_group/row_role 이 정본이다.
//   (로트 3종은 같은 z_routing 행의 속성이라 쪼개면 min>max 를 눈으로 잡을 수 없다.
//    정규/초과 작업 시간은 엔진이 버킷 능력을 한 번에 치환해 늘 같이 결정된다.)
//   입력칸 라벨은 (row_group, row_role) → 공용 i18n 키. 같은 role 이라도 그룹마다 뜻이 다르므로
//   (로트의 primary=배수 로트, 작업 시간의 primary=정규 시간) 그룹까지 키에 넣는다.
const MP_ROW_ROLE_LABEL = {
  lot_size:          { primary: "lotSize", min: "min", max: "max" },
  work_hour_per_day: { primary: "mpWorkHourRegularLabel", over: "mpWorkHourOverLabel" },
};
const mpRowRoleLabel = (d) => MP_ROW_ROLE_LABEL[d?.row_group]?.[d?.row_role] || null;
// 단일 값 파라미터의 열 머리글 — 단위는 정보가 되는 것만 괄호로 붙인다('일수'에 '일'은 겹말).
const MP_RES_HEAD_LABEL = {
  efficiency:        { key: "efficiency", unit: true },
  work_day_per_week: { key: "mpWorkDayLabel", unit: false },
};

// 자원 편집 표의 열 폭 — 값 열은 고정이고 식별 열(이름·건수)만 남는 폭을 먹는다.
const RES_CTRL_H = PARAM_CTRL_H;   // 행 안 입력·아이콘 버튼·날짜 입력 공통 높이
const RES_ROW_H = PARAM_ROW_MIN_H; // 데이터·구간 행 최소 높이
const RES_ROW_PY = (RES_ROW_H - RES_CTRL_H) / 2;   // 입력과 행 괘선 사이 여백 — 포커스 링이 행 안에 들어간다
const RES_NUM_W = 52;      // 값 입력 — 4자리(13.3·110)와 스피너가 들어가는 폭, 전 파라미터 공통
const RES_HEAD_PL = 7;     // 값 열 머리글 들여쓰기 — 입력칸 테두리+안쪽 여백만큼 밀어 숫자와 첫 글자를 맞춘다
const RES_SEL_W = 15;      // 선택 체크박스
const RES_RESET_W = 14;    // 되돌리기 슬롯 — 버튼이 없을 때도 비워 둬야 값 열이 흔들리지 않는다
const RES_SUM_W = 54;      // 합계 — 소수 + 단위('13.3시간')가 한 줄에 들어가는 실측 폭
const RES_DEL_W = RES_CTRL_H;   // 행 삭제 — 입력과 같은 높이
const RES_BTN_W = RES_DEL_W;   // 기간별 — 같은 행에 나란히 서므로 삭제 버튼과 크기를 맞춘다
const RES_ICON_PX = 12;    // 두 버튼의 글리프 크기
const RES_ICON_TR = "background-color .12s, box-shadow .12s, border-color .12s, color .12s";
const RES_CNT_MIN = 28;    // 건수 열 최소 — CJK 머리글(부족·지연) 기준
const RES_CNT_MAX = 46;    // 건수 열 최대 — 라틴 머리글이 길어도 이름 폭을 다 먹지 않게
const RES_ID_MIN = { wide: 260, mid: 220, narrow: 180 };
const RES_TIER_WIDE = 700, RES_TIER_MID = 500;

// 편집 표의 폭 티어 — 챗 패널 개폐로 카드 폭이 변하므로 뷰포트가 아니라 자기 폭을 잰다.
function ResGridFrame({ children }) {
  const ref = React.useRef(null);
  const [tier, setTier] = React.useState("wide");
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);   // 같은 프레임에 되쓰면 ResizeObserver 루프 경고가 난다
      raf = requestAnimationFrame(() => {
        const w = el.clientWidth;
        setTier(w >= RES_TIER_WIDE ? "wide" : (w >= RES_TIER_MID ? "mid" : "narrow"));
      });
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);
  // 글자 크기 토글은 <html data-font-scale> 만 바꾼다 — 머리글 실측 폭을 다시 재도록 다시 그린다.
  const [, setFontScale] = React.useState(null);
  React.useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const root = document.documentElement;
    const mo = new MutationObserver(() => setFontScale(root.dataset.fontScale));
    mo.observe(root, { attributes: true, attributeFilter: ["data-font-scale"] });
    return () => mo.disconnect();
  }, []);
  return <Box ref={ref}>{children(tier)}</Box>;
}
// 기간 입력 ↔ Date 변환 — DatePicker 는 Date 객체, override.periods 는 'yyyy-MM-dd' 문자열로 저장.
const periodStrToDate = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  return (y && m && d) ? new Date(y, m - 1, d) : null;
};
const periodDateToStr = (dt) => {
  if (!(dt instanceof Date) || isNaN(dt.getTime())) return "";
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
};

// 비가동 요일(JS getDay 0=일..6=토) — 엔진 wdpwNonWorkingDows 규칙 재현:
//   count=round(7-wdpw), 가동 시작 요일 전날부터 backward 로 count 개. wdpw>=7/미상 → 전일 가동(빈 집합).
function nonWorkingDows(wdpw, workStartDay) {
  const DOW = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const off = new Set(); const w = Number(wdpw);
  if (!(w > 0) || w >= 7) return off;
  const count = Math.round(7 - w);
  let cur = (((DOW[String(workStartDay || "mon").toLowerCase()] ?? 1) - 1) + 7) % 7;
  for (let i = 0; i < count; i++) { off.add(cur); cur = (cur - 1 + 7) % 7; }
  return off;
}
// iso 날짜가 비가동일이면 이전 가동일로 스냅(가동일/off 없음이면 그대로). 표시·leveling 공용.
function snapPrevWorkingDay(iso, wdpw, workStartDay) {
  if (!iso) return iso;
  const off = nonWorkingDows(wdpw, workStartDay);
  if (!off.size) return iso;
  const shift = (s, d) => { const x = new Date(s + "T00:00:00"); x.setDate(x.getDate() + d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
  let d = iso;
  while (off.has(new Date(d + "T00:00:00").getDay())) d = shift(d, -1);
  return d;
}

// 능력 레벨링(산 崩し) — 선택 주문의 날짜별 필요 추가능력을 deadline(=날짜) 지키며 후방 채움.
//   pairs=[{date, add(자원당 필요 추가능력 합, float)}] → 비중복 period[{start_dt,end_dt,value}].
//   각 날짜를 마감으로, 그 날부터 과거로 하루 room(=24-base) 상한까지 채운다(겹치면 같은 날에 가산=자동 병합).
//   plan_start 이전으로는 못 넘겨 넘치는 분은 dump(→24 초과=입력창 빨강). 연속·동일 value 일은 한 period 로 병합.
// pairs=[{date, add, base_whpd?}]. 날짜별 base(마스터 기간별 능력이 덮으면 그 값, 아니면 fallbackBase=플랫 whpd)
//   기준으로 room=24−base 를 지켜 후방 채움. 분산(마감→이전 가동일)된 날은 base 정보가 없어 fallbackBase 사용.
// overMode=true 면 값이 '초과(잔업) 시간' — 그 날 잔업 기준(base_over_whpd) + 추가분. 천장은 24−정규기준
//   이라 정규와 합쳐 24h 를 넘지 않는다(파이썬 capacity_periods.level_periods 와 같은 의미론).
function levelPeriods(pairs, fallbackBase, planStart, wdpw, workStartDay, overMode, fallbackOver) {
  const EPS = 1e-9;
  const shift = (iso, delta) => {
    const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + delta);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const off = nonWorkingDows(wdpw, workStartDay);   // 비가동 요일 집합(모듈 공용 헬퍼)
  const isWork = (iso) => !off.has(new Date(iso + "T00:00:00").getDay());
  const prevWork = (iso) => { let d = shift(iso, -1); while (!isWork(d)) d = shift(d, -1); return d; };
  const nextWork = (iso) => { let d = shift(iso, 1); while (!isWork(d)) d = shift(d, 1); return d; };

  const fbOver = Number(fallbackOver) || 0;
  const items = (pairs || [])
    .filter((p) => p.date && (Number(p.add) || 0) > 0)
    .map((p) => ({ date: p.date, add: Number(p.add),
                   base: (p.base_whpd == null) ? fallbackBase : Number(p.base_whpd),
                   over: (p.base_over_whpd == null) ? fbOver : Number(p.base_over_whpd) }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  if (!items.length) return [];

  const dayExtra = new Map();  // isoDay(가동일) -> 추가 시간
  const dayBase = new Map();   // isoDay -> 기준능력(마감일=그 날 base, 분산일=fallbackBase)
  const dayOver = new Map();   // isoDay -> 그 날 이미 걸려 있는 잔업(천장 계산·초과 모드의 시작값)
  const baseAt = (day) => (dayBase.has(day) ? dayBase.get(day) : fallbackBase);
  const overAt = (day) => (dayOver.has(day) ? dayOver.get(day) : fbOver);
  for (const { date, add, base, over } of items) {
    const dline = isWork(date) ? date : prevWork(date);   // 마감이 비가동이면 이전 가동일로 스냅
    if (24 - base - over <= EPS) {  // 그 날 천장(정규+잔업)이 이미 24h+ → 마감일에 몰아 표기(24 초과=빨강)
      if (!dayBase.has(dline)) dayBase.set(dline, base);
      if (!dayOver.has(dline)) dayOver.set(dline, over);
      dayExtra.set(dline, (dayExtra.get(dline) || 0) + add);
      continue;
    }
    let remaining = add;
    let day = dline;
    if (!dayBase.has(day)) dayBase.set(day, base);        // 마감(스냅)일 = 그 날 실제 기준능력
    if (!dayOver.has(day)) dayOver.set(day, over);
    while (remaining > EPS && (!planStart || day >= planStart)) {
      if (!dayBase.has(day)) dayBase.set(day, fallbackBase);   // 분산일 = 플랫 기준
      if (!dayOver.has(day)) dayOver.set(day, fbOver);
      const cur = dayExtra.get(day) || 0;
      const avail = (24 - baseAt(day) - overAt(day)) - cur;
      if (avail > EPS) { const put = Math.min(remaining, avail); dayExtra.set(day, cur + put); remaining -= put; }
      day = prevWork(day);                               // 비가동 요일 건너뜀
    }
    if (remaining > EPS) {  // plan_start 이전으로 못 넘김 → 넘치는 분 dump(가장 이른 가동일, 24 초과=빨강)
      let d = planStart || date;
      if (!isWork(d)) d = nextWork(d);
      if (!dayBase.has(d)) dayBase.set(d, fallbackBase);
      if (!dayOver.has(d)) dayOver.set(d, fbOver);
      dayExtra.set(d, (dayExtra.get(d) || 0) + remaining);
    }
  }
  const out = [];
  for (const day of [...dayExtra.keys()].sort()) {
    const value = (overMode ? overAt(day) : baseAt(day)) + Math.ceil(dayExtra.get(day));
    const last = out[out.length - 1];
    // 연속 '가동일' 이 동일 value 면 병합(사이 주말/비가동일은 range 에 포함되나 엔진이 skip).
    if (last && last.value === String(value) && nextWork(last.end_dt) === day) last.end_dt = day;
    else out.push({ start_dt: day, end_dt: day, value: String(value) });
  }
  return out;
}

// 효율 역산 적용 — pairs=[{date, eff}](날짜별 효율 배수) → 가동일 스냅 [day,day] period, 연속 동일값 병합.
//   whpd 와 달리 24h 분배(leveling) 없음: 효율은 스칼라 배수라 그 날 값만 override.
function efficiencyPeriods(pairs, wdpw, workStartDay) {
  const shift = (iso, delta) => {
    const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + delta);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const off = nonWorkingDows(wdpw, workStartDay);
  const isWork = (iso) => !off.has(new Date(iso + "T00:00:00").getDay());
  const prevWork = (iso) => { let d = shift(iso, -1); while (!isWork(d)) d = shift(d, -1); return d; };
  const nextWork = (iso) => { let d = shift(iso, 1); while (!isWork(d)) d = shift(d, 1); return d; };
  const items = (pairs || [])
    .filter((p) => p.date && (Number(p.eff) || 0) > 0)
    .map((p) => ({ date: isWork(p.date) ? p.date : prevWork(p.date), eff: Number(p.eff) }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const out = [];
  for (const { date, eff } of items) {
    const value = String(Math.round(eff * 100) / 100);   // 배수 2자리
    const last = out[out.length - 1];
    if (last && last.value === value && nextWork(last.end_dt) === date) last.end_dt = date;
    else out.push({ start_dt: date, end_dt: date, value });
  }
  return out;
}

// 자원 행 '상세' — 능력 역산(주문별 필요 추가 가동시간) Modal. 업무 화면 정석(MUI Dialog).
//   data = { pk, cand, unit, bc:{ orders, additional_hours_total, order_count, target_whpd, other_count, other_hours } }
function BackcalcDetailModal({ data, onClose, onApplyPeriods, tr }) {
  const bc = data?.bc;
  const orders = bc?.orders || [];
  const isEff = data?.pk === "efficiency";                            // variant — 효율 역산
  const wdpw = Number(data?.cand?.current?.work_day_per_week) || 7;   // 주당 작업일 수(비가동 요일 산출)
  const wsd = data?.cand?.current?.work_start_day || "mon";           // 가동 시작 요일
  const [checked, setChecked] = React.useState(() => new Set());
  const [visibleCount, setVisibleCount] = React.useState(10);
  const [editedEff, setEditedEff] = React.useState({});              // 효율 variant: 주문(order_cd)별 편집한 필요 효율%
  React.useEffect(() => { setChecked(new Set()); setVisibleCount(10); setEditedEff({}); }, [data]);
  // 효율 역산 — 주문 단위. 현재% = 현재효율×100. 필요%(prefill) = 현재% × (1 + 그 주문 add/base).
  const curEffPct = (o) => Math.round((Number(o.current_eff) || 1) * 100);
  const reqEffPct = (o) => {   // 필요% = 정수로 올림(ceil) — 효율은 소수점1(정수%) 올림
    const eff = Number(o.current_eff) || 1;
    const base = Number(o.base_whpd) || 0;
    const add = Number(o.additional_hours_per_res) || 0;
    return Math.ceil(eff * ((base > 0) ? 1 + add / base : 1) * 100);
  };
  const rowEffPct = (o) => {   // 편집값 우선, 없으면 계산 prefill
    const v = editedEff[o.order_cd];
    return (v != null && v !== "") ? Number(v) : reqEffPct(o);
  };
  const cellSx = { fontSize: FONT.MICRO, color: C.text2, px: "6px", py: "3px",
                   borderBottom: `0.5px solid ${C.border}`, whiteSpace: "nowrap" };
  const headSx = { ...cellSx, fontWeight: 700, color: C.text3, borderBottom: `1px solid ${C.border}`,
                   whiteSpace: "normal", lineHeight: 1.2,                 // 좁은 열의 긴 헤더 줄바꿈 허용
                   position: "sticky", top: 0, zIndex: 1, bgcolor: C.bg2, // 스크롤 시 헤더 고정(불투명 배경)
                   boxShadow: `inset 0 -1px 0 ${C.border}` };             // collapse 경계선 유실 방지
  const shown = orders.slice(0, visibleCount);
  const allChecked = orders.length > 0 && checked.size === orders.length;
  const toggle = (cd) => setChecked((s) => { const n = new Set(s); if (n.has(cd)) n.delete(cd); else n.add(cd); return n; });
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(orders.map((o) => o.order_cd)));
  // 행 선택 = canonical 체크박스풍 button+✓(자원 편집 표의 행 선택과 동일). Switch 아님(선택→별버튼 submit).
  const cbSx = (on) => ({ flex: "0 0 auto", width: 15, height: 15, borderRadius: "4px", p: 0,
    border: `1.5px solid ${on ? C.acc : C.border}`, bgcolor: on ? C.acc : "transparent",
    color: "#fff", fontSize: 10, fontWeight: 800, lineHeight: 1, cursor: "pointer",
    boxSizing: "border-box", verticalAlign: "middle",  // ✓ 유무로 baseline 이 바뀌어 셀 높이가 변하는 것 방지
    display: "inline-flex", alignItems: "center", justifyContent: "center" });
  const apply = () => {
    // 효율 variant: [{date, eff}](날짜별 효율 배수). whpd variant: [{date, add, base_whpd}].
    //   전체 선택 시 backend 집약(all_period_agg = 전 주문) 사용 → '표시분만 적용' 문제 해소.
    let pairs;
    const useAgg = allChecked && Array.isArray(bc?.all_period_agg) && bc.all_period_agg.length;
    if (isEff) {
      if (useAgg) {
        // 전체 선택: 적용일(가동일 스냅) 기준으로 add 집약(raw 날짜 달라도 스냅 후 같으면 합산)
        //   → 필요% 정수 올림 → 배수 = 필요%/100/현재효율.
        const byDate = new Map();   // 적용일 → { add, base, eff }
        bc.all_period_agg.forEach((p) => {
          if (!p.date) return;
          const d = snapPrevWorkingDay(p.date, wdpw, wsd);
          const e = byDate.get(d) || { add: 0, base: Number(p.base_whpd) || 0, eff: Number(p.current_eff) || 1 };
          e.add += Number(p.add) || 0;
          byDate.set(d, e);
        });
        pairs = [...byDate.entries()].map(([date, e]) => {
          const pct = Math.ceil(e.eff * ((e.base > 0) ? 1 + e.add / e.base : 1) * 100);
          return { date, eff: (pct / 100) / (e.eff || 1) };
        });
      } else {
        // 부분 선택: 적용일(가동일 스냅) 기준으로 선택 주문의 필요 '증가%p'(행% − 현재%)를 합산.
        //   ⚠ raw short_date 가 달라도 스냅 후 같은 적용일이면 한 구간으로 합산해야 함(납기/품목 상이 케이스).
        const byDate = new Map();   // 적용일 → { curPct, eff, inc }
        orders.filter((o) => checked.has(o.order_cd)).forEach((o) => {
          if (!o.short_date) return;
          const d = snapPrevWorkingDay(o.short_date, wdpw, wsd);   // 적용일(가동일 스냅) 기준 집약
          const eff = Number(o.current_eff) || 1;
          const e = byDate.get(d) || { curPct: Math.round(eff * 100), eff, inc: 0 };
          e.inc += rowEffPct(o) - Math.round(eff * 100);
          byDate.set(d, e);
        });
        pairs = [...byDate.entries()].map(([date, e]) => ({ date, eff: (e.curPct + e.inc) / 100 / (e.eff || 1) }));
      }
    } else if (useAgg) {
      pairs = bc.all_period_agg;   // {date, add, base_whpd, master_period_covered} — 날짜별 base 포함
    } else {
      const byDate = new Map();
      const baseByDate = new Map();   // 날짜별 기준능력(마스터 기간이면 그 값)
      orders.filter((o) => checked.has(o.order_cd)).forEach((o) => {
        if (!o.short_date) return;
        byDate.set(o.short_date, (byDate.get(o.short_date) || 0) + (Number(o.additional_hours_per_res) || 0));
        if (o.base_whpd != null) baseByDate.set(o.short_date, Number(o.base_whpd));
      });
      pairs = [...byDate.entries()].map(([date, add]) => ({ date, add, base_whpd: baseByDate.get(date) }));
    }
    if (pairs.length) onApplyPeriods(pairs);
  };
  return (
    <Dialog open={!!data} onClose={onClose} maxWidth="lg" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1, py: 1, px: 1.5,
                         display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="span" sx={{ flex: 1 }}>
          {(data?.cand?.name || data?.cand?.cd || "")} — {tr(isEff ? "resEfficiency" : "mpAddCapaLabel")}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0, color: C.text3, mr: -0.5 }}>
          <CloseIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 1.5, pb: 1.5 }}>
        <Box sx={{ maxHeight: "55vh", overflow: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "16%" }}>{tr("orderCd")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "19%" }}>{tr("item")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "8%" }}>{tr("dueDate")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "6%" }}>{tr("status")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "6%" }}>{tr("orderQty")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "8%" }}>{tr("orderAmt")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "8%" }}>{tr("mpAddCapaLabel")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "5%" }}>{tr("resCnt")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "12%" }}>{tr(isEff ? "resEfficiency" : "resourceCapa")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "8%" }}>{tr("date")}</Box>
              <Box component="th" sx={{ ...headSx, textAlign: "center", width: "4%" }} title={tr("selectAll")}>
                <Box component="button" type="button" aria-pressed={allChecked} onClick={toggleAll}
                  sx={cbSx(allChecked)}>{allChecked ? "✓" : ""}</Box>
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {shown.map((o, i) => {
              const on = checked.has(o.order_cd);
              return (
                <Box component="tr" key={o.order_cd ?? i}
                  sx={{ bgcolor: on ? `${C.acc}0A` : "transparent" }}>  {/* 선택 행 틴트 — 자원 편집 표의 선택 행과 동일 */}
                  <Box component="td" title={o.order_cd || ""}
                    sx={{ ...cellSx, overflow: "hidden", textOverflow: "ellipsis" }}>{o.order_cd || "-"}</Box>
                  <Box component="td" title={o.item_name || o.item_cd}
                    sx={{ ...cellSx, overflow: "hidden", textOverflow: "ellipsis" }}>{o.item_name || o.item_cd}</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "center" }}>{o.due_date || "-"}</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "center" }}>
                    <Box component="span" sx={{ px: "5px", py: "1px", borderRadius: "999px", fontSize: FONT.MICRO,
                         fontWeight: 700, bgcolor: o.is_short ? `${C.g3}1A` : C.bg3, color: o.is_short ? C.g3 : C.text3 }}>
                      {o.is_short ? tr("shortageBadge") : tr("delayed")}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {o.order_qty ?? "-"}</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {o.order_amt != null ? Math.round(Number(o.order_amt)).toLocaleString() : "-"}</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: C.accT,
                       fontVariantNumeric: "tabular-nums" }}>+{o.additional_hours} {tr("mpUnitHour")}</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {o.res_cnt ?? "-"}</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "center" }}>
                    {isEff ? (
                      // 효율 variant: 현재%(read-only) → [필요%(편집)] — 주문 단위. prefill = 현재×(1+그 주문 add/base).
                      o.base_whpd == null ? "-" : (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4,
                                   justifyContent: "center", fontVariantNumeric: "tabular-nums" }}>
                          <Box component="span" sx={{ fontWeight: 700, color: C.text2 }}>{curEffPct(o)}%</Box>
                          <Box component="span" sx={{ color: C.text3 }}>→</Box>
                          <Box component="input" type="number"
                            value={editedEff[o.order_cd] != null ? editedEff[o.order_cd] : reqEffPct(o)}
                            onChange={(e) => setEditedEff((m) => ({ ...m, [o.order_cd]: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ width: "5em", textAlign: "right", fontFamily: "inherit", fontSize: FONT.MICRO,
                                  fontWeight: 700, color: C.accT, border: `1px solid ${C.border}`, borderRadius: "4px",
                                  px: "3px", py: "1px", bgcolor: C.bg2,
                                  "&:focus": { outline: "none", borderColor: C.acc } }} />
                          <Box component="span" sx={{ color: C.text3 }}>%</Box>
                        </Box>
                      )
                    ) : (
                      o.base_whpd == null ? "-" : (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, justifyContent: "center" }}>
                          {/* 고정폭 그리드로 → 위치 정렬(자릿수 달라도 화살표 세로 정렬). */}
                          <Box sx={{ display: "inline-grid", gridTemplateColumns: "4.5ch 1.2em 4.5ch",
                                     alignItems: "center", fontVariantNumeric: "tabular-nums" }}>
                            <Box component="span" sx={{ textAlign: "right", fontWeight: 700, color: C.text2 }}>{o.base_whpd}</Box>
                            <Box component="span" sx={{ textAlign: "center", color: C.text3 }}>→</Box>
                            {/* 제안값 = 현재 + ceil(자원당 필요 추가 능력). 24h 초과면 빨강 경고. */}
                            {(() => {
                              const proposed = Number(o.base_whpd) + Math.ceil(Number(o.additional_hours_per_res) || 0);
                              return (
                                <Box component="span" sx={{ textAlign: "left", fontWeight: 700, color: proposed > 24 ? C.g3 : C.accT }}>
                                  {proposed}
                                </Box>
                              );
                            })()}
                          </Box>
                          <Box component="span" sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{tr("mpUnitHour")}</Box>
                        </Box>
                      )
                    )}
                  </Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "center", fontWeight: 700, color: C.accT }}>
                    {snapPrevWorkingDay(o.short_date, wdpw, wsd) || "-"}</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "center" }}>
                    <Box component="button" type="button" aria-pressed={on} onClick={() => toggle(o.order_cd)}
                      sx={cbSx(on)}>{on ? "✓" : ""}</Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
        </Box>
        {/* 더 보기·안내는 스크롤 영역 밖에 배치 → 추가 조회해도 항상 보임 */}
        {visibleCount < orders.length && (
          <Box sx={{ textAlign: "center", mt: 0.8 }}>
            <Box component="button" type="button" onClick={() => setVisibleCount((v) => v + 10)}
              sx={{ border: `1px solid ${C.acc}60`, borderRadius: "6px", px: "12px", py: "3px",
                    bgcolor: `${C.acc}0D`, color: C.acc, fontFamily: "inherit", fontSize: FONT.MICRO,
                    fontWeight: 600, cursor: "pointer" }}>
              {tr("moreLabel")} ({orders.length - visibleCount})
            </Box>
          </Box>
        )}
        {bc?.other_count > 0 && visibleCount >= orders.length && (
          <Box sx={{ fontSize: FONT.MICRO, color: C.text3, fontStyle: "italic", mt: 0.6, px: "6px" }}>
            {tr("mpCapacityBackcalcMore", { count: bc.other_count, hours: bc.other_hours })}
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Typography sx={{ flex: 1, fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1 }}>
            {tr("mpCapacityBackcalcTotal", { hours: bc?.additional_hours_total ?? 0, count: bc?.order_count ?? 0 })}
          </Typography>
          <Box component="button" type="button" onClick={apply} disabled={checked.size === 0}
            sx={{ border: `1px solid ${checked.size ? C.acc : C.border}`, borderRadius: "6px", px: "10px", py: "3px",
                  bgcolor: checked.size ? C.accL : C.bg3, color: checked.size ? C.accT : C.text3,
                  fontFamily: "inherit", fontSize: FONT.MICRO, fontWeight: 700,
                  cursor: checked.size ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
            {tr("mpApplySelectedRowsLabel")} ({checked.size})
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function MpScenarioParamFields({ scenario, accent, disabled, highlights, onScenarioChange,
                                 view = "all", onStats }) {
  // 번역 함수는 tr 로 별칭 — 이 컴포넌트 전반에서 대상(target) 파라미터명으로 t 를 매우
  //   광범위하게 사용하므로(t.cd/t.name 등) useTranslation() 의 t 와 이름이 충돌한다(섀도잉 방지).
  const { t: tr } = useTranslation();
  const mpDispatch = useDispatch();   // 휴무 캘린더 해제 행의 '캘린더 확인' → 주기 캘린더 화면 이동용
  // 파라미터·스코프별 선택 대상 기억 — 작업장↔자원 등 적용 단위 탭 이동 시 선택 유지.
  //   key = `${param_key}:${scope_type}` → targets[]. 스코프 전환 시 이전 스코프 선택을 저장하고
  //   새 스코프의 이전 선택을 복원한다(없으면 빈 선택). override 자체는 오염시키지 않음(컴포넌트 상태).
  const [scopeMem, setScopeMem] = React.useState({});
  const memKey = (pk, sc) => `${pk}:${sc}`;

  // 값 자동 제안 보호 — '사용자가 직접 입력한' 파라미터만 대상 변경 시 제안값으로 덮어쓰지 않는다.
  //   시드(DB 프리셋) 값은 보호 대상 아님: 대상 선택 시 현재값 기반 제안으로 갱신되어야 한다
  //   (프리셋 work_hour_per_day=9 등이 그 자체로 교체 대상). onChange 에서만 touched 로 표시.
  const [valueTouched, setValueTouched] = React.useState({});

  // 수요 우선순위 '기타' 드래그&드롭 드롭 위치 하이라이트 — 적용 리스트 인덱스(숫자)·풀("pool")·null.
  //   DEMAND_PRIORITY 는 카드당 1개라 단일 state 로 충분.
  const [dndOver, setDndOver] = React.useState(null);

  // 주문 스코프(loc_item/mc_order) 대표 원인 필터 — {param_key: 선택 원인명 or ""}.
  //   그룹핑 대신 필터: 특정 원인 자원/자재가 막는 주문만 걸러 보고 '그 원인이 몇 개 주문의 원인'인지 확인.
  const [causeFilter, setCauseFilter] = React.useState({});

  // 생산 공정 상세(routing_op) — 공정(route)별 접기/펼치기. key=`${pk}:${route_cd}` → bool(명시 토글만 저장).
  //   미저장 공정은 심각도 상위 N개만 기본 펼침(그 외 접힘) — 목록이 길어질 수 있어 스크롤 최소화.
  const [routeOpen, setRouteOpen] = React.useState({});

  // 주기 캘린더 — 드롭다운에서 골라 펼쳐 둔 대상. {scope: 키 배열}(생산 공정 상세는 route_cd, 그 외는 cd).
  //   스위치를 전부 켜도(=해제 대상 0) 행이 사라지지 않도록 '무엇을 펼쳤나'를 targets 와 분리해 쥔다
  //   (targets 는 '해제할 대상'만 담는다).
  const [calOpen, setCalOpen] = React.useState({});
  // 자원 능력 수치 파라미터 — 드롭다운에서 고른 대상. {`pk:scope`: cd 배열}.
  //   미설정(undefined)이면 '문제 대상 전체'가 기본값 — 화면을 열자마자 문제 자원이 서 있어야 한다.
  //   빈 배열은 '전부 뺐다'는 명시적 상태라 기본값으로 되돌리지 않는다.
  //   챗 적용(mpApplyChatParam)이 같은 행을 읽도록 시나리오의 UI 전용 필드(__numPick)에 둔다(전송 제외).
  //   읽기는 shownResourceCds 가 한다.
  const setNumPick = (fn) => onScenarioChange?.(sc => ({ ...sc, __numPick: fn(sc.__numPick || {}) }));

  // 자원 그리드 — 일괄 선택 대상(param_key → cd 배열), 자원 행 펼침, 전체기간 모드, 일괄 입력 초안.
  const [selectedCds, setSelectedCds] = React.useState({});        // {pk: string[]}
  const [resRowOpen, setResRowOpen]   = React.useState({});        // {`pk:cd`: bool}

  const [bulkDraft, setBulkDraft]     = React.useState({});        // {pk: {val,start,end,whole}}
  // 자원 행 '상세'(능력 역산) Modal — {pk, cand, unit, bc}. null=닫힘.
  const [bcModal, setBcModal] = React.useState(null);

  // 파라미터별 접기/펼치기 — 헤더 클릭 = toggle.
  // **단일 펼침** — 편집 중인 파라미터 하나만. 조정 여부는 접힌 행의 "현행 A → B" 가 알린다.
  const [openKeys, setOpenKeys] = React.useState(() => new Set());
  // 조정만 보기에서 "파라미터 추가"로 끌어온 행 — 값이 없어 조정 판정에 안 걸리므로 고정한다.
  const [pinnedKeys, setPinnedKeys] = React.useState(() => new Set());
  // 편집을 blur 로 끝낸 직후의 클릭은 무시(같은 행을 눌러 끝냈는데 도로 열리는 것 방지).
  //   ⚠ 훅이므로 base 카드 early return 보다 앞에서 호출해야 한다.
  const editGuard = useEditEndGuard();
  // ── 조정 건수 보고 + 뷰 필터 (공용 계약) ─────────────────────────────────
  // overrides 는 아래에서 다시 잡지만, 훅은 early return(base 카드) 앞에 있어야 하므로
  //   여기서는 scenario prop 을 직접 훑는다.
  const _ovs  = Array.isArray(scenario?.overrides) ? scenario.overrides : [];
  // MP override 는 값이 value 하나가 아니라 대상별/기간별/공정별로 흩어져 있다 —
  //   pruneOverridesForEngine 이 "전송"으로 치는 것과 같은 조건을 조정으로 본다.
  const _isAdjusted = (pk) => {
    const o = _ovs.find(x => x.param_key === pk);
    if (!o) return false;
    if (hasEffectiveValue(null, o.value)) return true;
    if (Array.isArray(o.targets) && o.targets.some(tg => hasEffectiveValue(null, tg?.value)
        || (Array.isArray(tg?.periods) && tg.periods.some(pd => hasEffectiveValue(null, pd?.value))))) return true;
    if (Array.isArray(o.periods) && o.periods.some(pd => hasEffectiveValue(null, pd?.value))) return true;
    if (o.route_policies && Object.keys(o.route_policies).length > 0) return true;
    if (Array.isArray(o.priority_items) && o.priority_items.length > 0) return true;
    return false;
  };

  // ── 행 그룹(row_group) — 카탈로그가 정본. primary 만 행을 그리고 나머지는 그 행의 입력칸이 된다. ──
  const _rowGroups = {};
  (scenario?.param_defs || []).forEach(d => {
    if (!d?.row_group) return;
    const g = (_rowGroups[d.row_group] ||= { primary: null, members: [] });
    g.members.push(d.param_key);
    if (d.row_role === "primary") g.primary = d.param_key;
  });
  const _groupOf = (pk) => Object.values(_rowGroups).find(g => g.members.includes(pk)) || null;
  // 그룹 대표 여부 / 그룹에 속하지만 대표가 아닌 키(=행을 갖지 않음) 여부.
  const isRowPrimary   = (pk) => (_groupOf(pk)?.primary ?? null) === pk;
  const isRowCompanion = (pk) => { const g = _groupOf(pk); return !!g && g.primary !== pk; };
  // 그 키가 대표라면 그룹 전체(카탈로그 정의 순), 아니면 자기 자신만.
  const rowMembers = (pk) => (isRowPrimary(pk) ? _groupOf(pk).members : [pk]);
  // 자원 능력=무한이면 효율·가동시간 등이 화면에서 빠지므로 총계에서도 뺀다(표시와 숫자 일치).
  const _rcInfiniteStat = _ovs.some(o => o.param_key === "RC"
    && o.scope_type === "all" && String(o.value) === "infinite");
  //   행을 갖지 않는 그룹 멤버(companion)도 뺀다 — 대표 행 1개를 세야 화면의 행 수와 맞는다.
  const _statDefs = (scenario?.param_defs || []).filter(d =>
    !(_rcInfiniteStat && d.param_key !== "RC" && mpGroupOf(d) === "resource")
    && !isRowCompanion(d.param_key));
  //   대표 행은 그룹 멤버 중 하나라도 조정되면 '조정됨'.
  const _changedN = _statDefs.filter(d => rowMembers(d.param_key).some(k => _isAdjusted(k))).length;
  const _totalN   = _statDefs.length;
  React.useEffect(() => {
    if (scenario?.role === "base") return;
    onStats?.({ changed: _changedN, total: _totalN });
    // onStats 는 부모가 매 렌더 새로 만드는 함수라 deps 에서 제외(넣으면 무한 루프).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_changedN, _totalN, scenario?.role]);

  const isParamVisible = (def) => {
    // 합성 행 — 그룹 멤버 중 하나만 조정해도 대표 행이 떠야 편집기에 남는다.
    const keys = rowMembers(def.param_key);
    return view === "all" || keys.some(k => _isAdjusted(k) || pinnedKeys.has(k));
  };

  React.useEffect(() => {
    setOpenKeys(new Set());
    setPinnedKeys(new Set());
    setValueTouched({});   // 시나리오 전환 시 사용자 편집 표시 초기화
    setCauseFilter({});    // 시나리오 전환 시 원인 필터 초기화(후보·원인 목록이 달라짐)
    setSelectedCds({}); setResRowOpen({}); setBulkDraft({});
    setCalOpen({});        // 시나리오마다 해제 대상이 다르다 — 앞 시나리오에서 펼친 행을 물려주지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.role, scenario?.scenario_cd]);

  // 주문/자재 단위(비전역) MC·RC 는 '무한'만 유효(유한 자재제약·유한 능력은 마스터 기준과 동일해
  // 의미 없음)해서 값 셀렉트가 옵션을 infinite 로만 노출한다(renderValueInput 호출부 참조).
  //   그런데 AI 추천 등으로 시드된 override.value 가 finite 로 남아 있으면, 컨트롤드 셀렉트는
  //   옵션에 없는 값을 만나 첫 옵션('무한')을 표시하지만 실제 저장값(value)은 finite 그대로다
  //   (표시-저장 desync) → 엔진에 유한으로 전달된다. non-all MC·RC 값을 infinite 로 정규화해
  //   표시·저장·엔진을 일치시킨다(idempotent: 한 번 정규화되면 needsFix=false 로 재실행 없음).
  React.useEffect(() => {
    const list = Array.isArray(scenario?.overrides) ? scenario.overrides : [];
    const isPartialInf = (o) => o
      && (o.param_key === "MC" || o.param_key === "RC")
      && o.scope_type && o.scope_type !== "all"
      && String(o.value) !== "infinite";
    if (!list.some(isPartialInf)) return;
    onScenarioChange?.((sc) => ({
      ...sc,
      overrides: (sc.overrides || []).map((o) =>
        isPartialInf(o) ? { ...o, value: "infinite" } : o),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.overrides]);

  const inputSx = {
    width: "100%", p: "4px 8px", border: `0.5px solid ${accent}60`,
    borderRadius: 1, fontSize: FONT.BODY, fontFamily: "inherit", color: C.text1,
    bgcolor: C.bg2, outline: "none", boxSizing: "border-box",
    "&:focus": { borderColor: accent },
    "&:disabled": { bgcolor: C.bg3, color: C.text3, cursor: "not-allowed", borderColor: C.border },
    // select 본체의 선택 강조색(accent/굵기)이 option 에 상속돼 목록 전체가 물드는 것을 차단.
    "& option": { color: C.text1, fontWeight: 400 },
  };

  // 수율: 저장은 소수(0.97), 표시/입력은 %(97). yieldDecToPct 는 mpScenarioParamUtil 공유.
  const yieldPctToDec = (pct) => (pct == null || pct === "") ? "" : Number((Number(pct) / 100).toFixed(4));

  // AI set_param 변경 강조 (공통 키 규칙: `${role}:${paramKey}`)
  const hotWrap = (paramKey, node) => {
    const hlTs = highlights?.[`${scenario.role}:${paramKey}`];
    if (hlTs == null) return <Box key={paramKey}>{node}</Box>;
    return (
      <Box key={paramKey}
        sx={{ position: "relative", borderRadius: PARAM_ROW_RADIUS, p: "8px 9px", mx: "-9px",
              bgcolor: `${C.acc}10`, border: `1px solid ${C.acc}55`,
              boxShadow: `0 0 0 3px ${C.acc}12` }}>
        <Box sx={{ position: "absolute", top: -7, right: 7, px: "5px", height: 14,
                   borderRadius: PARAM_ROW_RADIUS, bgcolor: C.acc, color: "#fff", fontSize: FONT.MICRO,
                   fontWeight: 700, display: "flex", alignItems: "center" }}>{tr("aiChangedBadge")}</Box>
        {node}
      </Box>
    );
  };

  // 값 입력 1개 렌더 (select / number / multiplier)
  const renderValueInput = (def, value, onChange, lock = false) => {
    const dis = disabled || lock;   // lock: 비-전체 스코프인데 변경 대상 후보가 없음 → 값 잠금
    if (def.type === "select") {
      // 계획 방식(PS)은 공급계획 설정과 동일 소스(planStrategies)에서 선택치를 파생하고 라벨은 언어팩을 따른다.
      const opts = def.param_key === "PS"
        ? PS_STRATEGY_VALUES.map(v => ({ value: v, label: psStrategyLabel(v) }))
        : (def.options || []);
      return (
        <Box component="select" value={value ?? opts?.[0]?.value ?? ""} disabled={dis}
          onChange={e => onChange(e.target.value)}
          sx={{ ...inputSx, ...PARAM_SELECT_W, cursor: dis ? "not-allowed" : "pointer" }}>
          {opts.map(o => <option key={o.value} value={o.value}>{tr(o.label_key || o.label)}</option>)}
        </Box>
      );
    }
    const isYield = def.param_key === "yield_rate";   // 수율: 상한 100% 클램프용(표시 %와 별개)
    // 수율·자원 효율: 저장은 소수/배수 그대로, 표시·입력만 %(수율 0.97→97%, 효율 1.2→120%).
    //   %표시 여부는 카탈로그 meta display="percent" 플래그로 판정(단일 진실원).
    const isPct = def.display === "percent";
    const numMax = isYield ? 100 : MP_NUM_MAX[def.param_key];   // 작업 시간/일 24 / 주당 작업일 수 7 등 상한(효율은 무제한)
    const numMin = MP_NUM_MIN[def.param_key] ?? 0;              // 하한(주당 작업일 수=1, 0 금지)
    // 회색 힌트는 '값'이 아니라 '무슨 값을 넣는 칸인지'만 안내한다. 현재값은 헤더('현재 …')에 이미 표시되므로
    //   입력칸에 숫자(예 '100'/'97')를 두면 '이미 설정됨'으로 오인 → 미편집 → 빈값 pruning → plan_config_obj 미반영.
    const numPh = def.param_key === "yield_rate" ? tr("mpYieldPctPlaceholder")
      : (isPct ? "%" : def.type === "multiplier" ? tr("multiplierUnitLabel") : "");
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
        <DraftNumberInput step={isPct ? "1" : def.type === "multiplier" ? "0.1" : undefined}
          min={String(numMin)} max={numMax}
          value={isPct ? yieldDecToPct(value) : (value ?? "")} disabled={dis}
          placeholder={numPh}
          clamp={(v) => {
            if (v === "") return v;
            if (numMax != null && Number(v) > numMax) return String(numMax);   // 상한 초과 시 클램프
            if (Number(v) < numMin) return String(numMin);                     // 하한 미만 시 클램프
            return v;
          }}
          onCommit={(v) => onChange(isPct ? yieldPctToDec(v) : v)}
          sx={{ ...inputSx, width: PARAM_NUM_INPUT_W, flex: "0 0 auto" }} />
        {(def.unit || def.type === "multiplier" || isPct) && (
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3, flex: "0 0 auto" }}>
            {isPct ? "%" : def.type === "multiplier" ? tr("multiplierUnitLabel") : (def.unit_key ? tr(def.unit_key) : def.unit)}
          </Typography>
        )}
      </Box>
    );
  };

  // 대상별 현재값 표시 포맷 (select=옵션 라벨 / number·multiplier=값+단위)
  const fmtCur = (def, v) => {
    if (v == null) return tr("noInfoLabel");
    // 휴무 캘린더 해제는 값이 없고 "clear" 마커뿐이다 — 마커를 그대로 노출하지 않는다.
    //   문구는 같은 동작을 하는 RP 캘린더 해제와 맞춘다.
    if (def.type === "holiday_calendar") return tr("rpCalendarOffShort");
    if (def.type === "select") return def.param_key === "PS"
      ? psStrategyLabel(v)
      : (() => { const o = (def.options || []).find(x => x.value === v); return o ? tr(o.label_key || o.label) : String(v); })();
    // 수율·자원 효율: 저장은 소수/배수, 표시만 %(효율 1.2→120%). display="percent" 카탈로그 플래그로 판정.
    if (def.display === "percent") return `${yieldDecToPct(v)}%`;
    if (def.type === "multiplier") return `${v}${tr("multiplierUnitLabel")}`;
    if (def.type === "number") return `${v}${def.unit_key ? tr(def.unit_key) : (def.unit || "")}`;
    return String(v);
  };

  // 기준선 현재값 표시 — baseline_kind 접두어(현행/평균) + 현재값(fmtCur 로 옵션 라벨·단위 다국어).
  //   파라미터명은 왼쪽 라벨 열이 이미 말하므로 재출력하지 않는다. desc 키 없으면 레거시 current_display 폴백.
  const fmtBaselineCurrent = (def) => {
    const cv = def.current_value;
    if (!def.baseline_desc_key || cv == null || cv === "") return def.current_display || null;
    return baselineCurrentText(def, fmtCur(def, cv), tr);
  };

  // base 카드 요약은 `라벨 · 값` 형태라 '현행' 접두어까지는 군더더기 — 다만 '평균'은 값의 성격이라 남긴다.
  const fmtBaseCardCurrent = (def) => {
    const cv = def.current_value;
    if (!def.baseline_desc_key || cv == null || cv === "") return def.current_display || null;
    const v = fmtCur(def, cv);
    if (def.baseline_kind === "avg") return tr("paramAvgValueLabel", { value: v });
    if (def.baseline_kind === "current") return v;
    return baselineCurrentText(def, v, tr);   // custom·구버전 응답
  };

  // ── 로트 합성 행 — 배수(lot_size)·최소·최대를 한 행에서 함께 다룬다 ────────────
  const defOfKey = (pk) => (scenario?.param_defs || []).find(d => d.param_key === pk) || null;
  // "100EA (50~350)" 요약. 대표값만 있으면 "100EA", 범위만 있으면 "(50~350)". 모두 없으면 null.
  //   작업 시간 그룹은 범위가 아니라 가산이라 "8시간 + 2" 로 쓴다(엔진이 정규에 더한다).
  const rowGroupText = (pk, valueOf) => {
    const members = rowMembers(pk);
    const roleKey = (role) => members.find(k => defOfKey(k)?.row_role === role) || null;
    const base = valueOf(pk);
    const ovKey = roleKey("over");
    if (ovKey) {
      const anchor = defOfKey(pk);
      const head = (base == null || base === "" || !anchor) ? "" : fmtCur(anchor, base);
      const ovv = valueOf(ovKey);
      const tail = (ovv == null || ovv === "") ? "" : `+ ${ovv}`;
      return [head, tail].filter(Boolean).join(" ") || null;
    }
    const mnKey = roleKey("min"), mxKey = roleKey("max");
    const mn = mnKey ? valueOf(mnKey) : null;
    const mx = mxKey ? valueOf(mxKey) : null;
    const anchorDef = defOfKey(pk);
    const head = (base == null || base === "" || !anchorDef) ? "" : fmtCur(anchorDef, base);
    const hasRange = (mn != null && mn !== "") || (mx != null && mx !== "");
    if (!hasRange) return head || null;
    const rng = `(${mn == null || mn === "" ? "-" : mn}~${mx == null || mx === "" ? "-" : mx})`;
    return head ? `${head} ${rng}` : rng;
  };

  // 대상 드롭다운 후보 — scope_targets 에서만 나오고 편집값과는 무관하다. 시나리오 state 가 바뀔 때마다
  //   다시 만들면(값 입력·행 삭제마다) 참조가 달라져 MUI Autocomplete 가 통째로 다시 계산한다.
  //   updateAt 은 {...sc} 스프레드라 scope_targets 참조가 유지되므로 이 memo 는 편집 중 계속 살아 있다.
  //   ⚠ 훅이므로 param_defs 미보유 early return 보다 앞에 있어야 한다.
  const stgt = scenario?.scope_targets;
  const routeOptsOf = React.useCallback((list) => {
    const byRoute = new Map();
    (list || []).forEach(c => {
      const rk = c.route_cd || String(c.cd || "").split("@")[0];
      if (!byRoute.has(rk)) {
        byRoute.set(rk, { route_name: c.route_name || rk, item_name: c.item_name,
                          loc_name: c.loc_name, items: [] });
      }
      byRoute.get(rk).items.push(c);
    });
    const opts = [...byRoute.keys()].map(rk => ({
      key: rk, label: byRoute.get(rk).route_name || rk,
      group: byRoute.get(rk).loc_name || undefined,
      icon: problemDot((byRoute.get(rk).items || []).some(c => Number(c.problem_count) > 0)) }));
    return { byRoute, opts };
  }, []);
  const jctRoutes = React.useMemo(
    () => routeOptsOf((stgt || {}).routing_op_all), [stgt, routeOptsOf]);
  const calRouteOpts = React.useMemo(
    () => routeOptsOf((stgt || {}).routing_op_cal), [stgt, routeOptsOf]);
  const calFlatOpts = React.useMemo(() => {
    const build = (list) => (list || []).map(t => ({
      key: t.cd, label: t.name || t.cd, group: t.loc_name || undefined,
      icon: problemDot(Number(t.problem_count) > 0 || Number(t.problem_resource_count) > 0) }));
    return { resource: build((stgt || {}).resource_cal),
             workcenter: build((stgt || {}).workcenter_cal) };
  }, [stgt]);
  // 자원 능력 수치 파라미터의 '대상 추가' 드롭다운 — 활성 전체 자원/작업장.
  //   문제 대상만으로는 "이 설비도 같이 늘려보자"를 표현할 수 없다. 붉은 dot 이 문제 보유 표시.
  const numFlatOpts = React.useMemo(() => {
    const build = (list) => (list || []).map(t => ({
      key: t.cd, label: t.name || t.cd, group: t.loc_name || undefined,
      icon: problemDot(Number(t.problem_count) > 0 || Number(t.problem_resource_count) > 0) }));
    return { resource: build((stgt || {}).resource_all),
             workcenter: build((stgt || {}).workcenter_all) };
  }, [stgt]);

  const defs = scenario?.param_defs;
  if (!defs?.length) {
    // param_defs 미보유 — z_ai_scenario.master_data_changes 가 MP_PARAM_CATALOG 키와
    // 불일치(미정렬)하면 빈 배열. 빈 카드로 보이지 않도록 안내.
    return (
      <Box sx={{ p: "10px 11px", borderRadius: 1, bgcolor: C.bg3, border: `0.5px dashed ${C.border}` }}>
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
          {tr("mpNoEditableParamsDesc")}
        </Typography>
      </Box>
    );
  }

  // base — 무변경 기준선 (현행 유지). 도메인 그룹 순서로 묶어 표시(alt 편집기와 일관).
  if (scenario.role === "base") {
    return (
      <Box sx={{ p: "10px 10px", borderRadius: 1, bgcolor: C.bg3, border: `0.5px dashed ${C.border}` }}>
        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text2, mb: 0.6 }}>
          {tr("dpBaseNoChangeLabel")}
        </Typography>
        <Stack spacing={0.7}>
          {MP_GROUP_ORDER.map(g => {
            const rows = defs.filter(d => d.current_display && mpGroupOf(d) === g.key);
            if (rows.length === 0) return null;
            return (
              <Box key={g.key}>
                <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, mb: 0.2 }}>
                  {g.icon} {tr(g.label)}
                </Typography>
                {rows.map(d => (
                  <Typography key={d.param_key} sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                    {tr(d.label_key || d.label)} · {fmtBaseCardCurrent(d) ?? d.current_display}
                  </Typography>
                ))}
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  }

  // ── alt — per-param 스코프 편집 ───────────────────────────────────────────
  const overrides    = Array.isArray(scenario.overrides) ? scenario.overrides : [];
  const scopeTargets = scenario.scope_targets || {};   // fetchTemplates 가 주입
  const ovOf = (pk) => overrides.find(o => o.param_key === pk) || null;

  // 파라미터 override 엔트리 upsert / 제거
  const upsertOv = (pk, patch) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    const i = list.findIndex(o => o.param_key === pk);
    if (i >= 0) list[i] = { ...list[i], ...patch };
    else list.push({ param_key: pk, scope_type: "all", targets: [],
                     value: null, reason: null, ...patch });
    return { ...sc, overrides: list };
  });
  // 여러 키를 한 번의 갱신으로 처리한다 — onScenarioChange 는 호출 시점의 scenarios 로 계산하므로
  //   같은 이벤트에서 연속 호출하면 뒤 호출이 앞 호출을 통째로 덮어쓴다(변경이 사라진다).
  const upsertOvMany = (patches) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    Object.entries(patches).forEach(([pk, patch]) => {
      const i = list.findIndex(o => o.param_key === pk);
      if (i >= 0) list[i] = { ...list[i], ...patch };
      else list.push({ param_key: pk, scope_type: "all", targets: [],
                       value: null, reason: null, ...patch });
    });
    return { ...sc, overrides: list };
  });
  const removeOvMany = (pks) => onScenarioChange?.(sc => ({
    ...sc, overrides: (sc.overrides || []).filter(o => !pks.includes(o.param_key)),
  }));
  // 대상(target) 복수 선택 토글 (scope=workcenter/resource/route)
  // 로트 합성 행은 대상 칩도 3키가 공유한다 — 토글 하나로 세 키의 targets 를 한 번에 맞춘다.
  //   (lot 3종은 suggestValue 대상이 아니라 값 자동 제안은 필요 없다.)
  const toggleTargetSynced = (pk, cand) => {
    if (!isRowPrimary(pk)) { toggleTarget(pk, cand); return; }
    const members = rowMembers(pk);
    onScenarioChange?.(sc => {
      const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
      const ai = list.findIndex(o => o.param_key === pk);
      if (ai < 0) return sc;
      const cur = Array.isArray(list[ai].targets) ? list[ai].targets : [];
      const has = cur.some(t => t.cd === cand.cd);
      const next = has ? cur.filter(t => t.cd !== cand.cd)
                       : [...cur, { cd: cand.cd, name: cand.name,
                                    ...(cand.loc_cd != null ? { loc_cd: cand.loc_cd } : {}) }];
      members.forEach(k => {
        const i = list.findIndex(o => o.param_key === k);
        if (i >= 0) list[i] = { ...list[i], targets: next };
      });
      return { ...sc, overrides: list };
    });
  };
  const toggleTarget = (pk, cand) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    const i = list.findIndex(o => o.param_key === pk);
    if (i < 0) return sc;
    const cur  = Array.isArray(list[i].targets) ? list[i].targets : [];
    const has  = cur.some(t => t.cd === cand.cd);
    const next = has ? cur.filter(t => t.cd !== cand.cd)
                     : [...cur, { cd: cand.cd, name: cand.name,
                                  ...(cand.loc_cd != null ? { loc_cd: cand.loc_cd } : {}),
                                  ...(cand.item_cd != null ? { item_cd: cand.item_cd } : {}) }];
    const patch = { targets: next };
    const def = defs?.find(d => d.param_key === pk);
    if (def && def.type === "holiday_calendar") {
      patch.value = "clear";   // 휴무 캘린더 해제는 값이 없고 "clear" 마커 고정
    } else if (def && def.type !== "select" && !valueTouched[pk]) {
      // 미편집 수치 파라미터: 선택 대상의 현재값 기준 초기값 자동 제안.
      //   저장된 targets 는 stripped(.current 없음) → 후보 목록(scopeTargets)에서 풀 객체 복원.
      const scope = list[i].scope_type;
      const full = (scopeTargets[scope] || []).filter(c => next.some(n => n.cd === c.cd));
      // 산출 불가(대상 없음/현재값 부재) 시 기존 값 유지 — fallback = 현재 value.
      patch.value = suggestValue(def, scope, full,
        { numMax: MP_NUM_MAX[pk], fallback: list[i].value ?? defaultValue(def) });
    }
    list[i] = { ...list[i], ...patch };
    return { ...sc, overrides: list };
  });

  // ── 수요 우선순위 빌더 — ov.priority_items(코드 배열). 날짜=단일 선택(맨 앞), 기타=선택 순서. ──
  const setPriorityItems = (def, next) => upsertOv(def.param_key, { priority_items: next });

  // 파라미터 변경 시작(현행→변경) 기본값
  //   holiday_calendar 는 값 개념이 없고 "clear" 마커로 둔다(전송 prune 통과 + 적용 판정용).
  const defaultValue = (def) =>
    def.type === "select" ? (def.options?.[0]?.value ?? "")
      : def.type === "multiplier" ? "1.2"
      : def.type === "holiday_calendar" ? "clear" : "";

  // ── 기간별 능력(periods) 편집 — override.periods = [{start_dt, end_dt, value}] ─────────
  //   기간 행이 1개 이상이면 백엔드(param_mapper)가 전체 value 대신 기간별 값을 적용한다.
  const addPeriod = (def, ov) => upsertOv(def.param_key, {
    periods: [...(ov?.periods || []),
              { start_dt: "", end_dt: "",
                value: (ov?.value != null && ov.value !== "") ? ov.value : defaultValue(def) }],
  });
  const updatePeriod = (pk, idx, patch) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    const i = list.findIndex(o => o.param_key === pk);
    if (i < 0) return sc;
    const periods = Array.isArray(list[i].periods) ? [...list[i].periods] : [];
    if (idx < 0 || idx >= periods.length) return sc;
    periods[idx] = { ...periods[idx], ...patch };
    list[i] = { ...list[i], periods };
    return { ...sc, overrides: list };
  });
  const removePeriod = (pk, idx) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    const i = list.findIndex(o => o.param_key === pk);
    if (i < 0) return sc;
    const periods = (list[i].periods || []).filter((_, k) => k !== idx);
    list[i] = { ...list[i], periods };
    return { ...sc, overrides: list };
  });

  // ── 생산 공정 상세 — 자원별 개별 값(targets[].value) 설정. (우선순위/로트 사이즈 공용) ──────
  //   값 입력 = 해당 자원을 targets 에 추가/갱신, 빈값 = 제외(일괄 아님).
  // 로트 합성 행(생산 공정 상세) — override 가 없으면 만들면서 대상별 값까지 한 번에 쓴다.
  //   upsertOv 후 setTargetValue 로 나눠 부르면 두 번째 호출이 첫 번째를 덮어써 값이 들어가지 않는다.
  const setLotTargetValue = (pk, cand, val, scopeType) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    let i = list.findIndex(o => o.param_key === pk);
    if (i < 0) {
      list.push({ param_key: pk, scope_type: scopeType, targets: [], value: null, reason: null });
      i = list.length - 1;
    }
    const tgts = Array.isArray(list[i].targets) ? [...list[i].targets] : [];
    const idx = tgts.findIndex(t => t.cd === cand.cd);
    if (val === "" || val == null) {
      if (idx >= 0) tgts.splice(idx, 1);
    } else {
      const base = { cd: cand.cd, name: cand.name,
        ...(cand.route_cd != null ? { route_cd: cand.route_cd } : {}),
        ...(cand.loc_cd != null ? { loc_cd: cand.loc_cd } : {}), value: val };
      if (idx >= 0) tgts[idx] = { ...tgts[idx], ...base };
      else tgts.push(base);
    }
    list[i] = { ...list[i], targets: tgts };
    return { ...sc, overrides: list };
  });
  const setTargetValue = (pk, cand, val) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    const i = list.findIndex(o => o.param_key === pk);
    if (i < 0) return sc;
    const tgts = seedTargetsFromValue(list[i],
      Array.isArray(list[i].targets) ? [...list[i].targets] : []);
    const idx = tgts.findIndex(t => t.cd === cand.cd);
    if (val === "" || val == null) {
      if (idx >= 0) tgts.splice(idx, 1);
    } else {
      const base = { cd: cand.cd, name: cand.name,
        ...(cand.route_cd != null ? { route_cd: cand.route_cd } : {}),
        ...(cand.loc_cd != null ? { loc_cd: cand.loc_cd } : {}), value: val };
      if (idx >= 0) tgts[idx] = { ...tgts[idx], ...base };
      else tgts.push(base);
    }
    list[i] = { ...list[i], targets: tgts };
    return { ...sc, overrides: list };
  });
  // ── 자원 그리드 — 대상(cd)별 기간(periods) 편집. targets[i].periods = [{start_dt,end_dt,value}] ──
  //   대상이 없으면 후보 메타(name/loc_cd)로 생성. setTargetValue(전체기간 단일값)와 공존.
  const mutateTargetOfCand = (pk, cand, mut) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    const i = list.findIndex(o => o.param_key === pk);
    if (i < 0) return sc;
    const tgts = Array.isArray(list[i].targets) ? [...list[i].targets] : [];
    let ti = tgts.findIndex(t => t.cd === cand.cd);
    if (ti < 0) {
      tgts.push({ cd: cand.cd, name: cand.name,
        ...(cand.loc_cd != null ? { loc_cd: cand.loc_cd } : {}) });
      ti = tgts.length - 1;
    }
    const next = mut({ ...tgts[ti] });
    // value·periods 모두 비면 대상 제거(빈 대상 방지).
    const empty = (next.value == null || next.value === "")
      && !(Array.isArray(next.periods) && next.periods.length > 0);
    if (empty) tgts.splice(ti, 1); else tgts[ti] = next;
    list[i] = { ...list[i], targets: tgts };
    return { ...sc, overrides: list };
  });
  // 합성 행(자원 그리드) — 여러 param_key 의 같은 대상(cd)을 한 번의 갱신으로 바꾼다.
  //   mutateTargetOfCand 를 멤버 수만큼 부르면 부모 updateAt 이 stale 클로저를 읽어 마지막 1건만 남는다.
  //   openParam 은 대표 키만 시드하므로 동반 키(초과 작업 시간)는 override 자체가 없다 →
  //   scopeType 을 받은 호출에 한해 만들어 준다(기존 '없으면 무시' 호출부는 그대로).
  //   anchorPeriods: 합성 행의 구간 정본(호출부가 멤버 전원에서 산출). 구간 index 는 멤버 공유가
  //   전제인데 AI 추천은 지목한 키에만 구간을 실어 오므로, 갱신 전에 짧은 멤버를 정본에 맞춰 채운다.
  const mutateTargetOfCandMany = (pks, cand, mutOf, scopeType, anchorPeriods) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    const anchor = Array.isArray(anchorPeriods) ? anchorPeriods : [];
    pks.forEach(pk => {
      let i = list.findIndex(o => o.param_key === pk);
      if (i < 0) {
        if (!scopeType) return;
        list.push({ param_key: pk, scope_type: scopeType, targets: [], value: null, reason: null });
        i = list.length - 1;
      }
      list[i] = { ...list[i], targets: seedTargetsFromValue(list[i], list[i].targets || []) };
      const tgts = Array.isArray(list[i].targets) ? [...list[i].targets] : [];
      let ti = tgts.findIndex(t => t.cd === cand.cd);
      if (ti < 0) {
        tgts.push({ cd: cand.cd, name: cand.name,
          ...(cand.loc_cd != null ? { loc_cd: cand.loc_cd } : {}) });
        ti = tgts.length - 1;
      }
      const cur = { ...tgts[ti] };
      const ps = Array.isArray(cur.periods) ? cur.periods : [];
      if (anchor.length > ps.length) {   // 날짜는 정본을 따르고 값은 그 멤버의 것을 유지
        cur.periods = anchor.map((a, j) => ({
          start_dt: a.start_dt || "", end_dt: a.end_dt || "", value: ps[j]?.value ?? "" }));
      }
      const next = mutOf(cur, pk);
      const empty = (next.value == null || next.value === "")
        && !(Array.isArray(next.periods) && next.periods.length > 0);
      if (empty) tgts.splice(ti, 1); else tgts[ti] = next;
      list[i] = { ...list[i], targets: tgts };
    });
    return { ...sc, overrides: list };
  });
  const addTargetPeriod = (def, cand, seed) => mutateTargetOfCand(def.param_key, cand, (t) => ({
    ...t, periods: [...(t.periods || []),
      { start_dt: seed?.start_dt || "", end_dt: seed?.end_dt || "",
        value: seed?.value ?? defaultValue(def) }],
  }));
  const updateTargetPeriod = (pk, cand, idx, patch) => mutateTargetOfCand(pk, cand, (t) => {
    const periods = Array.isArray(t.periods) ? [...t.periods] : [];
    if (idx < 0 || idx >= periods.length) return t;
    periods[idx] = { ...periods[idx], ...patch };
    return { ...t, periods };
  });
  const removeTargetPeriod = (pk, cand, idx) => mutateTargetOfCand(pk, cand, (t) => ({
    ...t, periods: (t.periods || []).filter((_, k) => k !== idx),
  }));
  // 구간이 전부 비어있으면(값·날짜 없음) 그 구간 제거 — 행 접을 때 유령 구간 정리에 사용.
  const pruneBlankPeriods = (pk, cand) => mutateTargetOfCand(pk, cand, (t) => ({
    ...t, periods: (t.periods || []).filter(p =>
      (p?.value != null && p.value !== "") || p?.start_dt || p?.end_dt),
  }));

  // 공정(route)별 대체 자원 정책 변경 — override.route_policies[route_cd] = fast/priority.
  const setRoutePolicy = (pk, routeCd, val) => onScenarioChange?.(sc => {
    const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
    const i = list.findIndex(o => o.param_key === pk);
    if (i < 0) return sc;
    const rp = { ...(list[i].route_policies || {}) };
    if (val === "" || val == null) delete rp[routeCd];
    else rp[routeCd] = val;
    list[i] = { ...list[i], route_policies: rp };
    return { ...sc, overrides: list };
  });
  // 대체 자원 정책 코드(fast/priority/masterData) → 표시 라벨.
  const altPolicyLabel = (cd) => {
    const opt = (defs?.find(d => d.param_key === "alt_res_policy")?.options || [])
      .find(o => o.value === cd);
    return opt ? tr(opt.label_key || opt.label) : (cd || "—");
  };

  // 헤더 클릭 펼침/접힘. 펼칠 때 override 없으면 생성(편집 가능), 접어도 유지(빈값은 제출 시 드롭).
  const openParam = (def) => {
    // 합성 행에서 동반 키가 이미 값을 들고 있으면 대표를 시드하지 않는다 — 전체 스코프 빈 껍데기가
    //   생기면 그 행의 적용 단위가 '전체'로 바뀌어 동반 키의 값이 화면에서 사라진다(실측).
    const memberHasOv = rowMembers(def.param_key).some(k => k !== def.param_key && ovOf(k));
    if (!ovOf(def.param_key) && !memberHasOv) {
      const scopes = def.scopes?.length ? def.scopes : ["all"];
      // 주기 캘린더 전체는 전 자원 파급 — 펼치기만으로 적용되지 않게 빈값으로 시드(체크박스가 스위치).
      const seed = (def.type === "holiday_calendar" && scopes[0] === "all") ? "" : defaultValue(def);
      upsertOv(def.param_key, { scope_type: scopes[0], value: seed });
    }
    setOpenKeys(new Set([def.param_key]));      // 단일 펼침 — 다른 파라미터는 접는다
  };
  const closeParam = (pk) => setOpenKeys(s => { const n = new Set(s); n.delete(pk); return n; });
  const endEditMp = (pk) => { editGuard.markEnd(); closeParam(pk); };
  const toggleOpenMp = (def) => {
    if (editGuard.justEnded()) return;
    return openKeys.has(def.param_key) ? closeParam(def.param_key) : openParam(def);
  };
  // 현행 유지로 되돌리기 — override 제거(미전송) + 접기.
  const revertParam = (pk) => {
    // 합성 행은 되돌리기 하나로 그룹 전체를 되돌린다.
    const keys = rowMembers(pk);
    removeOvMany(keys);
    closeParam(pk);
    setValueTouched(m => { const n = { ...m }; keys.forEach(k => delete n[k]); return n; });  // 재오픈 시 다시 제안
    // 고정(pin)도 함께 푼다 — 안 그러면 값만 비고 행이 남아 목록에서 치울 방법이 없다.
    setPinnedKeys(s => { const n = new Set(s); keys.forEach(k => n.delete(k)); return n; });
  };

  // 자원 능력(RC) 전체=무한 이면 자원 그룹 파라미터를 숨긴다 (능력 무제한 → 효율·가동시간·가동일수·캘린더 무의미).
  // 거점-품목 단위 무한은 전역이 아니므로 대상 아님(scope_type='all' 일 때만).
  const rcInfinite = overrides.some(o => o.param_key === "RC"
    && o.scope_type === "all" && String(o.value) === "infinite");
  // override 는 지우지 않는다 — RC 를 유한으로 되돌리면 입력값 그대로 복귀. 전송·미리보기는 mpScenarioParamUtil 가 제외.
  const voidedByRcInfinite = (def) => rcInfinite && def.param_key !== "RC" && mpGroupOf(def) === "resource";

  // ── 자원(resource) 스코프 — 자원별 행 그리드(기간별 우선). 칩+단일값 대신 사용. ──────────────
  //   행별 개별값/기간별 + 복수 선택 일괄. 전체 기간 단일값은 행별 체크박스 옵션.
  // ── 작업 전환 시간 조정(JOB_CHANGE_TIME) ────────────────────────────────
  //   JCT 반영 여부는 라우팅 마스터(z_routing.job_change_time)가 정하고, 이 파라미터는
  //   그 값을 어떻게 조정할지만 고른다. 적용 단위가 곧 파라미터 값이라 행의 값 dropdown
  //   (All / Production Routing)이 그것을 고르고, 여기서는 고른 단위에 따른 입력만 그린다 —
  //   all=전체 일괄 배율(jct_multiplier) / routing_op=공정을 골라 설비별 절대값(targets).
  //   조정값은 별도 param_key 가 아니라 이 override 의 부가 필드로 실린다
  //   (jct_multiplier / targets) — 수요 우선순위(priority_items) 와 같은 방식.
  //   절대값의 단위는 그 라우팅 행의 time_uom 이다 — 분으로 정규화하지 않는다.
  const renderJctAdjust = (def, ov) => {
    const pk = def.param_key;
    const { byRoute, opts: routeOpts } = jctRoutes;   // memo — 편집 중 참조가 유지된다
    // 적용 단위는 파라미터 값이다. 손대지 않았으면 카탈로그 default(=전체)를 따라간다.
    const mode = ov?.value ?? def.default;
    const targets = ov?.targets || [];
    const routeOf = (t) => t.route_cd || String(t.cd || "").split("@")[0];

    const picked = [];
    targets.forEach(t => { const rk = routeOf(t); if (rk && !picked.includes(rk)) picked.push(rk); });
    // 선택된 공정 카드의 부제 — 공정명만으로는 어느 품목·거점인지 알 수 없다.
    const routeSub = (g) => [g?.item_name, g?.loc_name].filter(Boolean).join(" · ");

    // 드롭다운은 공정(route) 단위로 고르고, 고른 공정의 설비 전체를 targets 로 편다.
    //   다중 선택이라 한 번의 변경에 추가·삭제가 섞이므로, 남는 공정의 입력값은 그대로 살린다.
    //   거점은 그룹 헤더가 이미 보여주므로 라벨에 괄호로 덧붙이지 않는다(routeOpts 는 memo).
    const setRoutes = (next) => {
      const keys = next.map(o => o.key);
      const kept = targets.filter(t => keys.includes(routeOf(t)));
      const added = keys.filter(rk => !picked.includes(rk))
        .flatMap(rk => (byRoute.get(rk)?.items || []).map(c => ({ cd: c.cd, name: c.name,
          route_cd: c.route_cd, loc_cd: c.loc_cd, value: "" })));
      upsertOv(pk, { targets: [...kept, ...added] });
    };
    const removeRoute = (rk) => upsertOv(pk, { targets: targets.filter(t => routeOf(t) !== rk) });
    const setVal = (cd, v) => upsertOv(pk, {
      targets: targets.map(t => (t.cd === cd ? { ...t, value: v } : t)),
    });

    const opEditor = byRoute.size === 0 ? (
      <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
        {tr("mpNoMatchingTargetDesc", { scope: tr("routing") })}.
      </Typography>
    ) : (
      <Stack spacing={0.8}>
        <AiTargetMultiSelect disabled={disabled} options={routeOpts}
          value={picked.map(rk => routeOpts.find(o => o.key === rk)).filter(Boolean)}
          onChange={setRoutes}
          placeholder={tr("select")}
          noOptionsText={tr("mpNoMatchingTargetDesc", { scope: tr("routing") })}
          sx={{ alignSelf: "flex-start", minWidth: 260, maxWidth: 380 }} />
        {picked.map(rk => {
          const g = byRoute.get(rk);
          return (
            <Box key={rk} sx={{ border: "1px solid " + C.border, borderRadius: 1, p: 0.8 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.5 }}>
                <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1 }}>
                  {g?.route_name || rk}
                </Typography>
                {routeSub(g) && (
                  <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{routeSub(g)}</Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <AiDeleteButton disabled={disabled} onClick={() => removeRoute(rk)} />
              </Box>
              <Stack spacing={0.4}>
                {targets.filter(t => routeOf(t) === rk).map(t => {
                  const cand = (g?.items || []).find(c => c.cd === t.cd);
                  const cur = cand?.current?.job_change_time;
                  const unit = tr(JCT_UNIT_KEY[cand?.time_uom] || "minutes");
                  return (
                    <Box key={t.cd} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      {/* 능력부족을 안고 있는 설비는 건수로 짚어준다(dot 은 드롭다운에만 — 여기선 중복). */}
                      <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "center",
                                 gap: 0.6, overflow: "hidden" }}>
                        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2, overflow: "hidden",
                                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {cand?.name || t.name || t.cd}
                        </Typography>
                        {Number(cand?.problem_count) > 0 && (
                          <Typography sx={{ flex: "0 0 auto", fontSize: FONT.MICRO, fontWeight: 700, color: C.g3 }}>
                            {tr("mpProblemOrderCountLabel", { count: Number(cand.problem_count) })}
                          </Typography>
                        )}
                      </Box>
                      {cur != null && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, flex: "0 0 auto" }}>
                          {tr("mpCurrentPrefixLabel", { value: String(cur) + unit })}
                        </Typography>
                      )}
                      {/* 빈칸 = 미변경. 현재값은 왼쪽 '현재 …' 라벨이 이미 말하고 있어 placeholder 에
                          숫자를 두지 않는다 — 두면 '이미 설정됨'으로 오인해 편집하지 않게 된다.
                          스피너도 감춘다: 빈칸에서 ^ 를 누르면 현재값이 아니라 1 부터 시작해 혼란스럽다. */}
                      <DraftNumberInput disabled={disabled} min="0"
                        step={JCT_STEP[cand?.time_uom] ?? 1}
                        value={t.value ?? ""}
                        clamp={(v) => (v !== "" && Number(v) < 0 ? "0" : v)}
                        onCommit={(v) => setVal(t.cd, v)}
                        sx={{ ...inputSx, width: 66, p: "3px 6px", textAlign: "center", flex: "0 0 auto",
                              fontVariantNumeric: "tabular-nums",
                              MozAppearance: "textfield",
                              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                                WebkitAppearance: "none", margin: 0 },
                              ...(hasEffectiveValue(null, t.value)
                                ? { borderColor: accent, bgcolor: accent + "0D", fontWeight: 700, color: C.accT }
                                : {}) }} />
                      <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, flex: "0 0 auto" }}>{unit}</Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );

    return (
      <Box sx={{ mt: 0.8, pt: 0.8, borderTop: "1px dashed " + C.border }}>
        {/* 적용 단위 = 파라미터 값. dropdown 대신 토글로 고른다(수요 우선순위의 날짜 선택과 같은 방식). */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.6 }}>
          <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3, flex: "0 0 auto" }}>
            {tr("rpApplyScopeLabel")}
          </Typography>
          <AiSegmentedControl accent={accent} disabled={disabled} value={mode}
            options={[{ value: "all", label: tr("all") },
                      { value: "routing_op", label: tr("routing") }]}
            onChange={(m) => upsertOv(pk, { value: m })} />
        </Box>
        {mode === "all" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2 }}>
              {tr("mpJobChangeMultiplier")}
            </Typography>
            {/* 기본 1 = 변경 없음. 저장하지 않고 표시만 하므로, 손대지 않으면 엔진에 아무것도 내려가지 않는다. */}
            {renderValueInput({ ...def, type: "multiplier", options: null },
              ov?.jct_multiplier ?? "1", (v) => upsertOv(pk, { jct_multiplier: v }))}
          </Box>
        ) : opEditor}
      </Box>
    );
  };

  // 자원 능력 수치 파라미터의 대상별 편집 그리드 — 자원·워크센터 두 스코프가 같은 화면을 쓴다.
  //   (칩+단일값이 아니라 대상마다 한 행. 주기 캘린더 목록과 같은 읽기 방식.)
  const renderResourceGrid = (def, ov, scope) => {
    const pk = def.param_key;
    // 행에 세울 대상 — 기본은 문제 대상 전체, 드롭다운으로 활성 전 대상에서 더하거나 뺄 수 있다.
    //   후보(전체)에 없는 cd 는 버린다(마스터가 비활성화된 대상이 유령 행으로 남지 않게).
    //   *_all 미보유(구버전 백엔드)면 종전대로 문제 대상만 — 화면이 비지 않게 한다.
    const allCands = (scopeTargets[`${scope}_all`]?.length
      ? scopeTargets[`${scope}_all`] : (scopeTargets[scope] || []));
    const pickKey = `${pk}:${scope}`;
    // 표시 행 판정은 챗 적용과 같은 함수를 쓴다 — 둘이 갈리면 챗 값이 안 보이는 행에 들어간다.
    const cands = shownResourceCds(scenario, pk, scope);
    const pickedCds = cands.map(c => c.cd);
    const periodCapable = MP_PERIOD_CAPABLE.has(pk);   // 주당 작업일 수는 전체기간만(체크박스 없음)
    const isPct = def.display === "percent";   // 카탈로그 meta 플래그(단일 진실원) — 자원 그리드는 효율만 해당
    const numMax = MP_NUM_MAX[pk];
    const unit = isPct ? "%" : (def.unit_key ? tr(def.unit_key) : (def.unit || ""));
    const inputW = RES_NUM_W;
    const valW = inputW + 4 + RES_RESET_W;       // 값 열 = 입력 + 되돌리기 슬롯
    const targets = ov?.targets || [];
    const tgtOf = (cd) => targets.find(t => t.cd === cd) || null;
    // 합성 행(row_group) — 정규/초과 작업 시간은 한 행 안의 두 입력칸이다. 그룹이 아니면 멤버 1개.
    //   행 모드·펼침·선택 상태의 키는 전부 대표(pk) 기준 — 멤버별로 갈리면 한쪽 편집이 화면에서 사라진다.
    const members = rowMembers(pk);
    const isMultiRow = members.length > 1;
    const defOfMember = (k) => (k === pk ? def : (defOfKey(k) || def));
    const ovOfMember = (k) => (k === pk ? ov : ovOf(k));
    const tgtOfMember = (k, cd) => (ovOfMember(k)?.targets || []).find(t => t.cd === cd) || null;
    // 워크센터 스코프의 일괄값(override 단일값) — 대상별 값이 없으면 엔진은 이 값을 그 대상에 건다.
    //   행 그리드가 이걸 무시하면 헤더는 '9시간'인데 행은 전부 현재값 그대로라 제안이 사라져 보인다.
    //   단 일괄값은 override 의 targets 에만 걸린다 — 후보로만 떠 있는 행에까지 칠하면 안 바뀌는
    //   대상을 바뀐 것처럼 보여 준다(문제 대상 전체가 행으로 서기 때문에 실제로 벌어진다).
    const ovBulk = (o) => { const v = o?.value; return (v == null || v === "") ? undefined : v; };
    const rawOfMember = (k, cd) => {
      const t = tgtOfMember(k, cd);
      if (t?.value != null && t.value !== "") return t.value;
      return t ? ovBulk(ovOfMember(k)) : undefined;
    };
    // 구간 목록의 정본 — 대표가 아니라 '구간을 가장 많이 가진 멤버'다. AI 추천은 지목한 키에만 구간을
    //   싣기 때문에, 대표만 보면 초과 작업 시간에 실려 온 구간이 화면에 아예 뜨지 않는다.
    const rowPeriods = (cd) => (isMultiRow
      ? members.map(k => tgtOfMember(k, cd)?.periods || [])
               .reduce((a, b) => (b.length > a.length ? b : a), [])
      : (tgtOf(cd)?.periods || []));
    // 멤버 전원을 1회 갱신으로 — 구간 추가·삭제·날짜는 전원 공유, 값만 해당 멤버.
    const mutMembers = (cand, mutOf, keys) =>
      mutateTargetOfCandMany(keys || members, cand, mutOf, scope, rowPeriods(cand.cd));
    // 능력 역산의 적용 대상 키 — 작업 시간 행은 '초과(잔업)' 멤버, 그 외는 자기 자신.
    const bcTargetKey = members.find(m => defOfMember(m)?.row_role === "over")
      || (pk === "work_hour_per_day" ? pk : null);
    const sel = selectedCds[pk] || [];
    const selSet = new Set(sel);
    const setSel = (arr) => setSelectedCds(m => ({ ...m, [pk]: arr }));
    const toggleSel = (cd) => setSel(selSet.has(cd) ? sel.filter(c => c !== cd) : [...sel, cd]);

    // 후보 자체가 없을 때만 안내로 끝낸다 — 사용자가 전부 빼서 비었을 때는 드롭다운을 남겨야
    //   다시 담을 수 있다(안내만 띄우면 되돌릴 수단이 사라진다).
    if (allCands.length === 0) {
      return (
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
          {tr("mpNoMatchingTargetDesc", { scope: tr(MP_SCOPE_LABEL[scope]) })} ({tr("mpCapacityShortageOccurHint")}).
        </Typography>
      );
    }
    const numOpts = numFlatOpts[scope]?.length
      ? numFlatOpts[scope] : allCands.map(t => ({ key: t.cd, label: t.name || t.cd,
                                                  group: t.loc_name || undefined,
                                                  icon: problemDot(Number(t.problem_count) > 0
                                                    || Number(t.problem_resource_count) > 0) }));
    // 행 제외 — 행의 [x] 와 드롭다운 체크 해제가 같은 동작을 하도록 한 곳에 모은다.
    //   화면에서만 빼면 그 대상의 입력값이 overrides 에 남아 보이지 않는 채로 엔진에 간다.
    //   nextKeys: 드롭다운 확정 목록(추가·삭제가 섞인 변경). 없으면 현재 목록에서 뺀다.
    const removeCds = (cds, nextKeys) => {
      const drop = new Set(cds);
      if (!drop.size) return;
      const keepCds = (nextKeys || pickedCds).filter(cd => !drop.has(cd));
      setSel(sel.filter(cd => !drop.has(cd)));
      // 마지막 조정 대상을 빼면 조정만 보기에서 행이 통째로 사라진다 — 편집 중인 행은 고정한다.
      setPinnedKeys(s => (s.has(pk) ? s : new Set(s).add(pk)));
      setResRowOpen(m => { const n = { ...m }; cds.forEach(cd => delete n[`${pk}:${cd}`]); return n; });
      // 표시 행과 멤버 전원의 대상을 1회 갱신으로 — 나눠 부르면 부모 updateAt 이 stale 클로저를 읽어 앞 갱신이 사라진다.
      //   남은 표시 행에 일괄값을 확정하지 않는다 — 대상이 아니던 자원까지 값이 바뀐다.
      onScenarioChange?.(sc => ({
        ...sc,
        __numPick: { ...(sc.__numPick || {}), [pickKey]: keepCds },
        overrides: (Array.isArray(sc.overrides) ? sc.overrides : [])
          .map(o => (members.includes(o.param_key) ? dropTargetCds(o, cds) : o)),
      }));
    };
    const resPicker = (
      <AiTargetMultiSelect disabled={disabled} options={numOpts}
        value={pickedCds.map(cd => numOpts.find(o => o.key === cd)).filter(Boolean)}
        onChange={(next) => {
          const keys = next.map(o => o.key);
          const removed = pickedCds.filter(cd => !keys.includes(cd));
          // 체크 해제도 행의 [x] 와 같은 뜻 — 입력값까지 함께 정리한다.
          if (removed.length) removeCds(removed, keys);
          else setNumPick(s => ({ ...s, [pickKey]: keys }));
        }}
        placeholder={tr("select")}
        noOptionsText={tr("mpNoMatchingTargetDesc", { scope: tr(MP_SCOPE_LABEL[scope]) })}
        sx={{ alignSelf: "flex-start", minWidth: 260, maxWidth: 380 }} />
    );


    // 작업장(WC) 그룹핑 — 후보에 wc 정보 없으면 단일 그룹(라벨 생략).
    const groups = []; const gmap = {};
    cands.forEach(t => {
      const gk = t.wc_cd || t.loc_cd || "_";
      if (!gmap[gk]) { gmap[gk] = { key: gk, label: t.wc_name || t.wc_cd || t.loc_name || null, items: [] }; groups.push(gmap[gk]); }
      gmap[gk].items.push(t);
    });
    const showGroupLabel = groups.length > 1 || !!groups[0]?.label;

    // 상한(24h 등)은 기본 소프트(초과 허용·빨강 경고, 하한 0 클램프). 단 하드 대상(주당 작업일 수)은 min~max 하드 클램프.
    const numMin = MP_NUM_MIN[pk] ?? 0;
    const hardRange = MP_NUM_HARD.has(pk);
    const numInput = (val, onNum, w, extraSx, ph, forceOver) => {
      const over = forceOver
        || (!hardRange && numMax != null && val != null && val !== "" && Number(val) > numMax);
      return (
        <DraftNumberInput disabled={disabled}
          step={isPct ? "1" : undefined} min={String(numMin)} max={hardRange && numMax != null ? String(numMax) : undefined}
          value={isPct ? yieldDecToPct(val) : (val ?? "")} placeholder={ph ?? (isPct ? "100" : "0")}
          clamp={(v) => {
            if (v === "") return v;
            if (hardRange) {   // 범위 밖 입력 차단(1~7). 스피너·직접입력 모두 클램프.
              let n = Number(v);
              if (!Number.isFinite(n)) n = numMin;
              return String(Math.min(numMax != null ? numMax : n, Math.max(numMin, n)));
            }
            return Number(v) < 0 ? "0" : v;
          }}
          onCommit={onNum}
          sx={{ ...inputSx, width: w, height: RES_CTRL_H, p: "0 6px",
                textAlign: "left", fontVariantNumeric: "tabular-nums",
                "&:focus": { borderColor: accent, boxShadow: `0 0 0 2px ${accent}26` },
                ...(extraSx || {}),
                ...(over ? { borderColor: C.g3, color: C.g3, fontWeight: 700,
                             "&:focus": { borderColor: C.g3, boxShadow: `0 0 0 2px ${C.g3}26` } } : {}) }} />
      );
    };
    const changedSx = { borderColor: accent, bgcolor: `${accent}0D`, fontWeight: 700, color: C.accT };
    // 그 구간의 정규 기준 — 역산 구간에 백엔드가 실어 준다(마스터 기간별 능력이 덮은 날은 평상시와
    //   다르다). 어느 멤버의 구간에 실려 있어도 같은 값이라 먼저 찾은 것을 쓴다.
    const periodBaseAt = (cand, idx) => {
      for (const k of members) {
        const b = tgtOfMember(k, cand.cd)?.periods?.[idx]?.base;
        if (b != null && b !== "") return b;
      }
      return undefined;
    };
    // 그 구간에서 칸을 비워 두면 실제로 적용되는 값 — 정규는 그 구간의 기준, 없으면 자원 현재값.
    const periodCurOf = (k, cand, idx) => {
      if (k === pk) {
        const b = periodBaseAt(cand, idx);
        if (b != null) return b;
      }
      const c = cand.current?.[k];
      return (c == null || c === "") ? undefined : c;
    };
    // ── 합성 행(정규+초과)의 하루 천장 검증 ────────────────────────────────
    //   엔진은 24h 를 넘어도 막지 않고 그대로 능력으로 쓴다(가산). 24h 露光機에 +1h 같은 입력이
    //   조용히 통과하면 없는 능력으로 계획이 서므로, 합계를 화면에서 계산해 빨강으로 알린다.
    //   미입력 칸은 현재값(placeholder 로 보이는 값)이 그대로 쓰이므로 합계에도 현재값을 넣는다.
    const effNum = (k, cand, raw) => {
      const v = (raw !== undefined) ? raw : rawOfMember(k, cand.cd);
      const use = (v != null && v !== "") ? v : cand.current?.[k];
      const n = Number(use);
      return Number.isFinite(n) ? n : 0;
    };
    const sumOver24 = (cand, valueOf) =>
      members.reduce((a, k) => a + effNum(k, cand, valueOf ? valueOf(k) : undefined), 0);
    // 구간 합계 — 빈 칸은 그 자원의 현재값이 그대로 쓰이므로 합계에도 넣는다(전체기간 행과 같은 규칙).
    //   아무 칸도 안 채운 구간만 표시를 비운다 — 손대지 않은 구간에 천장을 말할 이유가 없다.
    const periodSum = (cand, idx) => {
      if (!isMultiRow) return null;
      const vals = members.map(k => tgtOfMember(k, cand.cd)?.periods?.[idx]?.value);
      if (vals.every(v => v == null || v === "")) return null;
      return members.reduce((a, k, i) => {
        const v = vals[i];
        const use = (v != null && v !== "") ? v : periodCurOf(k, cand, idx);
        const n = Number(use);
        return a + (Number.isFinite(n) ? n : 0);
      }, 0);
    };

    // 값 셀 — '현재 N' 라벨 없이 현재값을 프리필하고, 손댄 칸만 강조 + 되돌리기를 단다.
    const toStore = (v) => ((v === "" || v == null) ? "" : (isPct ? yieldPctToDec(v) : v));
    // 툴팁은 화면 표준(MUI Tooltip · top · arrow)에 맞춘다 — 네이티브 title 은 지연·모양이 다르다.
    const tip = (label, node) => (label
      ? <Tooltip title={label} placement="top" arrow>{node}</Tooltip> : node);
    const setMemberValue = (k, cand, v) => (isMultiRow
      ? mutMembers(cand, (t) => ({ ...t, value: toStore(v) }), [k])
      : setTargetValue(k, cand, toStore(v)));
    const renderValueCell = (k, cand, bad) => {
      const raw = rawOfMember(k, cand.cd);
      const cur = cand.current?.[k];
      // 미편집 칸은 현재값을 띄운다 — 빈 칸이 '0 적용'으로 읽히지 않게. 손대지 않으면 저장되지 않는다.
      const prefilled = (raw == null || raw === "") && cur != null && cur !== "";
      const changed = raw != null && raw !== "" && Number(raw) !== Number(cur);
      // 되돌릴 지점은 '변경 전' = 마스터 현재값이다(되돌리기가 이 행의 대상을 지우므로 일괄값도 풀린다).
      const revLabel = (cur == null || cur === "") ? tr("revertChangesLabel")
        : tr("mpRevertToValueLabel", { value: fmtCur(defOfMember(k), cur) });
      return (
        <Box key={k} sx={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-start" }}>
          {numInput(prefilled ? cur : raw, (v) => setMemberValue(k, cand, v), inputW,
                    changed ? changedSx : null, "", bad)}
          {/* 되돌리기 자리는 항상 비워 둔다 — 없으면 값 열이 행마다 좌우로 흔들린다. */}
          <Box sx={{ width: RES_RESET_W, flex: "0 0 auto", display: "flex", justifyContent: "center" }}>
            {changed && !disabled && tip(revLabel, (
              <Box component="button" type="button" aria-label={revLabel}
                onClick={() => setMemberValue(k, cand, "")}
                sx={{ width: RES_RESET_W, height: RES_CTRL_H, p: 0, border: "none", borderRadius: "4px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: "transparent", color: accent, cursor: "pointer",
                      "&:hover": { bgcolor: `${accent}14` } }}>
                <RestartAltIcon sx={{ fontSize: 13 }} />
              </Box>
            ))}
          </Box>
        </Box>
      );
    };

    // 기간별 구간 — 합성 행은 멤버 전원이 같은 index/날짜의 구간을 공유한다(엔진이 버킷 능력을 한 번에 치환).
    const periodPatchAll = (cand, idx, patch) => mutMembers(cand, (t) => {
      const ps = Array.isArray(t.periods) ? [...t.periods] : [];
      if (idx < 0 || idx >= ps.length) return t;
      ps[idx] = { ...ps[idx], ...patch };
      return { ...t, periods: ps };
    });
    const periodValueOne = (k, cand, idx, val) => mutMembers(cand, (t) => {
      const ps = Array.isArray(t.periods) ? [...t.periods] : [];
      if (idx < 0 || idx >= ps.length) return t;
      ps[idx] = { ...ps[idx], value: val };
      return { ...t, periods: ps };
    }, [k]);
    const periodAddAll = (cand) => mutMembers(cand, (t, k) => ({
      ...t, periods: [...(t.periods || []),
        { start_dt: "", end_dt: "", value: defaultValue(defOfMember(k)) }],
    }));
    const periodRemoveAll = (cand, idx) => mutMembers(cand, (t) => ({
      ...t, periods: (t.periods || []).filter((_, j) => j !== idx),
    }));
    // 행 열고닫기 — 펼치면 빈 구간 1줄을 깔고, 닫을 때 값·날짜가 없는 구간만 지운다.
    const toggleRow = (cand) => {
      const rk = `${pk}:${cand.cd}`;
      const next = !(resRowOpen[rk] ?? false);
      setResRowOpen(m => ({ ...m, [rk]: next }));
      if (next) {
        if (periodCapable && !rowPeriods(cand.cd).length) {
          if (isMultiRow) periodAddAll(cand); else addTargetPeriod(def, cand);
        }
        return;
      }
      if (isMultiRow) {
        // 구간 index 는 멤버 전원이 공유하므로 멤버별로 거르면 index 정렬이 깨진다.
        const keep = new Set();
        members.forEach(m => (tgtOfMember(m, cand.cd)?.periods || []).forEach((p, i) => {
          if ((p?.value != null && p.value !== "") || p?.start_dt || p?.end_dt) keep.add(i);
        }));
        mutMembers(cand, (t) => ({ ...t, periods: (t.periods || []).filter((_, i) => keep.has(i)) }));
      } else {
        pruneBlankPeriods(pk, cand);
      }
    };

    // ── 표 본체 ────────────────────────────────────────────────────────────
    // 값 열은 고정폭, 식별 열(이름·건수)만 1fr — 챗 패널로 카드가 좁아지면 이름이 2줄로 접히고
    //   그래도 모자라면 가로 스크롤로 넘긴다. 식별 열은 sticky 라 스크롤해도 남는다.
    const renderTable = (tier) => {
      // 건수 머리글은 언어마다 길이가 달라 고정폭이면 en/vi 에서 잘린다 — 최장 단어를 실측해 잡는다.
      const cntFont = `700 ${getFontSizePx("caption")}px ${getFontFamily(i18n.language)}`;
      const widest = (s) => String(s).split(/\s+/)
        .reduce((a, w) => Math.max(a, measureText(w, cntFont)), 0);
      const cntW = Math.min(RES_CNT_MAX, Math.max(RES_CNT_MIN,
        Math.ceil(Math.max(widest(tr("short")), widest(tr("delayed")))) + 6));
      // 긴 머리글은 이름 폭을 깎지 않고 식별 열 자체를 넓힌다.
      const idMin = RES_ID_MIN[tier] + (cntW - RES_CNT_MIN) * 2;
      const dateW = tier === "narrow" ? 60 : 88;
      const dateFmt = tier === "narrow" ? "MM-dd" : "yyyy-MM-dd";
      // 값 열 머리글 — CJK 는 keep-all 이라 접히지 않으므로 열을 머리글 폭까지 넓힌다(ja '超過稼働時間').
      const headOf = (k) => {
        const h = isMultiRow ? null : MP_RES_HEAD_LABEL[k];
        return { h, text: isMultiRow ? tr(mpRowRoleLabel(defOfMember(k))) : (h ? tr(h.key) : unit) };
      };
      const colW = (k) => {
        const { h, text } = headOf(k);
        const w = Math.max(widest(text), (h?.unit && unit) ? measureText(`(${unit})`, cntFont) : 0);
        return Math.max(valW, Math.ceil(w) + RES_HEAD_PL + 2);
      };
      // 합계 값은 입력칸 숫자와 같은 크기 — 커진 글자가 넘치지 않게 표시될 합계를 실측해 열을 잡는다.
      const sumFont = `700 ${getFontSizePx("body")}px ${getFontFamily(i18n.language)}`;
      const sumW = isMultiRow ? Math.max(RES_SUM_W, 4 + 2 + Math.ceil(Math.max(
        measureText(`88.8${unit}`, sumFont),
        ...cands.map(c => measureText(`${sumOver24(c)}${unit}`, sumFont))))) : 0;
      const fixed = [...members.map(colW),
                     ...(isMultiRow ? [sumW] : []),
                     ...(periodCapable ? [RES_BTN_W] : []), RES_DEL_W];
      const cols = [`minmax(${idMin}px, 1fr)`, ...fixed.map(w => `${w}px`)];
      const minTotal = idMin + fixed.reduce((a, b) => a + b, 0) + (cols.length - 1) * 4 + 6;
      const rowSx = { display: "grid", gridTemplateColumns: cols.join(" "),
                      columnGap: "4px", alignItems: "center", pr: "6px" };
      // sticky 셀은 불투명해야 한다 — 선택 틴트는 흰 바탕 위에 배경 이미지로 덧칠한다.
      const stickySx = (bg, tint) => ({ position: "sticky", left: 0, zIndex: 1, bgcolor: bg,
        ...(tint ? { backgroundImage: `linear-gradient(${tint}, ${tint})` } : {}) });
      const sumSx = (bad) => ({ textAlign: "left", pl: "4px", fontSize: FONT.BODY, fontWeight: 700,
                                whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
                                color: bad ? C.g3 : C.text2 });
      const cntCell = (n, isShort) => (
        <Box sx={{ width: cntW, flex: "0 0 auto", display: "flex", justifyContent: "center" }}>
          {n > 0
            ? <Box component="span" sx={countChipSx(isShort)}>{n}</Box>
            : <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>-</Typography>}
        </Box>
      );
      // 행 끝 아이콘 버튼 공통 — 테두리 없이 아이콘만, 반응은 배경색으로. 포커스 링은 안쪽에 그린다.
      const resIconBtnSx = {
        width: RES_CTRL_H, height: RES_CTRL_H, p: 0, border: "none", borderRadius: "6px",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: RES_ICON_TR,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1,
        "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: "-2px" },
      };
      // 삭제 버튼은 화면 안에서 만든다 — 공용 버튼은 aria-label 을 받지 않는다.
      const delBtn = (onClick, label) => tip(label, (
        <Box component="button" type="button" disabled={disabled} onClick={onClick}
          aria-label={label}
          sx={{ ...resIconBtnSx, bgcolor: "transparent", color: C.text3,
                "&:hover": disabled ? {} : { bgcolor: C.g3l, color: C.g3 } }}>
          <CloseIcon sx={{ fontSize: RES_ICON_PX }} />
        </Box>
      ));
      // 전체 너비 주석 줄 — 경고·구간 추가처럼 열에 담기지 않는 것만 여기에 편다.
      const noteRow = (key, bg, node) => (
        <Box key={key} sx={{ bgcolor: bg, px: "8px", pb: "4px" }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap",
                     position: "sticky", left: 0, pl: "19px", bgcolor: bg }}>
            {node}
          </Box>
        </Box>
      );
      const warnSx = { fontSize: FONT.CAPTION, fontWeight: 700, color: C.g3 };

      const head = (
        <Box sx={{ ...rowSx, alignItems: "end", bgcolor: C.bg, py: `${RES_ROW_PY}px`,
                   borderBottom: `1px solid ${C.border}` }}>
          <Box sx={{ ...stickySx(C.bg), display: "flex", alignItems: "end", gap: "4px",
                     pl: "8px", minWidth: 0 }}>
            <Box sx={{ flex: "1 1 auto", minWidth: 0 }} />
            <Typography sx={{ width: cntW, flex: "0 0 auto", textAlign: "center", lineHeight: 1.2,
                              fontSize: FONT.CAPTION, fontWeight: 700, color: RES_SHORT_TEXT }}>{tr("short")}</Typography>
            <Typography sx={{ width: cntW, flex: "0 0 auto", textAlign: "center", lineHeight: 1.2,
                              fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2 }}>{tr("delayed")}</Typography>
          </Box>
          {/* keep-all 은 CJK 를 글자 단위로 쪼개지 않는다 — 라틴은 공백에서 그대로 접힌다.
              단위는 별도 inline-block 이라 'Efficiency(%)' 도 줄바꿈 자리를 갖는다. */}
          {members.map(k => {
            const { h, text } = headOf(k);
            return (
              <Typography key={k} sx={{ textAlign: "left", pl: `${RES_HEAD_PL}px`, lineHeight: 1.2,
                                        wordBreak: "keep-all",
                                        fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2 }}>
                {text}
                {h?.unit && unit && (
                  <Box component="span" sx={{ display: "inline-block" }}>({unit})</Box>
                )}
              </Typography>
            );
          })}
          {/* 다른 머리글과 같은 행간 — 하단 정렬이라 행간이 다르면 글자 높이가 어긋난다. */}
          {isMultiRow && <Typography sx={{ ...sumSx(false), fontSize: FONT.CAPTION, lineHeight: 1.2 }}>{tr("sum")}</Typography>}
          {periodCapable && <Box />}
          <Box />
        </Box>
      );

      const wcHead = (g) => (
        <Box key={`wc-${g.key}`} sx={{ bgcolor: C.bg, borderTop: `1px solid ${C.border}`, py: "4px" }}>
          <Box component="span" sx={{ display: "inline-block", position: "sticky", left: 0,
                 pl: "8px", bgcolor: C.bg, fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3 }}>
            {g.label}
          </Box>
        </Box>
      );

      const periodRows = (cand) => {
        const periods = rowPeriods(cand.cd);
        const overlapIdx = findOverlappingPeriodIndices(periods);
        const setDates = isMultiRow ? periodPatchAll
          : (c, i, patch) => updateTargetPeriod(pk, c, i, patch);
        // 달력 아이콘의 edgeEnd 음수 마진은 이 좁은 칸에서 테두리 밖으로 삐져나온다 — 0 으로 되돌린다.
        const dpSx = { width: dateW, flex: "0 0 auto",
          "& .MuiInputBase-root": { fontSize: FONT.CAPTION, pr: "4px", height: RES_CTRL_H },
          "& .MuiInputBase-input": { p: "0 2px 0 6px", minWidth: 0 },
          "& .MuiInputAdornment-root": { flexShrink: 0, ml: 0 },
          "& .MuiIconButton-root": { p: "2px", mr: 0 },
          "& .MuiSvgIcon-root": { fontSize: 14 } };
        const capOver = periods.some((_, i) => {
          const s = periodSum(cand, i);
          return s != null && s > MP_DAY_HOUR_CAP;
        });
        // 연도를 줄인 표기에서도 어느 해인지 읽을 수 있어야 한다.
        const dpTitle = (s) => ((tier === "narrow" && s) ? { inputProps: { title: s } } : null);
        const rows = periods.map((p, idx) => {
          const startD = periodStrToDate(p.start_dt), endD = periodStrToDate(p.end_dt);
          const bad = !!(startD && endD && startD > endD) || overlapIdx.has(idx);
          const pSum = periodSum(cand, idx);
          const capBad = pSum != null && pSum > MP_DAY_HOUR_CAP;
          return (
            <Box key={`p-${cand.cd}-${idx}`}
              sx={{ ...rowSx, minHeight: RES_ROW_H, bgcolor: C.bg3,
                    borderTop: `1px dashed ${(bad || capBad) ? C.g3 : C.border}` }}>
              <Box sx={{ ...stickySx(C.bg3), alignSelf: "stretch", display: "flex", alignItems: "center",
                         gap: "4px", pl: "27px", py: `${RES_ROW_PY}px`, minWidth: 0 }}>
                <DatePicker format={dateFmt} value={startD} disabled={disabled}
                  onChange={(dt) => setDates(cand, idx, { start_dt: periodDateToStr(dt) })}
                  slotProps={{ textField: { size: "small", placeholder: dateFmt, error: bad, sx: dpSx,
                                            ...dpTitle(p.start_dt) } }} />
                <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>~</Typography>
                <DatePicker format={dateFmt} value={endD} disabled={disabled} minDate={startD || undefined}
                  onChange={(dt) => setDates(cand, idx, { end_dt: periodDateToStr(dt) })}
                  slotProps={{ textField: { size: "small", placeholder: dateFmt, error: bad, sx: dpSx,
                                            ...dpTitle(p.end_dt) } }} />
              </Box>
              {members.map(k => {
                const pv = tgtOfMember(k, cand.cd)?.periods?.[idx]?.value;
                const filled = pv != null && pv !== "";
                // 미편집 칸은 그 구간의 기준값(마스터 기간별 능력이 덮으면 그 값)을 띄운다.
                const pCur = periodCurOf(k, cand, idx);
                return (
                  <Box key={k} sx={{ display: "flex", alignItems: "center", gap: "4px",
                                     justifyContent: "flex-start" }}>
                    {numInput(filled ? pv : (pCur ?? ""),
                              (v) => (isMultiRow
                                ? periodValueOne(k, cand, idx, v)
                                : updateTargetPeriod(pk, cand, idx, { value: toStore(v) })),
                              inputW, filled ? changedSx : null, "", capBad)}
                    <Box sx={{ width: RES_RESET_W, flex: "0 0 auto" }} />
                  </Box>
                );
              })}
              {isMultiRow && <Typography sx={sumSx(capBad)}>{pSum == null ? "" : `${pSum}${unit}`}</Typography>}
              {periodCapable && <Box />}
              {delBtn(() => (isMultiRow ? periodRemoveAll(cand, idx) : removeTargetPeriod(pk, cand, idx)),
                      tr("delete"))}
            </Box>
          );
        });
        const addRow = (
          <Box key={`add-${cand.cd}`} sx={{ bgcolor: C.bg3, px: "8px", py: "4px" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px",
                       position: "sticky", left: 0, pl: "19px", bgcolor: C.bg3 }}>
              <AiButton variant="solid" size="sm" accent={accent} disabled={disabled}
                onClick={() => (isMultiRow ? periodAddAll(cand) : addTargetPeriod(def, cand))}>
                {tr("mpAddPeriodLabel")}
              </AiButton>
              {/* 능력 역산은 곧 구간별 값이라 구간을 만드는 자리에 둔다. */}
              {(bcTargetKey || pk === "efficiency") && cand.capacity_backcalc?.orders?.length > 0 && (
                <AiButton variant="soft" size="sm" accent={accent} disabled={disabled}
                  onClick={() => setBcModal({ pk: bcTargetKey || pk, rowPk: pk,
                                              cand, unit, bc: cand.capacity_backcalc })}>
                  {tr("mpProblemOrderCheckLabel")}
                </AiButton>
              )}
            </Box>
          </Box>
        );
        const notes = [];
        if (periods.length === 0) {
          notes.push(<Typography key="empty" sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
            {tr("mpPeriodDefaultApplyDesc")}</Typography>);
        } else {
          if (isMultiRow && capOver) {
            notes.push(<Typography key="cap" sx={warnSx}>
              {tr("mpWorkHourSumOverWarning", { max: MP_DAY_HOUR_CAP })}</Typography>);
          }
          if (overlapIdx.size > 0) {
            notes.push(<Typography key="ovl" sx={warnSx}>
              {tr(periods.some((p) => p?.start_dt && p?.end_dt && p.start_dt > p.end_dt)
                ? "mpPeriodReversedWarning" : "mpPeriodOverlapWarning")}</Typography>);
          }
          // 구간 밖에는 현재값이 아니라 전체 기간 값이 적용된다(param_mapper 의 period_group).
          notes.push(<Typography key="out" sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
            {tr("mpOtherPeriodsWholeValueLabel")}</Typography>);
        }
        return (
          <React.Fragment key={`pg-${cand.cd}`}>
            {rows}
            {addRow}
            {noteRow(`pn-${cand.cd}`, C.bg3, notes)}
          </React.Fragment>
        );
      };

      const resRow = (cand) => {
        // 실질 값(전체값 또는 값이 든 구간)이 있어야 '적용' — 빈 구간만 있는 행은 미적용으로 본다.
        const active = members.some(m => {
          const t = tgtOfMember(m, cand.cd);
          return !!t && ((t.value != null && t.value !== "")
            || (Array.isArray(t.periods) && t.periods.some(p => p?.value != null && p.value !== "")));
        });
        const short = Number(cand.short_count || 0), late = Number(cand.late_count || 0);
        const capBad = isMultiRow && sumOver24(cand) > MP_DAY_HOUR_CAP;
        const selected = selSet.has(cand.cd);
        const rk = `${pk}:${cand.cd}`;
        // 기본 접힘 — 펼친 행만 DatePicker 를 마운트한다(탭 재진입 경량화).
        const open = resRowOpen[rk] ?? false;
        const nPeriods = rowPeriods(cand.cd).length;
        const tint = selected ? `${accent}0A` : null;
        const btnOn = nPeriods > 0 || open;
        const btnLabel = nPeriods > 0
          ? tr("mpByPeriodSetCountTitle", { count: nPeriods }) : tr("mpByPeriodSetTitle");
        return (
          <React.Fragment key={cand.cd}>
            <Box sx={{ ...rowSx, minHeight: RES_ROW_H, bgcolor: C.bg2, borderTop: `1px solid ${C.border}`,
                       ...(tint ? { backgroundImage: `linear-gradient(${tint}, ${tint})` } : {}) }}>
              <Box sx={{ ...stickySx(C.bg2, tint), alignSelf: "stretch", display: "flex", alignItems: "center",
                         gap: "4px", pl: "8px", py: `${RES_ROW_PY}px`, minWidth: 0 }}>
                <Box component="button" type="button" disabled={disabled} aria-pressed={selected}
                  onClick={() => toggleSel(cand.cd)}
                  sx={{ flex: "0 0 auto", width: RES_SEL_W, height: RES_SEL_W, borderRadius: "4px", p: 0,
                        border: `1.5px solid ${selected ? accent : C.border}`,
                        bgcolor: selected ? accent : "transparent", color: "#fff", fontSize: 10,
                        fontWeight: 800, lineHeight: 1, cursor: disabled ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {selected ? "✓" : ""}
                </Box>
                <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "center", gap: "4px" }}>
                  {/* 적용 표시는 점으로만 — 이름이 쓸 폭을 배지에 내주지 않는다. */}
                  {active && tip(tr("appliedLayout"), (
                    <Box sx={{ flex: "0 0 auto", width: 6, height: 6, borderRadius: "50%", bgcolor: accent }} />
                  ))}
                  {tip(cand.name || cand.cd, (
                    <Typography
                      sx={{ minWidth: 0, fontSize: FONT.BODY, fontWeight: 600, color: C.text1, lineHeight: 1.25,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                            overflow: "hidden", overflowWrap: "anywhere" }}>
                      {cand.name || cand.cd}
                    </Typography>
                  ))}
                </Box>
                {cntCell(short, true)}
                {cntCell(late, false)}
              </Box>
              {members.map(k => renderValueCell(k, cand, capBad))}
              {isMultiRow && <Typography sx={sumSx(capBad)}>{sumOver24(cand)}{unit}</Typography>}
              {periodCapable && tip(btnLabel, (
                <Box component="button" type="button" disabled={disabled} aria-expanded={open}
                  aria-label={btnLabel} onClick={() => toggleRow(cand)}
                  sx={{ ...resIconBtnSx,
                        bgcolor: btnOn ? C.accL : "transparent", color: btnOn ? C.accT : C.text2,
                        "&:hover": disabled ? {}
                          : { color: C.accT, bgcolor: btnOn ? `${accent}26` : `${accent}14` } }}>
                  <CalendarMonthOutlinedIcon sx={{ fontSize: RES_ICON_PX }} />
                </Box>
              ))}
              {delBtn(() => removeCds([cand.cd]), tr("delete"))}
            </Box>
            {capBad && noteRow(`cb-${cand.cd}`, C.bg2,
              <Typography sx={warnSx}>{tr("mpWorkHourSumOverWarning", { max: MP_DAY_HOUR_CAP })}</Typography>)}
            {open && periodCapable && periodRows(cand)}
          </React.Fragment>
        );
      };

      return (
        <Box sx={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden", bgcolor: C.bg2 }}>
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: `${minTotal}px` }}>
              {head}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                {groups.map(g => (
                  <React.Fragment key={g.key}>
                    {showGroupLabel && g.label && wcHead(g)}
                    {g.items.map(resRow)}
                  </React.Fragment>
                ))}
              </LocalizationProvider>
            </Box>
          </Box>
        </Box>
      );
    };

    // 일괄 툴바 — 선택 행에 기간 구간/전체기간 값 일괄 적용.
    const draft = bulkDraft[pk] || {};   // draft.val 은 저장형(효율=소수). numInput 이 %로 표시.
    const setDraft = (patch) => setBulkDraft(m => ({ ...m, [pk]: { ...(m[pk] || {}), ...patch } }));
    // 합성 행의 일괄 적용 대상 칸 — 기본은 '초과(잔업)'(능력 확보 사다리의 첫 레버).
    const bulkKey = isMultiRow ? (draft.member || bcTargetKey || pk) : pk;
    const applyBulk = () => {
      const val = draft.val;
      if (val == null || val === "") return;
      const byCd = new Map(cands.map(c => [c.cd, c]));
      const selCands = sel.map(cd => byCd.get(cd)).filter(Boolean);
      if (!selCands.length) return;
      const whole = !periodCapable || (draft.whole ?? true);   // 기본은 전체 기간(행 편집과 같은 규칙)
      // ⚠ 시나리오 변경은 반드시 1회 onScenarioChange 로 일괄. 자원별로 개별 호출하면 부모
      //   updateAt(AiScenarioPlanning) 이 stale scenarios 클로저를 읽어 last-write-wins →
      //   선택 다건 중 마지막 1건만 반영되는 버그.
      onScenarioChange?.(sc => {
        const list = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
        // 합성 행은 값이 안 들어가는 멤버에도 같은 날짜의 빈 구간을 넣는다 — 구간 index 가 어긋나면
        //   값이 엉뚱한 구간에 붙는다(정규·초과는 같은 구간을 공유한다).
        for (const mk of members) {
          let i = list.findIndex(o => o.param_key === mk);
          if (i < 0) {
            if (!isMultiRow) return sc;
            list.push({ param_key: mk, scope_type: "resource", targets: [], value: null, reason: null });
            i = list.length - 1;
          }
          const isTargetKey = (mk === bulkKey);
          const tgts = Array.isArray(list[i].targets) ? [...list[i].targets] : [];
          for (const cand of selCands) {
            const meta = { cd: cand.cd, name: cand.name,
              ...(cand.route_cd != null ? { route_cd: cand.route_cd } : {}),
              ...(cand.loc_cd != null ? { loc_cd: cand.loc_cd } : {}) };
            let ti = tgts.findIndex(t => t.cd === cand.cd);
            if (whole) {   // 전체기간 단일값 — value 설정·periods 정리(setRowWholeMode+setTargetValue 동등).
              if (isTargetKey) {
                const base = { ...meta, value: val, periods: [] };
                if (ti >= 0) tgts[ti] = { ...tgts[ti], ...base }; else tgts.push(base);
              } else if (ti >= 0) {
                tgts[ti] = { ...tgts[ti], periods: [] };   // 모드만 맞춘다(다른 칸의 값은 보존)
              }
            } else {       // 기간별 — 대상에 구간 1줄 추가(addTargetPeriod 동등, 기존 value 유지).
              if (ti < 0) { tgts.push({ ...meta }); ti = tgts.length - 1; }
              const t = tgts[ti];
              tgts[ti] = { ...t, periods: [...(t.periods || []),
                { start_dt: draft.start || "", end_dt: draft.end || "",
                  value: isTargetKey ? val : "" }] };
            }
          }
          list[i] = { ...list[i], targets: tgts };
        }
        return { ...sc, overrides: list };
      });
    };
    const bulkDpSx = { width: 112, "& .MuiInputBase-input": { p: "4px 6px", fontSize: FONT.MICRO } };

    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 600, color: C.text3 }}>{tr("changeTargetLabel")} {tr("productionResource")}</Typography>
          <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpCapacityShortageOccurHint")})</Typography>
        </Box>
        <Box sx={{ mb: 0.6 }}>{resPicker}</Box>
        {sel.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mb: 0.6, p: "6px 8px",
                borderRadius: "8px", bgcolor: `${accent}0D`, border: `1px solid ${accent}33` }}>
            <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 600, color: accent }}>{tr("mpSelectedRowsLabel", { count: sel.length })}</Typography>
            {periodCapable && !(draft.whole ?? true) && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker format="yyyy-MM-dd" value={periodStrToDate(draft.start)} disabled={disabled}
                  onChange={(dt) => setDraft({ start: periodDateToStr(dt) })}
                  slotProps={{ textField: { size: "small", placeholder: "yyyy-MM-dd", sx: bulkDpSx } }} />
                <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>~</Typography>
                <DatePicker format="yyyy-MM-dd" value={periodStrToDate(draft.end)} disabled={disabled} minDate={periodStrToDate(draft.start) || undefined}
                  onChange={(dt) => setDraft({ end: periodDateToStr(dt) })}
                  slotProps={{ textField: { size: "small", placeholder: "yyyy-MM-dd", sx: bulkDpSx } }} />
              </LocalizationProvider>
            )}
            {/* 합성 행 — 어느 칸에 넣을지 먼저 고른다(기본 초과). 값 하나로 두 칸을 동시에 바꾸면
                합계 24h 검증이 성립하지 않고, 무엇을 바꿨는지도 읽히지 않는다. */}
            {isMultiRow && (
              <AiSegmentedControl accent={accent} disabled={disabled} value={bulkKey}
                options={members.map(k => ({ value: k, label: tr(mpRowRoleLabel(defOfMember(k))) }))}
                onChange={(m) => setDraft({ member: m })} />
            )}
            {numInput(draft.val, (v) => setDraft({ val: v === "" ? "" : (isPct ? yieldPctToDec(v) : v) }), 54)}
            {unit && <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{unit}</Typography>}
            <Box component="button" type="button" disabled={disabled || draft.val == null || draft.val === ""} onClick={applyBulk}
              sx={{ border: "none", borderRadius: "6px", px: "9px", py: "3px", bgcolor: accent, color: "#fff",
                    fontFamily: "inherit", fontSize: FONT.MICRO, fontWeight: 700, cursor: "pointer",
                    "&:disabled": { bgcolor: C.border, color: C.text3, cursor: "not-allowed" } }}>{tr("mpApplySelectedRowsLabel")}</Box>
            {periodCapable && (
              <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 0.3, cursor: "pointer" }}>
                <Box component="input" type="checkbox" disabled={disabled} checked={draft.whole ?? true}
                  onChange={(e) => setDraft({ whole: e.target.checked })}
                  style={{ width: 12, height: 12, accentColor: accent }} />
                <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{tr("mpWholePeriodApplyLabel")}</Typography>
              </Box>
            )}
            <Box component="button" type="button" onClick={() => setSel([])}
              sx={{ border: "none", background: "none", color: C.text3, fontFamily: "inherit", fontSize: FONT.MICRO,
                    fontWeight: 500, cursor: "pointer", ml: "auto" }}>{tr("mpClearSelectionLabel")}</Box>
          </Box>
        )}
        {/* 폭 판정은 뷰포트가 아니라 이 표의 실제 폭으로 — 챗 패널이 카드를 밀어 좁힌다. */}
        <ResGridFrame>{renderTable}</ResGridFrame>
      </Box>
    );
  };

  // 파라미터 카드 1개 렌더 (그룹 무관 — 그룹 래퍼가 호출).
  const renderParamCard = (def, last = false) => {
        const ov     = ovOf(def.param_key);
        // 합성 행은 동반 키만 override 를 가질 수 있다 — AI 는 시간 레버를 잔업으로만 제안하므로
        //   대표(작업 시간/일)에는 override 가 없거나 펼침이 만든 빈 껍데기뿐이다. 그 빈 껍데기의
        //   적용 단위(전체)를 따르면 동반 키의 값이 화면에서 통째로 사라진다 → 내용을 가진 쪽을 따른다.
        const _hasBody = (o) => !!o && (hasEffectiveValue(null, o.value) || (o.targets || []).length > 0);
        const rowOv  = _hasBody(ov) ? ov
          : (rowMembers(def.param_key).map(ovOf).find(_hasBody) || ov || null);
        const active = ov != null;                         // override 존재 = 변경 대상
        const scopes = def.scopes?.length ? def.scopes : ["all"];
        const scope  = rowOv?.scope_type ?? scopes[0];
        let targetList = scope === "all" ? [] : (scopeTargets[scope] || []);
        // 대체 자원 정책은 대체 자원(2개 이상) 있는 라우팅만 — current.alt_res_policy 없으면(1자원) 후보 제외.
        // route 후보는 lot_size/yield_rate 와 공유하므로 alt_res_policy 일 때만 필터.
        if (def.param_key === "alt_res_policy" && scope === "route") {
          targetList = targetList.filter(t => t.current?.alt_res_policy != null);
        }
        // 대체 자원 정책 워크센터 스코프 — 대체 자원 보유 WC 만(route/routing_op 와 후보 유무 일치).
        if (def.param_key === "alt_res_policy" && scope === "workcenter") {
          targetList = targetList.filter(t => t.has_alt_res);
        }
        // 생산 공정 상세 = 자원별 개별 편집(일괄 아님) — 우선순위(정책 포함)·로트 사이즈·수율.
        const isRoutingOpPerRes = scope === "routing_op"
          && (def.param_key === "alt_res_policy" || def.param_key === "yield_rate"
              || isRowPrimary(def.param_key));
        // 자원 능력 수치 파라미터 = 대상별 행 그리드(칩+단일값 대신). 자원·워크센터 두 스코프 공용.
        const isResourceGrid = (scope === "resource" || scope === "workcenter")
          && MP_RESOURCE_NUM.has(def.param_key);
        // 수요 우선순위 = 순위 빌더(다중 기준 select 행 리스트).
        const isDemandPriorityBuilder = def.param_key === "DEMAND_PRIORITY";
        // 휴무 캘린더 해제 = 워크센터/자원 칩 선택만(값 입력 없음). 선택 = 해당 대상의 휴무 캘린더 미사용.
        const isHolidayCal = def.type === "holiday_calendar";
        // 주기 캘린더 중 드롭다운으로 대상을 고르는 스코프 — 행에 삭제 버튼을 붙인다.
        const calRowDeletable = isHolidayCal && (scope === "resource" || scope === "workcenter");
        // 작업 전환 시간 조정 — 적용 단위(Apply Scope)가 곧 파라미터 값이고, 고른 단위에 따라
        //   같은 행 안에서 배율/절대값을 편집한다. 값 셀은 그 세그먼트가 대신하므로 따로 두지 않는다.
        //   조정 대상 후보는 전 공정(routing_op_all) — 줄이려는 공정이 지금 병목이라는 보장이 없다.
        const isJctParam = def.param_key === "JOB_CHANGE_TIME";
        // 대체 자원 정책 + 생산 공정 상세 = 대체 자원 보유 공정의 'active 전체 자원'(별도 후보 routing_op_alt).
        //   능력 남는 자원도 모두 노출해야 우선순위 조정(능력 여유 자원 우선↑)이 가능하므로 문제 자원 한정이 아님.
        if (def.param_key === "alt_res_policy" && scope === "routing_op") {
          targetList = scopeTargets["routing_op_alt"] || [];
        }

        // 주기 캘린더 + 생산 공정 상세 = BOR 캘린더(z_routing.nop_cal_grp_id)가 걸린 공정 행만.
        //   문제 자원으로 좁히는 routing_op 를 쓰면 안 된다 — BOR 캘린더로 막힌 공정은 자원 가동률에
        //   드러나지 않아(z_resource_util 은 자원 캘린더만 반영) 원인 공정이 후보에서 빠진다.
        if (isHolidayCal && scope === "routing_op") {
          targetList = scopeTargets["routing_op_cal"] || [];
        }
        // 자원·워크센터도 같은 이유로 '캘린더가 걸린 전체'를 쓴다 — 문제 자원 한정 후보(scopeTargets[scope])는
        //   해제하려는 캘린더가 지금 능력부족을 내고 있어야만 목록에 뜨는 셈이라 대상을 못 찾는다.
        if (isHolidayCal && (scope === "resource" || scope === "workcenter")) {
          targetList = scopeTargets[`${scope}_cal`] || [];
        }

        // 해제 가능 여부는 실제 연결(nop_cal_grp_id)로 판단 — name 은 표시용 라벨이라 비어 있어도(NULL) 해제 대상이다.
        if (isHolidayCal) {
          targetList = targetList.filter(t => t.nop_cal_grp_id != null);
        }
        // 원인 필터(주문 스코프) — 그룹핑 대신 필터(planner ③). 옵션은 미필터 후보 전체에서 산출
        //   (각 원인이 막는 주문 수), 선택 시 그 원인을 cause_names 에 가진 주문만 노출. 옵션은
        //   실제 멤버십 기반이라 count≥1 → 선택해도 빈 목록이 되지 않는다.
        const isOrderScope = scope === "loc_item" || scope === "mc_order";
        const selCause = isOrderScope ? (causeFilter[def.param_key] || "") : "";
        const causeOptions = isOrderScope ? (() => {
          const m = new Map();
          targetList.forEach(t => (t.cause_names || []).forEach(n => m.set(n, (m.get(n) || 0) + 1)));
          return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
        })() : [];
        if (selCause) {
          targetList = targetList.filter(t => (t.cause_names || []).includes(selCause));
        }
        // 비-전체 스코프인데 변경 대상 후보가 없음 → 값 변경 무의미(값 잠금, '적용' 미표시). 전체는 전역이라 제외.
        //   자원 능력 수치 파라미터는 드롭다운으로 활성 전 대상에서 고를 수 있으므로, 문제 대상이
        //   0 이어도 '후보 없음'이 아니다 — 전체 목록으로 판정한다(안 그러면 값이 잠겨 추가해도 못 쓴다).
        const numAllCount = MP_RESOURCE_NUM.has(def.param_key)
          ? ((scopeTargets[`${scope}_all`] || []).length) : 0;
        const noScopeData = scope !== "all"
          && targetList.length === 0 && numAllCount === 0;
        // AI 변경 강조(hot)는 hotWrap(테두리·배경·"AI 변경" 배지)이 전담한다 — 펼침 상태와 분리.
        //   OR 하면 하이라이트 해제 코드가 없어 입력창이 영구히 열린 채 남고, 값 셀 포커스가
        //   Agent Chat 입력창을 뺏는다.
        const open    = openKeys.has(def.param_key);
        // 합성 행 — 그룹 멤버 중 하나라도 값이 있으면 '적용'.
        const isGroupRow = isRowPrimary(def.param_key);
        const groupKeys = rowMembers(def.param_key);
        const lotOv = (pk) => ovOf(pk);
        const lotHasAny = isGroupRow && groupKeys.some(pk => {
          const o = ovOf(pk);
          //   구간에만 값이 실린 경우(AI 역산 제안)도 '적용'이다 — 빠뜨리면 바꿔 놓고도 접힌 행이
          //   '현행 값 유지'로 보이고 조정 건수와도 어긋난다(_isAdjusted 와 같은 기준).
          return o && (hasEffectiveValue(null, o.value)
            || (o.targets || []).some(t => hasEffectiveValue(null, t?.value)
                || (t?.periods || []).some(pd => hasEffectiveValue(null, pd?.value))));
        });
        // 접힌 행의 '변경 후' 표기 — 자원 그리드는 값이 targets·periods 에 실려 override 단일값이
        //   비어 있다. 그대로 두면 바꿔 놓고도 '현행 값 유지'로 읽힌다.
        //   합성 행은 하루 합계(정규+초과)로 낸다 — 카드·미리보기와 같은 축으로 읽히게 한다.
        //   대상마다 값이 다르면 최소~최대로 압축한다(미리보기와 같은 규칙).
        const gridValueText = () => {
          const keys = rowMembers(def.param_key);
          const cands = scopeTargets[`${scope}_all`]?.length
            ? scopeTargets[`${scope}_all`] : (scopeTargets[scope] || []);
          const curNum = (cd, k) => {
            const n = Number(cands.find(x => x.cd === cd)?.current?.[k]);
            return Number.isFinite(n) ? n : 0;
          };
          const totals = [];
          keys.forEach(k => {
            (ovOf(k)?.targets || []).forEach(t => {
              // 다른 멤버는 손대지 않았으므로 그 대상의 현재값이 그대로 남는다 — 합계에 넣는다.
              const others = keys.filter(m => m !== k).reduce((a, m) => a + curNum(t.cd, m), 0);
              [t.value, ...((t.periods || []).map(pd => pd?.value))].forEach(v => {
                const n = Number(v);
                if (v != null && v !== "" && Number.isFinite(n)) totals.push(n + others);
              });
            });
          });
          if (!totals.length) return null;
          const lo = Math.min(...totals), hi = Math.max(...totals);
          return lo === hi ? fmtCur(def, lo) : `${fmtCur(def, lo)} ~ ${fmtCur(def, hi)}`;
        };
        const applied = isGroupRow ? (lotHasAny && !noScopeData) : active && !noScopeData && (
          // 해제 = 선택 대상 1개 이상. 전체는 대상이 없으므로 명시적 체크(value=clear)로 판정.
          isHolidayCal ? (scope === "all" ? ov.value === "clear" : (ov.targets || []).length > 0)
          // 생산 공정 상세 = 자원별 개별 편집 → ov.value 가 아니라 대상별 값·공정 정책 변경만 '적용'으로 본다
          //   (현재 정책이 이미 priority 등일 때 ov.value 로 인한 오탐(초록) 방지).
          : isRoutingOpPerRes ? ((ov.targets || []).some(t => hasEffectiveValue(null, t?.value))
                || Object.keys(ov.route_policies || {}).length > 0)
          // 자원 그리드 = 대상별 전체값 또는 '값이 든 구간'이 하나라도 있으면 적용(빈 구간만 있으면 미적용).
          //   워크센터 일괄값(override 단일값)도 적용이다 — 대상 값만 보면 등록 시나리오 시드처럼
          //   단일값만 실린 override 가 '현행 값 유지'로 보인다(조정 건수와도 어긋난다).
          : isResourceGrid ? (hasEffectiveValue(null, ov.value)
                || (ov.targets || []).some(t => hasEffectiveValue(null, t?.value)
                || (Array.isArray(t?.periods) && t.periods.some(p => hasEffectiveValue(null, p?.value)))))
          : isDemandPriorityBuilder ? (Array.isArray(ov.priority_items) && ov.priority_items.length > 0)
          : hasEffectiveValue(def, ov.value));

        // 값 셀에서 바로 고칠 수 있는 파라미터 — 계획 방식·백오더·안전재고 정책처럼
        //   적용 단위가 전체 하나뿐이고 컨트롤도 하나인 것들. 대상 선택(칩·그리드)·기간·
        //   우선순위 빌더·휴무 캘린더가 붙는 파라미터는 펼침 본문이 여전히 필요하다.
        const inline = isInlineEditable(def)
          && scopes.length === 1 && scopes[0] === "all"
          && !isRoutingOpPerRes && !isResourceGrid && !isDemandPriorityBuilder && !isHolidayCal
          // 작업 전환 시간 조정은 적용 단위에 따라 조정 입력이 따라 나오므로 값 셀만으로는 부족하다.
          && !isJctParam
          && !MP_PERIOD_CAPABLE.has(def.param_key);
        const inlineEditing = inline && open && !disabled;

        // 접힌 행의 적용 단위 칩 — 대상 1개면 그 코드, 여러 개면 "N개 워크센터" 식으로 압축.
        //   전체(all)는 기본값이라 굳이 알리지 않는다(칩이 모든 행에 붙으면 신호가 죽는다).
        //   합성 행은 대상을 멤버끼리 공유하므로 내용을 가진 쪽(rowOv)을 본다 — 대표만 보면
        //   잔업에만 값이 실린 행이 '어느 자원을 바꿨는지' 없이 값만 뜬다.
        const scopeTargetsSel = Array.isArray(rowOv?.targets) ? rowOv.targets : [];
        // 작업 전환 시간 조정의 공정별 값은 적용 단위가 전체(all)라 아래 기본 칩 경로가 잡지 못한다.
        //   접힌 행에서도 무엇을 바꿨는지 보이도록 별도로 만든다 — 공정 · 설비 (+ 건수).
        const jctEdited = (isJctParam && ov?.value === "routing_op")
          ? scopeTargetsSel.filter(t => hasEffectiveValue(null, t?.value)) : [];
        const jctCandOf = (t) => (scopeTargets["routing_op_all"] || []).find(c => c.cd === t.cd);
        const jctName = (t) => {
          const cand = jctCandOf(t);
          const parts = String(t.cd || "").split("@");
          return [cand?.route_name || t.route_cd || parts[0],
                  cand?.name || t.name || parts[1]].filter(Boolean).join(" · ");
        };
        const scopeChip = jctEdited.length > 0
          ? (jctEdited.length === 1 ? jctName(jctEdited[0])
             : tr("andNMoreLabel", { first: jctName(jctEdited[0]), count: jctEdited.length - 1 }))
          : (scope === "all" || scopeTargetsSel.length === 0) ? null
          : scopeTargetsSel.length === 1
            ? (scopeTargetsSel[0].name || scopeTargetsSel[0].cd)
            : tr("paramScopeCountLabel", { count: scopeTargetsSel.length,
                                           unit: tr(MP_SCOPE_LABEL[scope] || scope) });
        // 칩은 값 셀 폭에서 잘릴 수 있고, 압축된 다건 칩은 '어느 대상인지'를 아예 가린다.
        const scopeChipTitle = !scopeChip ? undefined
          : jctEdited.length > 0
            ? (() => {
                // 값까지 함께 — 어느 설비를 몇 분으로 바꿨는지가 접힌 상태에서 확인해야 할 정보다.
                const rows = jctEdited.map(t =>
                  `${jctName(t)} ${t.value}${tr(JCT_UNIT_KEY[jctCandOf(t)?.time_uom] || "minutes")}`);
                const head = rows.slice(0, 10);
                return head.length < rows.length
                  ? tr("andNMoreLabel", { first: head.join(", "), count: rows.length - head.length })
                  : head.join(", ");
              })()
          : scopeTargetsSel.length === 1
            ? [scopeTargetsSel[0].name || scopeTargetsSel[0].cd,
               scopeTargetsSel[0].cd && scopeTargetsSel[0].cd !== scopeTargetsSel[0].name
                 ? scopeTargetsSel[0].cd : null].filter(Boolean).join(" · ")
            : (() => {
                const names = scopeTargetsSel.map(t => t.name || t.cd).filter(Boolean);
                const head = names.slice(0, 10);   // 전체 선택이면 수십 줄짜리 툴팁이 된다
                return head.length < names.length
                  ? tr("andNMoreLabel", { first: head.join(", "), count: names.length - head.length })
                  : head.join(", ");
              })();

        return hotWrap(def.param_key, (
          <Box sx={paramRowSx({ accent, applied, last })}>
            {/* 컴팩트 헤더 — 클릭 = 펼침/접힘 */}
            <Box onClick={() => !disabled && toggleOpenMp(def)}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, px: PARAM_ROW_PAD_X, py: "6px",
                    height: PARAM_ROW_MIN_H, boxSizing: "border-box", overflow: "hidden",
                    cursor: disabled ? "default" : "pointer", userSelect: "none",
                    "&:hover": disabled ? {} : { bgcolor: `${accent}08` } }}>
              {/* 라벨은 폭 고정 — grow 를 주면 값 셀과 남는 공간을 나눠 가져 열이 어긋난다.
                  30%: 가장 긴 라벨(en "Alternative Resource Policy" 147px)에 아직 여유가 있고,
                  남긴 폭은 가장 좁은 현행 열로 간다 — en "Current setting: Master Data"(142px)가
                  잘리지 않는 하한이다(ko/ja 는 더 짧다). */}
              <Typography noWrap sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1,
                                       flex: "0 0 30%", minWidth: 0 }}>
                {tr(def.label_key || def.label)}
              </Typography>
              <ParamValueCells
                applied={!applied ? null : (isGroupRow
                  ? (rowGroupText(def.param_key, pk => {
                      const o = lotOv(pk);
                      return hasEffectiveValue(null, o?.value) ? o.value : null;
                    }) || gridValueText())
                  : (hasEffectiveValue(null, ov?.value) ? fmtCur(def, ov.value) : gridValueText()))}
                current={isGroupRow
                  ? (() => {
                      const body = rowGroupText(def.param_key, pk => defOfKey(pk)?.current_value ?? null);
                      return body ? baselineCurrentText(def, body, tr) : body;
                    })()
                  : fmtBaselineCurrent(def)} currentPrefix={currentPrefixFor(def, applied, tr)}
                accent={accent}
                input={inlineEditing ? renderValueInput(def, ov?.value, (val) => {
                  setValueTouched(m => ({ ...m, [def.param_key]: true }));
                  upsertOv(def.param_key, { value: val });
                }) : null}
                onEditEnd={() => endEditMp(def.param_key)}
                extras={scopeChip ? (
                  // maxWidth 기준은 부모(값 셀 34%)다 — %로 주면 실효 폭이 행의 11.6%까지 줄어 코드가 서너 자만 남는다.
                  //   calc 는 값 텍스트가 사라지지 않을 최소폭만 남기는 바닥선(전역 box-sizing 리셋이 없어 boxSizing 명시).
                  // inline-flex 면 텍스트가 익명 flex 아이템이 되어 말줄임표 없이 글자 중간에서 잘린다.
                  <Tooltip title={scopeChipTitle || ""} placement="top" arrow>
                    <Box component="span"
                      sx={{ flex: "0 1 auto", minWidth: 0, maxWidth: "calc(100% - 46px)",
                            boxSizing: "border-box", px: "6px", height: 18,
                            display: "inline-block", lineHeight: "18px", borderRadius: PARAM_ROW_RADIUS,
                            bgcolor: `${accent}14`, color: accent,
                            fontSize: FONT.CAPTION, fontWeight: 700,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {scopeChip}
                    </Box>
                  </Tooltip>
                ) : null} />
              {/* 값이 있으면 "되돌리기", 값 없이 고정만 된 행이면 "목록에서 빼기" — 같은 버튼이
                  두 역할을 한다. 후자를 감추면 스코프 전환·추가로 끌어온 행을 도로 뺄 수 없다. */}
              {!disabled && (
                <ParamRevertButton accent={accent} hidden={!applied && !pinnedKeys.has(def.param_key)}
                  title={tr(applied ? "rpRevertToCurrentLabel" : "removeParamLabel")}
                  onClick={() => revertParam(def.param_key)} />
              )}
              {/* 펼침 표시는 본문이 있는 행에만. 인라인 행도 자리는 남긴다(열 정렬 유지). */}
              <Box component="span" sx={{ flex: "0 0 auto", color: C.text3, fontSize: 11, lineHeight: 1,
                    width: 11, textAlign: "center",
                    ...(inline ? { visibility: "hidden" } : {}),
                    transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</Box>
            </Box>

            {open && !inline && (
              <Stack spacing={0.7} sx={{ px: PARAM_ROW_PAD_X, pb: "9px", pt: "4px", borderTop: `0.5px dashed ${C.border}` }}>
                {/* 적용 단위 세그먼트 (def.scopes) */}
                {scopes.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3,
                                      flex: "0 0 auto" }}>{tr("rpApplyScopeLabel")}</Typography>
                    <AiSegmentedControl accent={accent} disabled={disabled} value={scope}
                      options={scopes.map(s => ({ value: s, label: tr(MP_SCOPE_LABEL[s]) }))}
                      onChange={(s) => {
                        // 현재 스코프 선택을 기억하고, 새 스코프의 이전 선택을 복원(탭 이동 시 유지)
                        //   합성 행은 멤버별로 — 대상별 값이 각 멤버의 override 에만 있다.
                        setScopeMem(m => rememberRowScopeTargets(
                          m, rowMembers(def.param_key), scope, ovOf));
                        // 스코프 전환은 값·대상을 비울 수 있다 — 고정하지 않으면 편집 중인 행이
                        //   '조정만 보기'에서 그대로 사라진다(되돌리기 버튼으로 다시 뺄 수 있음).
                        setPinnedKeys(st => { const n = new Set(st); n.add(def.param_key); return n; });
                        const restored = scopeMem[memKey(def.param_key, s)];
                        const patch = { scope_type: s,
                                        targets: s === "all" ? [] : (restored || []) };
                        // 대체 자원 정책: 생산 공정 상세(routing_op)=우선순위 숫자 / 그 외=정책 select.
                        //   스코프 전환 시 값 타입을 보정한다.
                        if (def.param_key === "alt_res_policy") {
                          if (s === "routing_op") {
                            patch.value = "";                       // 우선순위 숫자 입력으로 전환(사용자 입력)
                          } else if (scope === "routing_op"
                                     || (s !== "all" && ov?.value === "masterData")) {
                            // 숫자→정책 스코프 전환, 또는 masterData 불가 스코프 → 정책 기본값으로 보정
                            patch.value = (def.options || []).find(o => o.value !== "masterData")?.value ?? "";
                          }
                        }
                        // 자재 제약·자원 능력: 전체가 아닌 단위(자재·주문)는 무한만 허용 → 무한으로 보정
                        if ((def.param_key === "MC" || def.param_key === "RC") && s !== "all") {
                          patch.value = "infinite";
                        }
                        // 주기 캘린더: 전체는 전 자원 파급이 커 명시적 체크 전까지 미적용으로 둔다.
                        if (def.type === "holiday_calendar") {
                          patch.value = s === "all" ? "" : "clear";
                        }
                        // 자원 그리드(자원 스코프 수치)는 값이 자원별(targets[].value)이라 override 단일값을 비운다
                        //   (자동 제안 스킵 — 미편집 시 빈 대상+값으로 애매한 전역 적용 방지).
                        if (s === "resource" && MP_RESOURCE_NUM.has(def.param_key)) {
                          patch.value = "";
                        }
                        // 미편집 수치 파라미터: 새 스코프 대상 기준 초기값 자동 제안.
                        if (def.type !== "select" && patch.value == null
                            && !valueTouched[def.param_key]) {
                          const full = (scopeTargets[s] || []).filter(
                            c => (patch.targets || []).some(n => n.cd === c.cd));
                          // 산출 불가(전체 스코프 등) 시 기존 값 유지 — fallback = 현재 value.
                          patch.value = suggestValue(def, s, full,
                            { numMax: MP_NUM_MAX[def.param_key], fallback: ov?.value ?? defaultValue(def) });
                        }
                        // 합성 행 — 적용 단위만 공유하고 대상·값은 멤버 자기 것을 복원한다
                        //   (대표의 대상을 값째 물려주면 초과 작업 시간이 정규 값으로 덮인다).
                        //   반드시 한 번의 갱신으로 — 나눠 부르면 뒤 호출이 스코프 변경을 덮어쓴다.
                        if (isRowPrimary(def.param_key)) {
                          upsertOvMany({ [def.param_key]: patch,
                            ...companionScopePatches(rowMembers(def.param_key), def.param_key,
                                                     s, scopeMem, ovOf, patch.targets) });
                        } else {
                          upsertOv(def.param_key, patch);
                        }
                      }} />
                  </Box>
                )}

                {/* 자원·워크센터 스코프 — 대상별 행 그리드. 칩+단일값 경로 대체. */}
                {isResourceGrid && renderResourceGrid(def, ov, scope)}

                {/* 대상 복수 선택 (scope != all) — 능력부족(CAPACITY) 발생 자원/WC/라우팅 후보 칩 토글 */}
                {scope !== "all" && !isResourceGrid && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
                      <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3 }}>
                        {tr("changeTargetLabel")} {tr(MP_SCOPE_LABEL[scope])}
                      </Typography>
                      {isHolidayCal && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpHolidayCalSetHint")})</Typography>
                      )}
                      {scope === "resource" && !isHolidayCal && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpCapacityShortageOccurHint")})</Typography>
                      )}
                      {scope === "workcenter" && !isHolidayCal && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpCapacityShortageResHint")})</Typography>
                      )}
                      {scope === "route" && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpCapacityShortageResUseHint")})</Typography>
                      )}
                      {/* 주기 캘린더는 능력부족 공정으로 좁히지 않으므로 이 힌트가 맞지 않는다. */}
                      {scope === "routing_op" && !isHolidayCal && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                          ({tr("mpCapacityShortageRouteHint")}{(def.param_key === "lot_size" || def.param_key === "yield_rate") ? ` · ${tr("mpTop10ProblemOrderSuffix")}` : ""})
                        </Typography>
                      )}
                      {scope === "loc_item" && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpDueDate50DownCapacityHint")})</Typography>
                      )}
                      {scope === "mc_order" && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpDueDate50DownMaterialHint")})</Typography>
                      )}
                      {scope === "material" && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpMaterialShortageOccurHint")})</Typography>
                      )}
                      {(ov?.targets?.length ?? 0) > 0 && (
                        <Box sx={{ minWidth: 16, height: 15, px: "5px", borderRadius: "8px",
                                   bgcolor: `${accent}16`, color: accent, fontSize: FONT.MICRO, fontWeight: 700,
                                   display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {ov.targets.length}
                        </Box>
                      )}
                    </Box>
                    {/* 원인 필터(주문 스코프) — 특정 원인 자원/자재가 막는 주문만 걸러 본다. 원인 2종 이상일 때만 노출. */}
                    {isOrderScope && causeOptions.length > 1 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                        <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3 }}>
                          {tr("problemCause")}
                        </Typography>
                        <Box component="select" disabled={disabled} value={selCause}
                          onChange={(e) => setCauseFilter(m => ({ ...m, [def.param_key]: e.target.value }))}
                          sx={{ ...inputSx, width: "auto", flex: "0 0 auto", p: "2px 6px",
                                fontSize: FONT.MICRO, fontWeight: 700,
                                color: selCause ? accent : C.text2,
                                borderColor: selCause ? `${accent}80` : `${accent}40`,
                                cursor: disabled ? "not-allowed" : "pointer" }}>
                          <option value="">{tr("all")}</option>
                          {causeOptions.map(([name, cnt]) => (
                            <option key={name} value={name}>{name} ({cnt})</option>
                          ))}
                        </Box>
                        {selCause && (
                          <Typography component="span" sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                            {tr("mpProblemOrderCountLabel", { count: targetList.length })}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {targetList.length === 0 ? (
                      <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                        {isHolidayCal
                          ? tr("mpNoHolidayCalTargetDesc", { scope: tr(MP_SCOPE_LABEL[scope]) })
                          : tr("mpNoMatchingTargetDesc", { scope: tr(MP_SCOPE_LABEL[scope]) })}
                        {isHolidayCal ? ""
                          : scope === "resource" ? ` (${tr("mpCapacityShortageOccurHint")})`
                          : scope === "workcenter" ? ` (${tr("mpCapacityShortageResHint")})`
                          : scope === "route" ? ` (${tr("mpCapacityShortageResUseHint")})`
                          : scope === "routing_op" ? ` (${tr("mpCapacityShortageRouteHint")})`
                          : scope === "loc_item" ? ` (${tr("mpDueDate50DownCapacityHint")})`
                          : scope === "mc_order" ? ` (${tr("mpDueDate50DownMaterialHint")})`
                          : scope === "material" ? ` (${tr("mpMaterialShortageOccurHint")})` : ""}.
                      </Typography>
                    ) : isRoutingOpPerRes ? (() => {
                      // 생산 공정 상세 — 자원별 개별 입력. 거점 > 공정 > 자원 행.
                      //   alt_res_policy = 우선순위(정책 select 포함) / lot_size = 로트 사이즈.
                      const showPolicy = def.param_key === "alt_res_policy";
                      const polOpts = (def.options || []).filter(o => o.value !== "masterData");
                      // 로트 사이즈·수율 = 문제 주문 건수 상위 10건만 (능력 여유 자원 포함 정책인 alt_res_policy 는 전량 노출).
                      const rankLimited = def.param_key === "lot_size" || def.param_key === "yield_rate";
                      const shownList = rankLimited
                        ? [...targetList].sort((a, b) => Number(b.problem_count || 0) - Number(a.problem_count || 0)).slice(0, 10)
                        : targetList;
                      const locOrder = [];
                      const locG = {};
                      shownList.forEach(t => {
                        const lk = t.loc_cd || "_";
                        if (!locG[lk]) { locG[lk] = { loc_name: t.loc_name || t.loc_cd, rOrder: [], routes: {} }; locOrder.push(lk); }
                        const rk = t.route_cd || "_";
                        if (!locG[lk].routes[rk]) {
                          locG[lk].routes[rk] = { route_name: t.route_name || t.route_cd, policy: t.route_alt_policy,
                                                  altCnt: t.alt_res_cnt, items: [] };
                          locG[lk].rOrder.push(rk);
                        }
                        locG[lk].routes[rk].items.push(t);
                      });
                      // 공정별 심각도(문제 주문 건수 합) → 상위 N개만 기본 펼침. 목록이 길어질 수 있어 스크롤 최소화.
                      const ROUTE_DEFAULT_OPEN = 2;
                      const routeSev = {};
                      const allRks = [];
                      Object.keys(locG).forEach(lk => locG[lk].rOrder.forEach(rk => {
                        routeSev[rk] = locG[lk].routes[rk].items
                          .reduce((s, t) => s + Number(t.problem_count || 0), 0);
                        allRks.push(rk);
                      }));
                      const defaultOpen = new Set(
                        [...allRks].sort((a, b) => routeSev[b] - routeSev[a]).slice(0, ROUTE_DEFAULT_OPEN));
                      const isRouteOpen = (rk) => {
                        const v = routeOpen[`${def.param_key}:${rk}`];
                        return v == null ? defaultOpen.has(rk) : v;
                      };
                      const toggleRoute = (rk) =>
                        setRouteOpen(m => ({ ...m, [`${def.param_key}:${rk}`]: !isRouteOpen(rk) }));
                      const setAllRoutes = (val) => setRouteOpen(m => {
                        const n = { ...m };
                        allRks.forEach(rk => { n[`${def.param_key}:${rk}`] = val; });
                        return n;
                      });
                      const anyRouteClosed = allRks.some(rk => !isRouteOpen(rk));
                      return (
                        <Stack spacing={0.8}>
                          {allRks.length > 1 && (
                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                              <Box component="button" type="button" onClick={() => setAllRoutes(anyRouteClosed)}
                                sx={{ background: "none", border: "none", p: 0, cursor: "pointer", fontFamily: "inherit",
                                      fontSize: FONT.MICRO, fontWeight: 700, color: C.text3 }}>
                                {anyRouteClosed ? tr("expandArrowLabel") : tr("collapseArrowLabel")}
                              </Box>
                            </Box>
                          )}
                          {locOrder.map(lk => (
                            <Box key={lk}>
                              {lk !== "_" && (
                                <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, mb: 0.4 }}>
                                  {locG[lk].loc_name}
                                </Typography>
                              )}
                              <Stack spacing={0.6} sx={{ pl: lk !== "_" ? 0.6 : 0 }}>
                                {locG[lk].rOrder.map(rk => {
                                  const rg = locG[lk].routes[rk];
                                  const rawPol = ov?.route_policies?.[rk] ?? rg.policy;   // 정책 현재값 유지(명시 변경 시에만 override)
                                  const polVal = (rawPol === "fast" || rawPol === "priority") ? rawPol : "fast";
                                  const polChanged = rawPol !== rg.policy;   // 현재 정책과 다를 때만 '변경됨' 강조(미변경 priority 초록 오탐 방지)
                                  const open = isRouteOpen(rk);
                                  const rProbSum = routeSev[rk] || 0;                                        // 공정 문제 주문 건수 합
                                  const rProbRes = rg.items.filter(t => Number(t.problem_count || 0) > 0).length;   // 문제 자원 수
                                  return (
                                    <Box key={rk} sx={{ borderRadius: "8px", border: `1px solid ${C.border}`,
                                                        overflow: "hidden", bgcolor: C.bg2 }}>
                                      {/* 공정 헤더 — 좌: 셰브런+공정명(접기/펼치기) / 요약(문제 건수·자원) / 우: (정책 파라미터만) 현재 정책 + 변경 select */}
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: "8px", py: "5px",
                                                 bgcolor: C.bg3, borderBottom: open ? `1px solid ${C.border}` : "none" }}>
                                        {/* 셰브런+공정명 = 접기/펼치기 토글(정책 select 히트영역과 분리) */}
                                        <Box component="button" type="button" onClick={() => toggleRoute(rk)}
                                          sx={{ display: "flex", alignItems: "center", gap: 0.4, flex: 1, minWidth: 0,
                                                background: "none", border: "none", p: 0, fontFamily: "inherit",
                                                cursor: "pointer", textAlign: "left" }}>
                                          <Box component="span" sx={{ flex: "0 0 auto", width: 10, fontSize: FONT.MICRO, color: C.text3,
                                                 transition: "transform 0.15s", transform: open ? "none" : "rotate(-90deg)" }}>
                                            ▾
                                          </Box>
                                          <Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2,
                                                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {rg.route_name}
                                          </Typography>
                                        </Box>
                                        {/* 공정 요약 — 접힘·펼침 공통(앵커 유지): 문제 주문 건수 합(빨강) + 접힘 시 문제 자원 수 */}
                                        <Box component="span" sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
                                          <Box component="span" sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.g3 }}>
                                            {tr("mpProblemOrderCountLabel", { count: rProbSum })}
                                          </Box>
                                          {!open && rProbRes > 0 && (
                                            <Box component="span" sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                                              {tr("mpProblemResCountLabel", { count: rProbRes })}
                                            </Box>
                                          )}
                                        </Box>
                                        {showPolicy && (
                                          <Typography sx={{ flex: "0 0 auto", fontSize: FONT.MICRO, color: C.text3 }}>
                                            {tr("mpCurrentPrefixLabel", { value: altPolicyLabel(rg.policy) })}
                                          </Typography>
                                        )}
                                        {showPolicy && (
                                          <Box component="select" disabled={disabled} value={polVal}
                                            // 현재 정책과 동일 선택 → override 제거(변경 없음 = 초록 미표시).
                                            onChange={(e) => setRoutePolicy(def.param_key, rk,
                                              e.target.value === rg.policy ? "" : e.target.value)}
                                            sx={{ ...inputSx, width: "auto", flex: "0 0 auto", p: "2px 6px",
                                                  fontSize: FONT.MICRO, fontWeight: 700,
                                                  color: polChanged ? accent : C.text2,
                                                  borderColor: polChanged ? `${accent}80` : `${accent}40`,
                                                  cursor: disabled ? "not-allowed" : "pointer" }}>
                                            {polOpts.map(o => <option key={o.value} value={o.value}>{tr(o.label_key || o.label)}</option>)}
                                          </Box>
                                        )}
                                      </Box>
                                      {/* 자원 행 — 설비명 + 문제·가동률(빨강) / 현재값 / 개별 입력(기본=현재) */}
                                      <Collapse in={open} timeout="auto" unmountOnExit>
                                      <Box sx={{ px: "8px", py: "2px" }}>
                                        {rg.items.map((t, ri) => {
                                          const ovv = (ov?.targets || []).find(x => x.cd === t.cd)?.value;
                                          const cur = showPolicy ? t.current?.priority : t.current?.[def.param_key];  // 배치/수율=현재값
                                          const pv = ovv ?? (cur != null ? cur : "");   // 기본값 = 현재값
                                          const isYield = def.param_key === "yield_rate";   // 상한 100% 클램프·플레이스홀더용(파라미터 정체성)
                                          const isPct = def.display === "percent";          // 소수↔% 표시 변환(카탈로그 플래그, 단일 진실원)
                                          const mx = showPolicy ? (rg.altCnt || undefined) : isYield ? 100 : undefined;  // 우선순위=대체자원 수 / 수율=100%
                                          // 로트 합성 행은 입력이 3벌이라 좁은 카드에서 넘친다 — 줄바꿈을 허용한다.
                                          return (
                                            <Box key={t.cd} sx={{ display: "flex", alignItems: "center", gap: 0.6, py: "5px",
                                                  flexWrap: "wrap", rowGap: 0.4,
                                                  borderTop: ri > 0 ? `0.5px dashed ${C.border}` : "none" }}>
                                              <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "wrap" }}>
                                                <Typography sx={{ fontSize: FONT.CAPTION, color: C.text1, minWidth: 0,
                                                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                  {t.name || t.cd}
                                                </Typography>
                                                {/* 자원명 옆 빨간색 지표 — 다른 파라미터와 동일 스타일 */}
                                                <Box component="span" sx={{ flex: "0 0 auto", fontSize: FONT.MICRO, fontWeight: 700, color: C.g3 }}>
                                                  {tr("mpProblemOrderCountLabel", { count: Number(t.problem_count || 0) })}
                                                  {t.util_pct != null ? tr("mpAvgUtilSuffixLabel", { pct: Number(t.util_pct) }) : ""}
                                                </Box>
                                              </Box>
                                              {/* 로트 합성 행은 현재값을 입력칸별 라벨 아래에 각각 붙인다(아래) — 여기서는 생략. */}
                                              {cur != null && !isGroupRow && (
                                                <Typography sx={{ flex: "0 0 auto", fontSize: FONT.MICRO, color: C.text3 }}>
                                                  {tr("mpCurrentPrefixLabel", { value: isPct ? `${yieldDecToPct(cur)}%` : cur })}
                                                </Typography>
                                              )}
                                              {isGroupRow ? (() => {
                                                // 배수 · 최소 · 최대 — 자원 행 하나에 3 입력. 값은 각자의 param_key 대상별 값으로.
                                                const rowBad = findLotBoundConflicts(overrides)
                                                  .has(`${scope}\u0000${t.cd}`);
                                                // 3 입력을 등폭 3열 그리드로 — 라벨 길이가 달라도 자원 행마다 입력칸이 같은 열에 선다.
                                                return (
                                                  <Box sx={{ flex: "1 1 260px", minWidth: 240, display: "grid",   // 좁으면 통째로 다음 줄로(칸 찌그러짐 방지)
                                                             gridTemplateColumns: "repeat(3, minmax(0, 1fr))", columnGap: 0.6 }}>
                                                    {groupKeys.map((pk, pi) => {
                                                      const pDef = defOfKey(pk);
                                                      const pOv = ovOf(pk);
                                                      const pVal = (pOv?.targets || []).find(x => x.cd === t.cd)?.value ?? "";
                                                      const bad = pi > 0 && rowBad;
                                                      const rk = mpRowRoleLabel(pDef);
                                                      const unit = pDef?.unit_key ? tr(pDef.unit_key) : (pDef?.unit || "");
                                                      // 자원별 마스터 값(z_routing) — 입력 기준선을 라벨 아래에 붙인다.
                                                      const pCur = t.current?.[pk];
                                                      return (
                                                        <Box key={pk} sx={{ display: "flex", alignItems: "center", gap: 0.4, minWidth: 0 }}>
                                                          {/* 라벨·현재값은 우측 정렬 — 입력칸에 붙여 읽는다 */}
                                                          {rk && (
                                                            <Box sx={{ flex: "1 1 auto", minWidth: 0, textAlign: "right" }}>
                                                              <Typography noWrap sx={{ fontSize: FONT.MICRO, fontWeight: 700, lineHeight: 1.35,
                                                                                       color: bad ? C.g3 : C.text3 }}>
                                                                {tr(rk)}
                                                              </Typography>
                                                              <Typography noWrap sx={{ fontSize: FONT.MICRO, lineHeight: 1.35, color: C.text3 }}>
                                                                {tr("mpCurrentPrefixLabel", { value: pCur == null ? "-" : Number(pCur).toLocaleString() })}
                                                              </Typography>
                                                            </Box>
                                                          )}
                                                          <DraftNumberInput min="0" disabled={disabled}
                                                            value={pVal}
                                                            clamp={(v) => (v !== "" && Number(v) < 0 ? "0" : v)}
                                                            onCommit={(v) => setLotTargetValue(pk, t, v, scope)}
                                                            sx={{ ...inputSx, width: 62, p: "3px 4px", flex: "0 0 auto", textAlign: "center",
                                                                  ...(bad ? { borderColor: C.g3, color: C.g3, fontWeight: 700 } : {}) }} />
                                                          {unit && (
                                                            <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, flex: "0 0 auto" }}>
                                                              {unit}
                                                            </Typography>
                                                          )}
                                                        </Box>
                                                      );
                                                    })}
                                                  </Box>
                                                );
                                              })() : (
                                              <DraftNumberInput min={showPolicy ? "1" : "0"} max={mx}
                                                step={isPct ? "1" : undefined} disabled={disabled}
                                                value={isPct ? yieldDecToPct(pv) : pv}
                                                placeholder={showPolicy ? tr("priority") : isYield ? tr("mpYieldPctPlaceholder") : ""}
                                                clamp={(v) => {
                                                  if (v === "") return v;
                                                  if (mx && Number(v) > mx) return String(mx);
                                                  const lo = showPolicy ? 1 : 0;
                                                  return Number(v) < lo ? String(lo) : v;
                                                }}
                                                onCommit={(v) => setTargetValue(def.param_key, t, isPct ? yieldPctToDec(v) : v)}
                                                sx={{ ...inputSx, width: 84, p: "3px 6px", flex: "0 0 auto", textAlign: "center" }} />
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      );
                    })() : (() => {
                      // 칩 1개 렌더
                      const renderChip = (t) => {
                        const sel = (ov?.targets || []).some(x => x.cd === t.cd);
                        // 2줄 컴팩트 칩(주문 loc_item/mc_order · 자재 material) — 1줄 대상명 / 2줄 ●심각도색 + 규모.
                        //   원인명·상세는 인라인 제거 → 호버 툴팁 + 좌측 원인 필터(과밀 해소). ●색: 부족=위험, 지연=주의.
                        //   심각도 문법은 스코프 공통, 단위만 스왑(주문=부족·지연 규모 / 자재=부족 규모).
                        if (isOrderScope || scope === "material" || scope === "resource"
                            || scope === "workcenter" || scope === "route" || scope === "routing_op") {
                          const fmtQty = (q) => Number(q).toLocaleString(undefined, { maximumFractionDigits: 0 });
                          let dotColor, magnitude, tip, current;
                          if (scope === "material") {
                            // 자재 = 부족 원인 자재 → '부족 N건'(주문 건수, distinct 없이).
                            //   부족 수량(EA)은 표시하지 않는다: 엔진 probe(오더=1) 모드에서 토큰 부족량이 축소될 수 있어 스케일 신뢰 불가.
                            const cnt = Number(t.shortage_count || 0);
                            dotColor = sel ? "rgba(255,255,255,0.9)" : C.g3;   // 자재 부족 = 위험색
                            magnitude = tr("mpShortCountLabel", { count: cnt });
                            tip = (t.cd && t.cd !== t.name) ? t.cd : undefined;
                          } else if (scope === "resource") {
                            // 자원 = 능력부족 발생 자원 → ●색(부족 있으면 위험, 없으면 주의) + 부족/지연 분해 건수(심각도).
                            //   부족이 지연보다 심각 → 부족 앞·부족 있으면 ● 위험색. 현재값(B)=우측 무채색, 가동률(C)=툴팁.
                            const shortN = Number(t.short_count || 0);
                            const lateN  = Number(t.late_count || 0);
                            dotColor = sel ? "rgba(255,255,255,0.9)" : (shortN > 0 ? C.g3 : C.g2);
                            const parts = [];
                            if (shortN > 0) parts.push(tr("mpShortCountLabel", { count: shortN }));
                            if (lateN > 0)  parts.push(tr("mpLateCountLabel", { count: lateN }));
                            magnitude = parts.join(" · ") || tr("mpProblemOrderCountLabel", { count: Number(t.problem_count || 0) });
                            const cv = t.current?.[def.param_key];
                            current = cv != null ? tr("mpCurrentPrefixLabel", { value: fmtCur(def, cv) }) : undefined;
                            tip = [
                              (t.cd && t.cd !== t.name) ? t.cd : null,
                              t.util_pct != null ? tr("mpAvgUtilSuffixLabel", { pct: Number(t.util_pct) }).replace(/^[\s·]+/, "") : null,
                            ].filter(Boolean).join(" · ") || undefined;
                          } else if (scope === "workcenter" || scope === "route") {
                            // 작업장·공정 = 능력부족 발생 자원 보유 → ●위험색 + 문제 자원 수(심각도).
                            //   현재값(B)=평균값 우측 무채색, 정책분포(C)=툴팁(작업장 대체자원정책만).
                            dotColor = sel ? "rgba(255,255,255,0.9)" : C.g3;
                            magnitude = tr("mpProblemResCountLabel", { count: Number(t.problem_resource_count || 0) });
                            const cv = t.current?.[def.param_key];
                            current = cv != null
                              ? tr("mpCurrentPrefixLabel", { value: `${def.type !== "select" ? tr("mpAvgPrefixLabel") : ""}${fmtCur(def, cv)}` })
                              : undefined;
                            const tipParts = [(t.cd && t.cd !== t.name) ? t.cd : null];
                            if (def.param_key === "alt_res_policy" && scope === "workcenter" && t.alt_res_policy_counts?.length > 0) {
                              tipParts.push(t.alt_res_policy_counts.map(pc => {
                                const opt = (def.options || []).find(o => o.value === pc.policy);
                                return tr("mpPolicyCountLabel", { label: opt ? tr(opt.label_key || opt.label) : pc.policy, count: pc.count });
                              }).join(" · "));
                            }
                            tip = tipParts.filter(Boolean).join(" · ") || undefined;
                          } else if (scope === "routing_op") {
                            // 생산공정상세(칩형) = 문제 주문 건수(심각도). 현재값(B)=우측 무채색, 툴팁=자원코드.
                            dotColor = sel ? "rgba(255,255,255,0.9)" : C.g3;
                            magnitude = tr("mpProblemOrderCountLabel", { count: Number(t.problem_count || 0) });
                            const cv = t.current?.[def.param_key];
                            current = cv != null ? tr("mpCurrentPrefixLabel", { value: fmtCur(def, cv) }) : undefined;
                            tip = t.res_cd || ((t.cd && t.cd !== t.name) ? t.cd : undefined);
                          } else {
                            const shortN = Number(t.short_order_count || 0);
                            const lateN  = Number(t.late_order_count || 0);
                            const qty    = Number(t.short_order_qty || 0);
                            const isShort = shortN > 0;
                            dotColor = sel ? "rgba(255,255,255,0.9)" : (isShort ? C.g3 : C.g2);
                            magnitude = isShort
                              ? tr("mpShortCountLabel", { count: shortN }) + (qty > 0 ? ` (${fmtQty(qty)}${t.uom ? ` ${t.uom}` : ""})` : "")
                              : tr("mpLateCountLabel", { count: lateN });
                            tip = [
                              (t.cd && t.cd !== t.name) ? t.cd : null,
                              t.cause_names?.length ? `${tr("problemCause")}: ${t.cause_names.join(", ")}` : null,
                            ].filter(Boolean).join("\n") || undefined;
                          }
                          return (
                            <Box key={t.cd} component="button" type="button" disabled={disabled}
                              onClick={() => toggleTarget(def.param_key, t)} title={tip}
                              sx={{ display: "inline-flex", flexDirection: "column", alignItems: "stretch",
                                    gap: 0.1, minWidth: 0, maxWidth: "100%",
                                    border: `1px solid ${sel ? accent : C.border}`, borderRadius: "7px",
                                    px: "8px", py: "3px", fontFamily: "inherit",
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    color: sel ? "#fff" : C.text2, bgcolor: sel ? accent : C.bg2,
                                    transition: "background-color .12s, border-color .12s, color .12s",
                                    ...(!sel && !disabled && { "&:hover": { borderColor: accent, bgcolor: `${accent}0A` } }) }}>
                              <Box component="span" sx={{ maxWidth: "100%", fontSize: FONT.CAPTION, fontWeight: 700,
                                     overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {sel ? "✓ " : ""}{t.name || t.cd}
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, whiteSpace: "nowrap" }}>
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%",
                                       flex: "0 0 auto", bgcolor: dotColor }} />
                                <Box component="span" sx={{ fontSize: FONT.MICRO, fontWeight: 700,
                                       color: sel ? "rgba(255,255,255,0.9)" : C.text2 }}>
                                  {magnitude}
                                </Box>
                                {current && (
                                  <Box component="span" sx={{ ml: "auto", pl: 0.5, flex: "0 0 auto",
                                         fontSize: FONT.MICRO, whiteSpace: "nowrap",
                                         color: sel ? "rgba(255,255,255,0.8)" : C.text3 }}>
                                    {current}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        }
                        return (
                          <Box key={t.cd} component="button" type="button" disabled={disabled}
                            onClick={() => toggleTargetSynced(def.param_key, t)}
                            title={scope === "routing_op" ? t.res_cd
                                   : (t.cd && t.cd !== t.name ? t.cd : undefined)}
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.3,
                                  border: `1px solid ${sel ? accent : C.border}`, borderRadius: "7px",
                                  px: "8px", py: "3px", fontFamily: "inherit", fontSize: FONT.CAPTION,
                                  fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
                                  color: sel ? "#fff" : C.text2, bgcolor: sel ? accent : C.bg2,
                                  transition: "background-color .12s, border-color .12s, color .12s",
                                  ...(!sel && !disabled && { "&:hover": { borderColor: accent, bgcolor: `${accent}0A` } }) }}>
                            {sel ? "✓ " : ""}{t.name || t.cd}
                            {/* 자원·생산 공정 상세: 능력부족 문제 주문 건수 / 워크센터·라우팅: 능력부족 발생 자원 수 */}
                            {(scope === "resource" || scope === "routing_op") && t.problem_count != null && (
                              <Box component="span" sx={{ fontSize: FONT.MICRO, fontWeight: 700,
                                     color: sel ? "rgba(255,255,255,0.85)" : C.g3 }}>
                                {tr("mpProblemOrderCountLabel", { count: Number(t.problem_count) })}
                              </Box>
                            )}
                            {(scope === "workcenter" || scope === "route") && t.problem_resource_count != null && (
                              <Box component="span" sx={{ fontSize: FONT.MICRO, fontWeight: 700,
                                     color: sel ? "rgba(255,255,255,0.85)" : C.g3 }}>
                                {tr("mpProblemResCountLabel", { count: Number(t.problem_resource_count) })}
                              </Box>
                            )}
                            {(() => {
                              // 대체 자원 정책 + 생산 공정 상세 = 자원별 우선순위(current.priority). 그 외는 param_key.
                              const curKey = (def.param_key === "alt_res_policy" && scope === "routing_op")
                                ? "priority" : def.param_key;
                              const cv = t.current?.[curKey];
                              if (cv == null) return null;
                              return (
                                <Box component="span" sx={{ fontSize: FONT.MICRO,
                                       color: sel ? "rgba(255,255,255,0.8)" : C.text3 }}>
                                  {/* 작업장·라우팅 스코프의 수치값은 자원 평균 (select 정책 제외) */}
                                  {tr("mpCurrentPrefixLabel", { value: `${(scope === "workcenter" || scope === "route") && def.type !== "select" ? tr("mpAvgPrefixLabel") : ""}${curKey === "priority" ? cv : fmtCur(def, cv)}` })}
                                </Box>
                              );
                            })()}
                            {/* 대체 자원 정책 + 작업장: 그 작업장 라우팅의 정책별 건수 분포 (fast N · priority M) */}
                            {def.param_key === "alt_res_policy" && scope === "workcenter"
                              && (t.alt_res_policy_counts?.length > 0) && (
                              <Box component="span" sx={{ fontSize: FONT.MICRO,
                                     color: sel ? "rgba(255,255,255,0.8)" : C.text3 }}>
                                {t.alt_res_policy_counts.map(pc => {
                                  const opt = (def.options || []).find(o => o.value === pc.policy);
                                  return tr("mpPolicyCountLabel", { label: opt ? tr(opt.label_key || opt.label) : pc.policy, count: pc.count });
                                }).join(" · ")}
                              </Box>
                            )}
                          </Box>
                        );
                      };
                      // calOpen 은 컴포넌트 state 라 화면을 옮겼다 오면 비어 있다. 그 상태에서 행을 건드리면
                      //   기준이 targets 로 돌아가, 스위치를 켜는 순간 그 행이 사라지고(해제 대상에서 빠지므로)
                      //   삭제 버튼은 빈 배열을 걸러 전 행을 날린다. 그래서 손대기 직전에 한 번 채운다.
                      const seededCalOpen = (s) => {
                        if (s[scope]) return s[scope];
                        const sel = (ov?.targets || []).map(x => x.cd);
                        if (scope === "routing_op") {
                          const out = [];
                          targetList.forEach(c => {
                            const rk = c.route_cd || String(c.cd || "").split("@")[0];
                            if (sel.includes(c.cd) && rk && !out.includes(rk)) out.push(rk);
                          });
                          return out;
                        }
                        return targetList.filter(c => sel.includes(c.cd)).map(c => c.cd);
                      };
                      // 휴무 캘린더 해제 — 칩 대신 행: 명 / 문제수 / 캘린더명 / [캘린더 확인] / 활성·비활성 토글.
                      const renderHolidayRow = (t) => {
                        const released = (ov?.targets || []).some(x => x.cd === t.cd);   // 해제됨 = targets 포함
                        const noId = t.nop_cal_grp_id == null;
                        return (
                          <Box key={t.cd} sx={{ display: "flex", alignItems: "center", gap: 0.6, py: "5px",
                                borderTop: `0.5px dashed ${C.border}` }}>
                            {/* 명 + 문제수 = 왼쪽 그룹(문제수 좌측 정렬). 이 그룹이 flex 1 로 우측 요소를 밀어낸다. */}
                            <Box sx={{ flex: "1 1 auto", minWidth: 60, display: "flex", alignItems: "baseline",
                                       gap: 0.6, overflow: "hidden" }}>
                              <Typography title={t.cd && t.cd !== t.name ? t.cd : undefined}
                                sx={{ flex: "0 1 auto", minWidth: 0, fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1,
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {t.name || t.cd}
                              </Typography>
                              {/* 주기 캘린더 후보는 문제 대상으로 좁히지 않아 0 건이 흔하다 — 있을 때만 표시. */}
                              {(scope === "resource" || scope === "routing_op")
                                && Number(t.problem_count) > 0 && (
                                <Typography sx={{ flex: "0 0 auto", fontSize: FONT.MICRO, fontWeight: 700, color: C.g3 }}>
                                  {tr("mpProblemOrderCountLabel", { count: Number(t.problem_count) })}
                                </Typography>
                              )}
                              {scope === "workcenter" && Number(t.problem_resource_count) > 0 && (
                                <Typography sx={{ flex: "0 0 auto", fontSize: FONT.MICRO, fontWeight: 700, color: C.g3 }}>
                                  {tr("mpProblemResCountLabel", { count: Number(t.problem_resource_count) })}
                                </Typography>
                              )}
                            </Box>
                            <Tooltip title={t.nop_cal_grp_name || ""} placement="top" arrow>
                              <Typography
                                // 캘린더명은 대부분 "주7일 가동 (월 1회 정기보전 휴무)" 처럼 길다 — 150 은
                                //   왼쪽에 여백이 남는데도 잘렸다. 왼쪽 이름 묶음이 minWidth 60 까지 줄어드니
                                //   여기서 더 받아도 행이 깨지지 않는다.
                                sx={{ flex: "0 1 auto", maxWidth: 260, fontSize: FONT.MICRO, color: C.text3,
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {t.nop_cal_grp_name || "—"}
                              </Typography>
                            </Tooltip>
                            <AiButton variant="soft" size="sm" accent={accent}
                              disabled={disabled || noId}
                              onClick={() => openCycleCalendar(mpDispatch, t.nop_cal_grp_id, t.nop_cal_grp_name)}>
                              {tr("mpCheckCalendarLabel")}
                            </AiButton>
                            <AiStyledSwitch accent={accent} disabled={disabled} checked={!released}
                              onChange={() => {
                                setCalOpen(s => ({ ...s, [scope]: seededCalOpen(s) }));
                                toggleTarget(def.param_key, t);
                              }} sx={{ flex: "0 0 auto" }} />
                            {/* 드롭다운으로 고른 스코프만 행 단위 삭제 — 공정 상세는 공정 카드에 삭제가 있다. */}
                            {calRowDeletable && (
                              <AiDeleteButton disabled={disabled} onClick={() => {
                                setCalOpen(s => ({
                                  ...s, [scope]: seededCalOpen(s).filter(cd => cd !== t.cd) }));
                                if (released) toggleTarget(def.param_key, t);
                              }} />
                            )}
                          </Box>
                        );
                      };
                      // 주기 캘린더 + 생산 공정 상세: 후보가 마스터에 따라 수백 건이 될 수 있어 칩으로
                      //   전부 깔지 않는다. 작업 전환 시간과 같은 방식 — 드롭다운은 거점>공정(route)
                      //   단위로 고르고, 고른 공정 안의 자원(생산 공정 상세)을 행으로 편다.
                      //   행은 다른 스코프와 같은 renderHolidayRow — 토글을 되돌리면 목록에서도 빠진다.
                      if (isHolidayCal && scope === "routing_op") {
                        const { byRoute: calRoutes, opts: calOpts } = calRouteOpts;   // memo
                        const selCds  = (ov?.targets || []).map(x => x.cd);
                        const routeOfCd = (cd) => String(cd || "").split("@")[0];
                        // 펼칠 공정 = 저장된 targets 가 가리키는 공정 + 사용자가 편집 중 펼친 공정.
                        //   후자를 따로 두어야 자원 스위치를 전부 켜도(=해제 대상 0) 카드가 남는다.
                        //   순서 정본 = 고른 순(새로 고른 공정이 끝에 붙는다). 드롭다운을 아직 만지지 않은
                        //   저장 시나리오는 후보 목록 순 — targets 순으로 세우면 스위치를 켰다 끌 때마다
                        //   그 자원이 targets 끝으로 붙어 카드 순서가 뒤바뀐다.
                        const savedRoutes = new Set(selCds.map(routeOfCd).filter(Boolean));
                        const calPicked = (calOpen.routing_op
                          ?? [...calRoutes.keys()].filter(rk => savedRoutes.has(rk))
                        ).filter(rk => calRoutes.has(rk));
                        // 공정을 고르면 행만 펼친다 — 해제는 각 자원의 스위치를 끄는 행위다.
                        //   추가 직후 스위치는 현재 상태(=캘린더 적용 중, ON)로 둔다.
                        //   공정을 빼면 그 공정에서 이미 해제한 것만 되돌린다.
                        const setCalRoutes = (next) => {
                          const keys = next.map(o => o.key);
                          setCalOpen(s => ({ ...s, routing_op: keys }));
                          calPicked.filter(rk => !keys.includes(rk)).forEach(rk =>
                            (calRoutes.get(rk)?.items || [])
                              .filter(t => selCds.includes(t.cd))
                              .forEach(t => toggleTarget(def.param_key, t)));
                        };
                        const calSub = (g) => [g?.item_name, g?.loc_name].filter(Boolean).join(" · ");
                        return (
                          <Stack spacing={0.8}>
                            <AiTargetMultiSelect disabled={disabled} options={calOpts}
                              value={calPicked.map(rk => calOpts.find(o => o.key === rk)).filter(Boolean)}
                              onChange={setCalRoutes}
                              placeholder={tr("select")}
                              noOptionsText={tr("mpNoHolidayCalTargetDesc", { scope: tr("routing") })}
                              sx={{ alignSelf: "flex-start", minWidth: 260, maxWidth: 380 }} />
                            {calPicked.map(rk => {
                              const g = calRoutes.get(rk);
                              return (
                                <Box key={rk} sx={{ border: "1px solid " + C.border, borderRadius: 1, p: 0.8 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.5 }}>
                                    <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1 }}>
                                      {g?.route_name || rk}
                                    </Typography>
                                    {calSub(g) && (
                                      <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{calSub(g)}</Typography>
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    <AiDeleteButton disabled={disabled}
                                      onClick={() => setCalRoutes(calOpts.filter(
                                        o => calPicked.includes(o.key) && o.key !== rk))} />
                                  </Box>
                                  {/* 스위치를 켜도(해제 취소) 행은 남는다 — 다시 끌 수 있어야 한다. */}
                                  {(g?.items || []).map(renderHolidayRow)}
                                </Box>
                              );
                            })}
                          </Stack>
                        );
                      }
                      // 생산 공정 상세(routing_op): 거점 > 공정(route) 2단 그룹.
                      if (scope === "routing_op") {
                        const locOrder = [];
                        const locG = {};   // loc_cd → {loc_name, rOrder:[], routes:{route_cd:{route_name,items:[]}}}
                        targetList.forEach(t => {
                          const lk = t.loc_cd || "_";
                          if (!locG[lk]) { locG[lk] = { loc_name: t.loc_name || t.loc_cd, rOrder: [], routes: {} }; locOrder.push(lk); }
                          const rk = t.route_cd || "_";
                          if (!locG[lk].routes[rk]) { locG[lk].routes[rk] = { route_name: t.route_name || t.route_cd, items: [] }; locG[lk].rOrder.push(rk); }
                          locG[lk].routes[rk].items.push(t);
                        });
                        return (
                          <Stack spacing={0.6}>
                            {locOrder.map(lk => (
                              <Box key={lk}>
                                {lk !== "_" && (
                                  <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, mb: 0.3 }}>
                                    {locG[lk].loc_name}
                                  </Typography>
                                )}
                                <Stack spacing={0.4} sx={{ pl: lk !== "_" ? 0.8 : 0 }}>
                                  {locG[lk].rOrder.map(rk => (
                                    <Box key={rk}>
                                      {rk !== "_" && (
                                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, mb: 0.2 }}>
                                          {locG[lk].routes[rk].route_name}
                                        </Typography>
                                      )}
                                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {locG[lk].routes[rk].items.map(renderChip)}
                                      </Box>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        );
                      }
                      // 주기 캘린더 + 자원/워크센터: 후보가 캘린더 보유 전체라 길어질 수 있어 드롭다운으로
                      //   고른 것만 행으로 편다(거점은 드롭다운 그룹 헤더와 아래 행 그룹 양쪽에 나온다).
                      const calScope = isHolidayCal && (scope === "resource" || scope === "workcenter");
                      let calPicker = null;
                      let rowList = targetList;
                      if (calScope) {
                        const byCd = new Map(targetList.map(t => [t.cd, t]));
                        const selCds = (ov?.targets || []).map(x => x.cd);
                        const opts = calFlatOpts[scope] || [];   // memo — 편집 중 참조가 유지된다
                        // 순서 정본 = 사용자가 고른 순(새로 고른 것이 끝에 붙는다). 드롭다운을 아직 만지지 않은
                        //   저장 시나리오는 후보 목록 순으로 세운다 — targets 순으로 세우면 스위치를 켰다 끌 때마다
                        //   그 대상이 targets 끝으로 붙어 행 순서가 뒤바뀐다.
                        const shownCds = (calOpen[scope]
                          ?? targetList.filter(t => selCds.includes(t.cd)).map(t => t.cd)
                        ).filter(cd => byCd.has(cd));
                        rowList = shownCds.map(cd => byCd.get(cd));
                        calPicker = (
                          <AiTargetMultiSelect disabled={disabled} options={opts}
                            value={shownCds.map(cd => opts.find(o => o.key === cd)).filter(Boolean)}
                            // 고르면 행만 펼친다 — 해제는 스위치를 끄는 행위다. 추가 직후 스위치는
                            //   현재 상태(=캘린더 적용 중, ON). 빼면 이미 해제한 것만 되돌린다.
                            onChange={(next) => {
                              const keys = next.map(o => o.key);
                              setCalOpen(s => ({ ...s, [scope]: keys }));
                              selCds.filter(k => !keys.includes(k))
                                .forEach(k => toggleTarget(def.param_key, byCd.get(k) || { cd: k }));
                            }}
                            placeholder={tr("select")}
                            noOptionsText={tr("mpNoHolidayCalTargetDesc", { scope: tr(MP_SCOPE_LABEL[scope]) })}
                            sx={{ alignSelf: "flex-start", minWidth: 260, maxWidth: 380 }} />
                        );
                      }
                      // 그 외(생산 공정·작업장·자원): 거점(loc)별 1단 그룹.
                      const order = [];
                      const groups = {};
                      rowList.forEach(t => {
                        const k = t.loc_cd || "_";
                        if (!groups[k]) { groups[k] = { loc_name: t.loc_name || t.loc_cd, items: [] }; order.push(k); }
                        groups[k].items.push(t);
                      });
                      return (
                        <Stack spacing={0.6}>
                          {calPicker}
                          {order.map(k => (
                            <Box key={k}>
                              {k !== "_" && (
                                <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, mb: 0.3 }}>
                                  {groups[k].loc_name}
                                </Typography>
                              )}
                              {isHolidayCal ? (
                                <Stack sx={{ pl: k !== "_" ? 0.6 : 0 }}>
                                  {groups[k].items.map(renderHolidayRow)}
                                </Stack>
                              ) : (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {groups[k].items.map(renderChip)}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      );
                    })()}
                  </Box>
                )}

                {/* 값 입력 — 스코프별 선택지 제한:
                    · 대체 자원 정책의 '마스터 기준(masterData)'은 scope=전체에서만 선택 가능
                    · 자재 제약·자원 능력은 전체가 아닌 단위(자재·주문)에서 무한(infinite)만 선택 가능 */}
                {/* 수요 우선순위 — 날짜(하나 선택) + 기타(순서 지정). priority_items=[날짜?, ...기타] */}
                {isDemandPriorityBuilder ? (() => {
                  const rows = ov?.priority_items ?? (ov?.value ? [String(ov.value)] : []);
                  // 공급계획 설정과 동일 소스(CodeUtil) 파생 — 날짜/기타 목록을 코드 기준으로 분리 생성.
                  // 분류는 아래 dateSet/etcSet(코드 값 멤버십)으로만 하고, 그룹 표시 문자열은 비교하지 않는다.
                  const dateOpts = mpDatePriorityOpts();
                  const etcOpts  = mpDemandPriorityEtcOpts();
                  const opts     = [...dateOpts, ...etcOpts];
                  const dateSet  = new Set(dateOpts.map(o => o.value));
                  const etcSet   = new Set(etcOpts.map(o => o.value));
                  const dateSel  = rows.find(c => dateSet.has(c)) || null;   // 단일 날짜
                  const etcRows  = rows.filter(c => etcSet.has(c));          // 기타 순서(미지원 코드 제거)
                  // 날짜는 RADIO — 무조건 하나 선택(해제 불가). 미선택 시 첫 옵션을 기본 선택으로 간주.
                  const effDate  = dateSel ?? (dateOpts[0]?.value ?? null);
                  const commit   = (date, etc) => setPriorityItems(def, [...(date ? [date] : []), ...etc]);
                  const pickDate = (code) => commit(code, etcRows);          // 항상 선택, 해제 없음
                  // 같은 기준(base, 예: 수요 수량 ▲/▼)은 한 방향만 — 추가 시 같은 base 다른 방향 자동 해제.
                  const baseOf = (code) => { const o = opts.find(x => x.value === code); return (o && o.base) || code; };
                  const poolChipSx = { display: "inline-flex", alignItems: "center", gap: 0.4,
                    border: `1px solid ${C.border}`, borderRadius: "7px", px: "8px", py: "3px",
                    fontFamily: "inherit", fontSize: FONT.CAPTION, fontWeight: 700,
                    cursor: disabled ? "not-allowed" : "pointer", color: C.text2, bgcolor: C.bg2 };
                  // 적용 행 우선순위 버튼(위/아래/삭제) — 연한 틴트 배경 위 accent 글자(밝은 행 배경에 맞춤).
                  const prioBtnSx = (off) => ({ minWidth: 16, height: 16, px: "2px", border: "none", borderRadius: "4px",
                    bgcolor: `${accent}14`, color: accent, fontFamily: "inherit", fontSize: FONT.MICRO,
                    lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center",
                    cursor: off ? "default" : "pointer", opacity: off ? 0.35 : 1,
                    "&:hover": off ? {} : { bgcolor: `${accent}26` } });
                  // 기타 드래그&드롭 — 풀↔적용 이동·적용 내 재배치. 모두 commit(dateSel, ...) 로 priority_items 갱신.
                  //   풀: 미적용·같은 기준 미선택 기준만 노출(블록 방향 숨김 → 적용/미적용 명확).
                  const poolOpts = etcOpts.filter(o =>
                    !etcRows.includes(o.value) && !etcRows.some(c => baseOf(c) === baseOf(o.value)));
                  const applyAt = (code, idx) => {            // 풀→적용: 같은 기준 다른 방향 제거 후 idx 삽입
                    const b = baseOf(code);
                    const rest = etcRows.filter(c => c !== code && baseOf(c) !== b);
                    const at = idx == null ? rest.length : Math.max(0, Math.min(idx, rest.length));
                    rest.splice(at, 0, code);
                    commit(effDate, rest);
                  };
                  const reorder = (code, idx) => {            // 적용 내 재배치(자기 뒤로 이동 시 제거분 보정)
                    const cur = etcRows.indexOf(code);
                    if (cur < 0) return;
                    const rest = etcRows.filter(c => c !== code);
                    let at = idx == null ? rest.length : (idx > cur ? idx - 1 : idx);
                    at = Math.max(0, Math.min(at, rest.length));
                    rest.splice(at, 0, code);
                    commit(effDate, rest);
                  };
                  const dropAt = (idx) => (e) => {            // 드롭 = 풀이면 추가 / 적용이면 재배치
                    e.preventDefault(); setDndOver(null);
                    const code = e.dataTransfer.getData("text/plain");
                    if (!code) return;
                    if (etcRows.includes(code)) reorder(code, idx); else applyAt(code, idx);
                  };
                  const removeEtc = (code) => commit(effDate, etcRows.filter(c => c !== code));
                  const dragStart = (code) => (e) => {
                    e.dataTransfer.setData("text/plain", code);
                    e.dataTransfer.effectAllowed = "move";
                  };
                  const dropOnPool = (e) => {                 // 풀에 드롭 = 적용 해제(풀로 복귀)
                    e.preventDefault(); setDndOver(null);
                    const code = e.dataTransfer.getData("text/plain");
                    if (code && etcRows.includes(code)) removeEtc(code);
                  };
                  return (
                    <Stack spacing={0.6}>
                      {/* 날짜 — 하나만 선택 */}
                      <Box>
                        <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, mb: 0.3 }}>{tr("date")}</Typography>
                        <AiSegmentedControl accent={accent} value={effDate} onChange={pickDate} disabled={disabled} options={dateOpts} />
                      </Box>
                      {/* 기타 — 적용/미적용 2영역 드래그&드롭. 좌(미적용 풀) → 우(적용·우선순위 순). */}
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3 }}>
                            {tr("others")}
                          </Typography>
                          <Typography component="span" sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                            <DirArrow sorting="asc" /> {tr("mpSmallValueFirstLabel")}
                            <Box component="span" sx={{ ml: 0.6 }}><DirArrow sorting="desc" /></Box> {tr("mpBigValueFirstLabel")}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.8, alignItems: "start" }}>

                          {/* 좌: 미적용(사용 가능) 풀 — 드롭 시 적용 해제 */}
                          <Box onDragOver={e => { if (!disabled) { e.preventDefault(); setDndOver("pool"); } }}
                            onDragLeave={() => setDndOver(p => p === "pool" ? null : p)}
                            onDrop={disabled ? undefined : dropOnPool}
                            sx={{ borderRadius: "8px", border: `1px dashed ${dndOver === "pool" ? accent : C.border}`,
                                  bgcolor: dndOver === "pool" ? `${accent}0D` : C.bg3, p: "7px 8px", minHeight: 64,
                                  boxSizing: "border-box", minWidth: 0 }}>
                            <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, mb: 0.5 }}>
                              {tr("basicCandidates")}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {poolOpts.length === 0 ? (
                                <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{tr("mpAllCriteriaAppliedDesc")}</Typography>
                              ) : poolOpts.map(o => (
                                <Box key={o.value} component="div"
                                  draggable={!disabled} onDragStart={dragStart(o.value)}
                                  title={tr("mpDragToApplyTitle")}
                                  sx={{ ...poolChipSx, cursor: disabled ? "not-allowed" : "grab",
                                        transition: "background-color .12s, box-shadow .12s, border-color .12s",
                                        "&:hover": disabled ? {} : { bgcolor: `${accent}0D`, borderColor: accent, boxShadow: `0 0 0 1px ${accent}` } }}>
                                  {tr(o.label_key || o.label)}
                                  {o.sorting && <DirArrow sorting={o.sorting} />}
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* 우: 적용(우선순위 순) — 말미 추가 드롭 + 행별 삽입 드롭 */}
                          <Box onDragOver={e => { if (!disabled) e.preventDefault(); }}
                            onDrop={disabled ? undefined : dropAt(etcRows.length)}
                            sx={{ borderRadius: "8px", border: `1px solid ${accent}55`,
                                  bgcolor: C.bg2, p: "7px 8px", minHeight: 64, boxSizing: "border-box", minWidth: 0 }}>
                            <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: accent, mb: 0.5 }}>
                              {tr("appliance")}
                            </Typography>
                            <Stack spacing={0.4}>
                              {/* 0번 고정 행 — 날짜는 해제 불가 */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, width: "100%", boxSizing: "border-box",
                                         border: `1px solid ${accent}`, borderRadius: "7px", px: "6px", py: "3px",
                                         bgcolor: `${accent}24`, color: C.text1, fontSize: FONT.CAPTION, fontWeight: 700 }}>
                                {/* 순번 배지 크기·행간은 공급계획 설정(MpSettings) 과 동일 — 좁히면 숫자가 중앙에서 밀려 보인다 */}
                                <Box component="span" sx={{ minWidth: 16, height: 16, px: "3px", borderRadius: "50%",
                                       bgcolor: accent, color: "#fff", fontSize: FONT.MICRO, fontWeight: 700, lineHeight: 1,
                                       display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>0</Box>
                                <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {dateOpts.find(o => o.value === effDate)?.label ?? ""}
                                  <Box component="span" sx={{ fontWeight: 400, color: C.text3, ml: 0.5 }}>({tr("date")})</Box>
                                </Box>
                                <LockOutlinedIcon sx={{ fontSize: 14, color: accent, flexShrink: 0 }} />
                              </Box>
                              {etcRows.length === 0 ? (
                                <Box sx={{ borderRadius: "6px", border: `1px dashed ${C.border}`, py: "9px",
                                           textAlign: "center" }}>
                                  <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{tr("mpDropHereDesc")}</Typography>
                                </Box>
                              ) : (
                                etcRows.map((code, i) => {
                                  const o = opts.find(x => x.value === code) || { value: code, label: code };
                                  return (
                                    <Box key={code} draggable={!disabled} onDragStart={dragStart(code)}
                                      onDragOver={e => { if (!disabled) { e.preventDefault(); e.stopPropagation(); setDndOver(i); } }}
                                      onDragLeave={() => setDndOver(p => p === i ? null : p)}
                                      onDrop={disabled ? undefined : (e => { e.stopPropagation(); dropAt(i)(e); })}
                                      sx={{ display: "flex", alignItems: "center", gap: 0.4, width: "100%", boxSizing: "border-box",
                                            border: `1px solid ${accent}`, borderRadius: "7px", px: "6px", py: "3px",
                                            bgcolor: `${accent}14`, color: C.text1, fontSize: FONT.CAPTION, fontWeight: 700,
                                            cursor: disabled ? "default" : "grab", transition: "background-color .12s, box-shadow .12s",
                                            boxShadow: dndOver === i ? `inset 0 2px 0 0 ${accent}` : "none",
                                            "&:hover": disabled ? {} : { bgcolor: `${accent}2E`, boxShadow: dndOver === i ? `inset 0 2px 0 0 ${accent}` : `0 0 0 1px ${accent}` } }}>
                                      <Box component="span" sx={{ minWidth: 16, height: 16, px: "3px", borderRadius: "50%",
                                             bgcolor: accent, color: "#fff", fontSize: FONT.MICRO, fontWeight: 700, lineHeight: 1,
                                             display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        {i + 1}
                                      </Box>
                                      <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {tr(o.label_key || o.label)}
                                      </Box>
                                      {o.sorting && <DirArrow sorting={o.sorting} />}
                                      {!disabled && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, flexShrink: 0, ml: 0.2 }}>
                                          <Box component="button" type="button"
                                            onClick={() => removeEtc(code)} title={tr("mpUnapplyTitle")}
                                            sx={prioBtnSx(false)}>✕</Box>
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })
                              )}
                            </Stack>
                          </Box>

                        </Box>
                      </Box>
                    </Stack>
                  );
                })() : isHolidayCal ? (
                  scope === "all" ? (
                    // 전체는 대상 칩이 없어 체크박스가 유일한 적용 스위치 — 기본 OFF.
                    <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 0.5,
                           cursor: disabled ? "default" : "pointer" }}>
                      <Box component="input" type="checkbox" disabled={disabled}
                        checked={ov?.value === "clear"}
                        onChange={(e) => upsertOv(def.param_key,
                          { value: e.target.checked ? "clear" : "", targets: [] })}
                        style={{ width: 13, height: 13, accentColor: accent, cursor: disabled ? "not-allowed" : "pointer" }} />
                      <Typography sx={{ fontSize: FONT.MICRO, lineHeight: 1.5,
                                        color: ov?.value === "clear" ? accent : C.text3 }}>
                        {tr("mpHolidayCalIgnoreAllDesc")}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, lineHeight: 1.5 }}>
                      {tr("mpHolidayCalIgnoreDesc", { scope: scope === "workcenter" ? tr("mpWorkcenterAllResLabel")
                        : scope === "routing_op" ? tr("routing") : tr("productionResource") })}
                    </Typography>
                  )
                ) : (!isRoutingOpPerRes && !isResourceGrid && !isJctParam) ? (() => {
                  let valueDef = def;
                  if (def.param_key === "alt_res_policy" && scope !== "all") {
                    valueDef = { ...def, options: (def.options || []).filter(o => o.value !== "masterData") };
                  } else if ((def.param_key === "MC" || def.param_key === "RC") && scope !== "all") {
                    valueDef = { ...def, options: (def.options || []).filter(o => o.value === "infinite") };
                  }
                  if (isGroupRow) {
                    // 로트 3종을 한 줄에 — 배수(무라벨) · 최소 · 최대. 각 입력은 자기 param_key 의 override 에 쓴다.
                    //   동반 키의 적용 단위·대상은 anchor 를 그대로 따른다(따로 두면 min>max 대조가 불가능).
                    const lotBad = [...findLotBoundConflicts(overrides)]
                      .some(k => k.startsWith(`${scope}\u0000`));
                    const cell = (pk, labelKey) => {
                      const d = defOfKey(pk) || def;
                      const o = ovOf(pk);
                      const markBad = !!labelKey && lotBad;
                      return (
                        <Box key={pk} sx={{ display: "flex", alignItems: "center", gap: 0.4,
                              ...(markBad ? { "& input": { borderColor: C.g3, color: C.g3, fontWeight: 700 } } : {}) }}>
                          {labelKey && (
                            <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700,
                                              color: markBad ? C.g3 : C.text3 }}>
                              {tr(labelKey)}
                            </Typography>
                          )}
                          {renderValueInput(d, o?.value, (val) => {
                            setValueTouched(m => ({ ...m, [pk]: true }));   // 직접 입력 → 자동 제안 비활성
                            upsertOv(pk, { value: val, scope_type: scope, targets: ov?.targets || [] });
                          }, noScopeData)}
                        </Box>
                      );
                    };
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        {groupKeys.map(pk => cell(pk, mpRowRoleLabel(defOfKey(pk))))}
                      </Box>
                    );
                  }
                  return renderValueInput(valueDef, ov?.value ?? def.default, (val) => {
                    setValueTouched(m => ({ ...m, [def.param_key]: true }));  // 직접 입력 → 자동 제안 비활성
                    upsertOv(def.param_key, { value: val });
                  }, noScopeData);
                })() : null}

                {/* 작업 전환 시간 조정 — 값 dropdown 이 고른 단위에 따라 배율 / 설비별 시간. */}
                {isJctParam && renderJctAdjust(def, ov)}

                {/* 적용 기간 — 자원 효율·작업 시간/일만. 구간 카드 리스트(미설정 시 전체 값 적용). */}
                {/* 변경 대상 후보 없는 비-전체 스코프에서는 적용 대상이 없어 기간 편집도 숨김. */}
                {MP_PERIOD_CAPABLE.has(def.param_key) && !noScopeData && !isResourceGrid && (() => {
                  const periods = ov?.periods || [];
                  // 자원 효율: 저장은 배수 그대로, 표시·입력만 %(1.2↔120%). 카탈로그 meta 플래그로 판정(나머지는 기존 단위).
                  const isPct = def.display === "percent";
                  const unit = isPct ? "%" : def.type === "multiplier" ? tr("multiplierUnitLabel") : (def.unit_key ? tr(def.unit_key) : (def.unit || ""));
                  // 구간끼리 일자가 겹치는(또는 시작>종료) index 집합 — 빨간 테두리·하단 경고에 사용
                  const overlapIdx = findOverlappingPeriodIndices(periods);
                  const hasOverlap = overlapIdx.size > 0;
                  return (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
                      <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3 }}>
                        {tr("mpApplyPeriodLabel")}
                      </Typography>
                      <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({tr("mpUnsetFullPeriodHint")})</Typography>
                      <AiButton variant="solid" size="sm" accent={accent} disabled={disabled}
                        onClick={() => addPeriod(def, ov)} sx={{ ml: "auto" }}>
                        {tr("mpAddPeriodLabel")}
                      </AiButton>
                    </Box>
                    {periods.length === 0 ? (
                      <Box sx={{ borderRadius: "8px", border: `1px dashed ${C.border}`, bgcolor: C.bg3,
                                 px: "10px", py: "7px" }}>
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                          {tr("mpPeriodDefaultApplyDesc")} <b>{tr("mpAddPeriodLabel")}</b>.
                        </Typography>
                      </Box>
                    ) : (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Stack spacing={0.5}>
                        {periods.map((p, idx) => {
                          const startD = periodStrToDate(p.start_dt);
                          const endD   = periodStrToDate(p.end_dt);
                          // 시작>종료 또는 다른 구간과 일자 겹침이면 오류 표시(빨간 테두리)
                          const bad = !!(startD && endD && startD > endD) || overlapIdx.has(idx);
                          const dpSx = { width: 130,
                            "& .MuiInputBase-root": { fontSize: FONT.BODY, bgcolor: C.bg1 },
                            "& .MuiInputBase-input": { p: "5px 8px" } };
                          return (
                            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap",
                                  border: `1px solid ${bad ? C.g3 : C.border}`, borderRadius: "8px",
                                  bgcolor: C.bg2, px: "8px", py: "6px" }}>
                              <Box component="span" sx={{ flex: "0 0 auto", minWidth: 34, fontSize: FONT.MICRO,
                                     fontWeight: 700, color: accent }}>{tr("mpSectionIndexLabel", { idx: idx + 1 })}</Box>
                              <DatePicker
                                format="yyyy-MM-dd" value={startD} disabled={disabled}
                                onChange={(dt) => updatePeriod(def.param_key, idx, { start_dt: periodDateToStr(dt) })}
                                slotProps={{ textField: { size: "small", placeholder: "yyyy-MM-dd", error: bad, sx: dpSx } }} />
                              <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>~</Typography>
                              <DatePicker
                                format="yyyy-MM-dd" value={endD} disabled={disabled} minDate={startD || undefined}
                                onChange={(dt) => updatePeriod(def.param_key, idx, { end_dt: periodDateToStr(dt) })}
                                slotProps={{ textField: { size: "small", placeholder: "yyyy-MM-dd", error: bad, sx: dpSx } }} />
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, ml: "auto", flex: "0 0 auto" }}>
                                <DraftNumberInput disabled={disabled}
                                  step={isPct ? "1" : def.type === "multiplier" ? "0.1" : undefined}
                                  min="0" max={MP_NUM_MAX[def.param_key]}
                                  value={isPct ? yieldDecToPct(p.value) : (p.value ?? "")}
                                  placeholder={isPct ? "100" : def.type === "multiplier" ? "1.0" : "0"}
                                  clamp={(v) => {                    // 상한 클램프 — 작업 시간/일 24 등(renderValueInput 과 동일)
                                    if (v === "") return v;
                                    const mx = MP_NUM_MAX[def.param_key];
                                    if (mx != null && Number(v) > mx) return String(mx);
                                    return Number(v) < 0 ? "0" : v;
                                  }}
                                  onCommit={(v) => updatePeriod(def.param_key, idx, { value: isPct ? yieldPctToDec(v) : v })}
                                  sx={{ ...inputSx, width: 58, p: "3px 6px", textAlign: "center" }} />
                                {unit && <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>{unit}</Typography>}
                              </Box>
                              <AiDeleteButton disabled={disabled}
                                onClick={() => removePeriod(def.param_key, idx)} />
                            </Box>
                          );
                        })}
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                          ※ {tr("mpPeriodOverrideNote")}
                        </Typography>
                        {hasOverlap && (
                          <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.g3 }}>
                            {tr(periods.some((p) => p?.start_dt && p?.end_dt && p.start_dt > p.end_dt)
                              ? "mpPeriodReversedWarning" : "mpPeriodOverlapWarning")}
                          </Typography>
                        )}
                      </Stack>
                    </LocalizationProvider>
                    )}
                  </Box>
                  );
                })()}

              </Stack>
            )}
          </Box>
        ));
  };

  // 도메인 그룹별 섹션 — 비어있지 않은 그룹만 헤더+카드 렌더 (순서: MP_GROUP_ORDER).
  //   조정만 보기에서는 그룹 안이 다 비면 그룹 헤더도 같이 사라진다(빈 제목만 남지 않게).
  const addCands = defs
    .filter(d => !voidedByRcInfinite(d) && !isParamVisible(d)
                 && !isRowCompanion(d.param_key))
    .map(d => ({ key: d.param_key, label: tr(d.label_key || d.label) }));
  return (
    <>
    <Stack spacing={1.3}>
      {MP_GROUP_ORDER.map(g => {
        const groupDefs = defs.filter(d => mpGroupOf(d) === g.key && !voidedByRcInfinite(d)
          && isParamVisible(d)
          && !isRowCompanion(d.param_key));   // 그룹 멤버는 대표 행 안에서 편집
        if (groupDefs.length === 0) return null;
        const hiddenByRc = defs.some(d => mpGroupOf(d) === g.key && voidedByRcInfinite(d));
        return (
          <Box key={g.key}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.6 }}>
              <Typography sx={{ fontSize: FONT.CAPTION, flex: "0 0 auto" }}>{g.icon}</Typography>
              <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2,
                                letterSpacing: "0.02em", flex: "0 0 auto" }}>
                {tr(g.label)}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: C.border }} />
            </Box>
            <Stack spacing={0}>
              {groupDefs.map((d, di) => (
                <React.Fragment key={d.param_key}>
                  {renderParamCard(d, di === groupDefs.length - 1)}
                  {/* 숨긴 자원 파라미터의 흔적 — 이유(RC 무한)를 자원 능력 카드 바로 아래에 남긴다. */}
                  {hiddenByRc && d.param_key === "RC" && (
                    <Typography sx={{ px: "10px", fontSize: FONT.MICRO, color: C.text3 }}>
                      {tr("mpRcInfiniteShortLabel")} — {tr("mpRcInfiniteParamsVoidLabel")}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Box>
        );
      })}
      {view === "changed" && !disabled && (
        <ScenarioParamAddPicker items={addCands} accent={accent}
          onPick={(k) => {
            setPinnedKeys(st => { const n = new Set(st); n.add(k); return n; });
            const d = defs.find(x => x.param_key === k);
            if (d) openParam(d);            // override 시드 생성까지 필요 — 펼침은 openParam 이 맡는다
          }} />
      )}
    </Stack>
    <BackcalcDetailModal
      data={bcModal}
      onClose={() => setBcModal(null)}
      onApplyPeriods={(pairs) => {
        // pairs=[{date, add, base_whpd?, base_over_whpd?}] → 능력 레벨링(날짜별 base·중복 합산·FROM 전날·
        //   plan_start 클램프) → 비중복 period. 마스터 기간별 능력이 덮는 날은 그 날 base 로 천장 계산.
        //   24h 초과(plan_start 제약)는 입력창이 빨간 경고.
        const applyKey = bcModal?.pk;                  // 값이 들어갈 칸 — 작업 시간 행이면 '초과(잔업)'
        const rowPk = bcModal?.rowPk || applyKey;      // 행 상태(모드·펼침)의 키는 언제나 대표
        const def = defs?.find((d) => d.param_key === applyKey);
        const cand = bcModal?.cand;
        if (!def || !cand || !pairs?.length) { setBcModal(null); return; }
        const fallbackBase = Number(bcModal?.bc?.current_whpd ?? cand?.current?.work_hour_per_day) || 0;  // 기간 밖 플랫 whpd
        const fallbackOver = Number(cand?.current?.over_work_hour_per_day) || 0;  // 기간 밖 플랫 잔업
        const wdpw = Number(cand?.current?.work_day_per_week) || 7;               // 주당 작업일 수(비가동 요일 산출)
        const wsd = cand?.current?.work_start_day || "mon";                       // 가동 시작 요일
        // 효율 variant: [{date, eff}] → efficiencyPeriods(스칼라 배수, 24h 분배 없음).
        //   시간 variant: [{date, add, base_whpd, base_over_whpd}] → levelPeriods(천장 24−정규−잔업).
        const newPeriods = (applyKey === "efficiency")
          ? efficiencyPeriods(pairs, wdpw, wsd)
          : levelPeriods(pairs, fallbackBase, bcModal?.bc?.plan_start || null, wdpw, wsd,
                         applyKey === "over_work_hour_per_day", fallbackOver);
        if (newPeriods.length) {
          setResRowOpen((m) => ({ ...m, [`${rowPk}:${cand.cd}`]: true }));         // 모달 적용 → 해당 행 펼침
          // ⚠ 반드시 1회 뮤테이션으로 일괄 추가. addTargetPeriod 를 forEach 로 N회 호출하면
          //   updateAt(AiScenarioPlanning) 이 stale scenarios 클로저를 읽어 마지막 1건만 남는다(다건→1건 버그).
          //   합성 행은 값이 안 들어가는 칸에도 같은 날짜의 빈 구간을 넣어 구간 index 를 맞춘다.
          mutateTargetOfCandMany(rowMembers(rowPk), cand, (t, k) => ({
            ...t, periods: [...(t.periods || []),
              ...newPeriods.map(p => (k === applyKey ? p : { ...p, value: "" }))],
          }), "resource");
        }
        setBcModal(null);
      }}
      tr={tr} />
    </>
  );
}

MP_MODULE.CustomScenarioFields = MpScenarioParamFields;
// MP 도 param_defs 를 자체 렌더(overrides 포함)하므로 공통 제네릭 param_defs 분기를 우회.
MP_MODULE.handlesParamDefs = true;

// ── 정책 미리보기 현재값 해석기 (공통 화면이 mod.previewOverrideCurrent 로 위임 호출) ──
// def.current_value 는 파라미터 전체 평균(예: 평균 작업 시간/일 10.6시간)이라 자원 지정 변경에는 부정확하다.
//   override 대상 자원의 실제 현재값(scope_targets[scope].current[param_key], 예: 전극 코팅기 8시간)을 우선 표시한다.
//   대상들의 현재값이 하나로 일치하면 그 값을, 다르거나 조회 불가하면 평균(def.current_value)으로 폴백한다.
//   (override.targets 는 {cd,name}만 보유 → 현재값은 후보 목록 scope_targets 에서 cd 로 조회.)
MP_MODULE.previewOverrideCurrent = (ov, def, sc) => {
  const cands = (sc?.scope_targets || {})[ov?.scope_type] || [];
  // 대체 자원 정책(select) — 전역 현재값이 '마스터 기준(masterData)'이면 각 라우팅이 자체 마스터
  //   (fast/priority)를 따르므로 어느 정책인지 알 수 없다. 대상(route/작업장)의 실제 정책
  //   (route_alt_policy / 작업장 단일 정책)으로 확정해 'Fast'·'우선순위' 로 표기한다.
  //   전역이 이미 fast/priority(전역 상수 적용)면 그 값이 실효 현재값이므로 확정하지 않는다.
  //   current 는 후보의 별도 필드(current.alt_res_policy 아님)라 제너릭 경로가 못 잡아 여기서 선처리.
  //   생산 공정 상세(routing_op)는 우선순위 숫자 편집이라 제외.
  if (def?.param_key === "alt_res_policy" && String(def?.current_value) === "masterData"
      && ov?.targets?.length && ov.scope_type !== "routing_op" && cands.length > 0) {
    const resolveOne = (cd) => {
      const c = cands.find(x => x.cd === cd);
      if (!c) return null;
      if (c.route_alt_policy) return c.route_alt_policy;                 // route 스코프 = 단일 정책
      if (Array.isArray(c.alt_res_policy_counts) && c.alt_res_policy_counts.length === 1)
        return c.alt_res_policy_counts[0].policy;                       // 작업장 = 정책 1종일 때만
      return null;
    };
    const pols = ov.targets.map(t => resolveOne(t.cd)).filter(Boolean);
    // 대상 전부 해석되고 한 정책으로 일치할 때만 확정(섞이면 모호 → 전역값 폴백).
    if (pols.length === ov.targets.length && new Set(pols).size === 1) return pols[0];
    return def?.current_value;
  }
  if (!ov?.targets?.length || cands.length === 0) return def?.current_value;
  const vals = ov.targets
    .map(t => cands.find(c => c.cd === t.cd)?.current?.[ov.param_key])
    .filter(v => v != null);
  if (!vals.length) return def?.current_value;
  return new Set(vals.map(String)).size === 1 ? vals[0] : def?.current_value;
};

// ── 잔업 대책의 미리보기 표기 — 카드와 같은 '하루 능력 합계'로 읽힌다 ────────────
// 잔업 값만 보여 주면(0시간 → 16시간) 그 자원의 하루 천장이 얼마가 되는지 알 수 없다.
//   대상들의 정규 시간이 하나로 일치할 때만 합계로 환산한다 — 자원마다 다르면 더할 기준이 없다.
const mpOverBase = (ov, sc) => {
  if (ov?.param_key !== "over_work_hour_per_day") return null;
  const cands = (sc?.scope_targets || {})[ov?.scope_type] || [];
  const vals = (ov.targets || [])
    .map(t => cands.find(c => c.cd === t.cd)?.current?.work_hour_per_day)
    .filter(v => v != null);
  if (!vals.length || new Set(vals.map(String)).size !== 1) return null;
  return Number(vals[0]);
};

// 미리보기 이름 — 잔업 대책도 '작업 시간/일'로 부른다(권장 대책 카드 제목과 통일).
MP_MODULE.previewOverrideLabelDef = (ov, def, sc) =>
  (ov?.param_key === "over_work_hour_per_day"
    ? (sc?.param_defs || []).find(d => d.param_key === "work_hour_per_day") : null) || null;

// ── 정책 미리보기 값 포맷 (공통 화면이 mod.formatOverrideValue 로 위임 호출) ──
// 수율·자원 효율은 저장은 소수/배수, 표시는 %(수율 0.97→97%, 효율 1.2→120%).
//   그 외 키는 null 반환 → 공통 제너릭 포맷(multiplier/unit)에 맡긴다.
MP_MODULE.formatOverrideValue = (raw, def, sc, ov) => {
  if (def?.display === "percent")
    return `${yieldDecToPct(raw)}%`;
  // 잔업은 정규에 더해지는 값 — 미리보기는 카드와 같이 하루 합계로 보여 준다.
  if (ov?.param_key === "over_work_hour_per_day" && raw != null && raw !== "") {
    const base = mpOverBase(ov, sc);
    const n = Number(raw);
    if (base != null && Number.isFinite(n)) return `${base + n}${i18n.t("mpUnitHour")}`;
  }
  // 계획 방식(PS)은 언어팩(planStrategies 파생)으로 표시 언어를 따른다.
  if (def?.param_key === "PS" && raw != null && raw !== "") return psStrategyLabel(raw);
  return null;
};

// 산업별 표준 시나리오 (시나리오 구성 탭 — 표준 시나리오 모드). 공통 화면이 mod.templates 로 참조.
// 최상위 industry/name/useCase/desc 값은 i18n 키 문자열 — 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
// tags 도 i18n 키 문자열로 통일(현재 소비처 없음, 향후 노출 대비).
// scenarios[].name/desc 는 시나리오 카드 편집 필드(사용자 편집 가능한 초기값)라 번역 대상에서 제외한다.
MP_MODULE.templates = [
  {
    id: "mp_standard",
    industry: "standardTagLabel",
    name: "mpTplStandardName",
    useCase: "mpTplStandardUseCase",
    desc: "mpTplStandardDesc",
    tags: ["scnTagBackwardScheduling", "scnTagStandardLot", "FIFO", "scnTagUtilizationBalance"],
    scenarios: [
      { role: "base", name: "표준 역방향 계획",
        desc: "납기 기준 역방향, 표준 로트, FIFO로 안정적인 생산 스케줄을 운영합니다.",
        params: { scheduling_method: "backward", capacity_policy: "standard", lot_size_policy: "standard", priority_policy: "FIFO" } },
      { role: "alt1", name: "납기 우선 (EDD + 잔업)",
        desc: "EDD 우선순위와 잔업을 허용하여 납기 준수율을 극대화합니다.",
        params: { scheduling_method: "backward", capacity_policy: "overtime", lot_size_policy: "standard", priority_policy: "EDD" } },
      { role: "alt2", name: "순방향 + 외주 활용",
        desc: "순방향 착공 기준으로 최대한 빨리 생산을 시작하고 외주로 자원 부족을 보완합니다.",
        params: { scheduling_method: "forward", capacity_policy: "outsource", lot_size_policy: "min_lot", priority_policy: "FIFO" } },
    ],
  },
  {
    id: "mp_otd_focus",
    industry: "mpTplIndustryOtdFocus",
    name: "mpTplOtdFocusName",
    useCase: "mpTplOtdFocusUseCase",
    desc: "mpTplOtdFocusDesc",
    tags: ["scnTagEddPriority", "scnTagOvertimeAllowed", "scnTagDeliveryTopPriority", "scnTagOtdMaximize"],
    scenarios: [
      { role: "base", name: "EDD + 잔업",
        desc: "납기 빠른 순 처리, 잔업 허용, 표준 로트로 납기 준수율을 높입니다.",
        params: { scheduling_method: "backward", capacity_policy: "overtime", lot_size_policy: "standard", priority_policy: "EDD" } },
      { role: "alt1", name: "EDD + 최소 로트",
        desc: "EDD 우선, 최소 로트 분할을 허용하여 납기 임박 소량 주문을 신속 처리합니다.",
        params: { scheduling_method: "backward", capacity_policy: "overtime", lot_size_policy: "min_lot", priority_policy: "EDD" } },
      { role: "alt2", name: "우선순위 코드 + 외주",
        desc: "우선순위 코드 기반 처리, 외주 활용으로 자원 병목을 회피합니다.",
        params: { scheduling_method: "backward", capacity_policy: "outsource", lot_size_policy: "standard", priority_policy: "custom" } },
    ],
  },
  {
    id: "mp_capacity_focus",
    industry: "mpTplIndustryCapacityFocus",
    name: "mpTplCapacityFocusName",
    useCase: "mpTplCapacityFocusUseCase",
    desc: "mpTplCapacityFocusDesc",
    tags: ["scnTagForwardStart", "scnTagMinLot", "scnTagUtilizationMax", "scnTagSplitAllowed"],
    scenarios: [
      { role: "base", name: "순방향 표준",
        desc: "순방향 착공, 표준 가동, 표준 로트로 자원 낭비를 최소화합니다.",
        params: { scheduling_method: "forward", capacity_policy: "standard", lot_size_policy: "standard", priority_policy: "FIFO" } },
      { role: "alt1", name: "순방향 + 최소 로트",
        desc: "순방향 착공, 최소 로트 허용으로 자원을 쉬지 않고 가동합니다.",
        params: { scheduling_method: "forward", capacity_policy: "standard", lot_size_policy: "min_lot", priority_policy: "FIFO" } },
      { role: "alt2", name: "순방향 + 외주 확장",
        desc: "순방향 착공, 외주 활용으로 자원 가용량을 확장하여 총 생산량을 극대화합니다.",
        params: { scheduling_method: "forward", capacity_policy: "outsource", lot_size_policy: "min_lot", priority_policy: "FIFO" } },
    ],
  },
];

// 표준 시나리오 동적 로딩 (z_ai_scenario 카탈로그 — 기존 하드코딩 templates 대체).
// 공통 StepScenarioCreate 가 mod.fetchTemplates 존재 시 이 결과를 템플릿 카드로 렌더한다.
// display-only: 변형(base/alt1/alt2)은 이름·설명·변경항목(change_items)만 담고 엔진 params 는 비움
// → seedScenarios(param_defs 미보유)가 mod.params 기본값으로 백필, 엔진 바인딩은 모듈별 후속.
MP_MODULE.fetchTemplates = async () => {
  const res = await scenarioService.getScenarios("MP");
  rememberModuleCatalog(MP_MODULE, res);   // 표준 시나리오 0건이어도 직접 입력 카드가 그려지도록 최상위 카탈로그 보관
  const problemTargets = res?.problem_targets || {};
  const scopeTargets   = res?.scope_targets || {};   // per-param scope 대상 후보(WC/자원/route)
  return (res?.scenarios || []).map(scn => ({
    id:          scn.scenario_cd,
    industry:    scn.source_type === "USER" ? "userTagLabel"
                 : (scn.recommended_flg === "Y" ? "recommendedTagLabel" : "standardTagLabel"),
    // 표시용 i18n 키(*_key)를 우선 사용, 없으면 원문 폴백. LLM 은 원문을 그대로 소비.
    name:        scn.scenario_name_key || scn.scenario_name,
    direction:   scn.scenario_direction_key || scn.scenario_direction,
    useCase:     scn.desc_txt_key || scn.desc_txt,
    desc:        scn.desc_txt_key || scn.desc_txt,
    recommended: scn.recommended_flg === "Y",
    // base/alt1/alt2 — overrides + param_defs 임베드. scope_targets(WC/자원/route 후보) 주입.
    scenarios:   (scn.variants || []).map(v => ({ ...v, problem_targets: problemTargets, scope_targets: scopeTargets })),
  }));
};

// 직접 입력 시나리오 — 표준 시나리오(SCN-MP-001~004)에서 편집 가능한 모든 파라미터(param_defs 합집합)를
// alt1/alt2 가 자유롭게 편집할 수 있도록 구성한다. fetchTemplates 로 동적 로딩된 카탈로그(param_defs·
// scope_targets)를 그대로 재사용하므로 표준 시나리오와 동일한 스코프 편집기(MpScenarioParamFields)가
// 적용된다. 공통 StepScenarioCreate 가 custom 모드에서 이 결과를 시나리오 카드로 렌더한다.
//   - 입력: fetchTemplates 결과 목록(각 tpl.scenarios = base/alt1/alt2 변형, param_defs·scope_targets 보유)
//   - base : 현행 유지(읽기전용, current_display). alt1/alt2 : overrides=[](사용자가 항목 선택·편집).
//   - 카탈로그 미적재(param_defs 0개) 시 null 반환.
//     이때 공통 addBlank 가 paramDefsUnion 의 모듈 전체 카탈로그 폴백으로 param_defs 를 채운다.
MP_MODULE.buildCustomScenarios = (templates) => {
  const list = Array.isArray(templates) ? templates : [];
  const defMap = new Map();            // param_key → param_def (등장 순서 유지, 중복 제거 = 합집합)
  let scopeTargets = {};
  for (const tpl of list) {
    for (const v of (tpl.scenarios || [])) {
      if (v?.scope_targets && Object.keys(v.scope_targets).length) scopeTargets = v.scope_targets;
      for (const d of (v?.param_defs || [])) {
        if (d?.param_key && !defMap.has(d.param_key)) defMap.set(d.param_key, d);
      }
    }
  }
  const paramDefs = [...defMap.values()];
  if (!paramDefs.length) return null;  // 카탈로그 미적재 — 레거시 폴백
  const mk = (role, name, desc) => ({
    role, name, desc, param_defs: paramDefs, scope_targets: scopeTargets,
    overrides: [], enabled: true,
  });
  // "현행 유지"는 공통 파일의 keepCurrentName 과 동일 문구라 그 키를 그대로 문자열 값으로 재사용한다
  //   (이 배열은 화면에 t() 없이 sc.name 편집 필드 초기값으로 그대로 들어가므로, 다른 모듈의 base 시나리오와
  //   동일하게 한국어 원문을 직접 둔다 — makeBaseScenario 의 keepCurrentName 폴백과 동일 문구 유지 목적).
  return [
    mk("base", "현행 유지", "현재 설정을 변경 없이 유지하는 기준 시나리오입니다."),
    mk("alt1", "직접 입력 시나리오 1", "편집할 파라미터를 선택해 값을 직접 설정합니다."),
    mk("alt2", "직접 입력 시나리오 2", "편집할 파라미터를 선택해 값을 직접 설정합니다."),
  ];
};

// ── MP AI 시나리오 플래닝 페이지 ──────────────────────────────────────────────
// AiScenarioPlanning 을 lazy import 하여 순환 참조 방지
const AiScenarioPlanningLazy = React.lazy(() => import("./AiScenarioPlanning"));

export default function AiMpScenarioPlanning({ viewName }) {
  return (
    <React.Suspense fallback={null}>
      <AiScenarioPlanningLazy viewName={viewName} singleModuleCode="MP" />
    </React.Suspense>
  );
}
